/* eslint-disable @typescript-eslint/no-explicit-any */
import { EdgeWithMetadata, NodeWithMetadata, WebColaLayout, WebColaTranslator, NodePositionHint, TransformInfo, LayoutState, WebColaLayoutOptions, WebColaRenderTransitionMode } from './webcolatranslator';
import { InstanceLayout, isAlignmentConstraint, isInstanceLayout, isLeftConstraint, isTopConstraint, LayoutNode } from '../../layout/interfaces';
import type { GridRouter, Group, Layout, Node, Link } from 'webcola';
import { IInputDataInstance, ITuple, IAtom } from '../../data-instance/interfaces';
import { MAIN_LABEL_FONT_SIZE, SECONDARY_FONT_SIZE, LABEL_LINE_HEIGHT_RATIO } from '../../layout/text-extent';

let d3 = window.d3v4 || window.d3; // Use d3 v4 if available, otherwise fallback to the default window.d3
let cola = window.cola;

/**
 * Checks if two SVG elements are overlapping.
 * 
 * @param element1 - First element
 * @param element2 - Second element
 * @returns True if elements overlap
 */
function isOverlapping(element1: SVGElement, element2: SVGElement): boolean {
  function hasgetBBox(target: any): target is { getBBox: any } {
    return target && typeof target === 'object' && 'getBBox' in target;
  }

  const bbox1 = hasgetBBox(element1) ? element1.getBBox() : { x: 0, y: 0, width: 0, height: 0 };
  const bbox2 = hasgetBBox(element2) ? element2.getBBox() : { x: 0, y: 0, width: 0, height: 0 };
  
  return !(bbox2.x > bbox1.x + bbox1.width ||
           bbox2.x + bbox2.width < bbox1.x ||
           bbox2.y > bbox1.y + bbox1.height ||
           bbox2.y + bbox2.height < bbox1.y);
}

function hasInnerBounds(target: any): target is { innerBounds: any } {
  return target && typeof target === 'object' && 'innerBounds' in target;
}


const DEFAULT_SCALE_FACTOR = 5;

/**
 * WebCola CnD Graph Custom Element
 * Full implementation using WebCola constraint-based layout with D3 integration
 * @field currentLayout - Holds the current custom WebColaLayout instance
 * @field colaLayout - Holds the current layout instance used by WebCola
 * 
 * Features:
 * - Interactive edge input mode with keyboard shortcuts (Cmd/Ctrl)
 * - Visual edge creation by clicking and dragging between nodes
 * - Edge modification by clicking on existing edges in input mode
 * - Centralized state management for IInputDataInstance
 * - Automatic layout regeneration when data instance changes
 * - Self-loop edge support with confirmation
 * - Zoom/pan disable during input mode
 * - Comprehensive event system for external integration
 * 
 * Events Fired:
 * - 'input-mode-activated': When Cmd/Ctrl is pressed to activate input mode
 * - 'input-mode-deactivated': When Cmd/Ctrl is released to deactivate input mode  
 * - 'edge-creation-requested': When user drags between nodes to create a new edge
 *   * event.detail: { relationId: string, sourceNodeId: string, targetNodeId: string, tuple: ITuple }
 * - 'edge-modification-requested': When user clicks on existing edge to modify it
 *   * event.detail: { oldRelationId: string, newRelationId: string, sourceNodeId: string, targetNodeId: string, tuple: ITuple }
 * 
 * External State Management:
 * React components should subscribe to these events and handle:
 * 1. Updating the IInputDataInstance with new atoms/relations
 * 2. Regenerating CnD layout constraints from the updated data
 * 3. Calling renderLayout() to apply changes and re-render the visualization
 * 
 * This ensures React components serve as the single source of truth for state
 * while the WebCola component focuses purely on visualization and user interaction.
 */
export class WebColaCnDGraph extends  HTMLElement { //(typeof HTMLElement !== 'undefined' ? HTMLElement : (class {} as any)) {
  private svg!: any;
  private container!: any;
  private currentLayout!: WebColaLayout;
  private colaLayout!: Layout;
  private readonly lineFunction: d3.Line<{ x: number; y: number }>;
  /**
   * Snapshot of the seed positions the most-recent policy resolved
   * before the solver ran. Captured for the seed-vs-output
   * decomposition in the appropriateness experiment. `null` when no
   * policy was applied this render (e.g., first frame, no prior).
   */
  private lastSeedState: LayoutState | null = null;

  /**
   * Configuration constants for SVG
   */

  private static readonly DEFAULT_SVG_WIDTH = 800;
  private static readonly DEFAULT_SVG_HEIGHT = 600;

  /**
   * Configuration constants for node visualization
   */
  private static readonly SMALL_IMG_SCALE_FACTOR = 0.3;
  private static readonly NODE_BORDER_RADIUS = 6;
  private static readonly NODE_STROKE_WIDTH = 1.5;

  /**
   * Default canvas color (tufte-css `#fffff8` warm white). Used when the host
   * element has no `background` attribute. The canvas color drives the
   * container background, node fills, edge-label halos, and PNG export
   * background so they all stay in sync.
   */
  private static readonly DEFAULT_CANVAS_BG = '#fffff8';

  /**
   * Returns the active canvas color: the host's `background` attribute if
   * set, otherwise the default warm white.
   */
  private getCanvasBackground(): string {
    return this.getAttribute('background') ?? WebColaCnDGraph.DEFAULT_CANVAS_BG;
  }

  /**
   * Default font stack. Atkinson Hyperlegible (Braille Institute, OFL) is
   * designed for low-vision and dyslexic readers — distinguishable letterforms
   * (a/g/q/d, 0/O, 1/l/I) without looking childlike. Falls back to system-ui
   * if the host hasn't loaded the web font.
   */
  private static readonly DEFAULT_FONT_FAMILY = "'Atkinson Hyperlegible', system-ui, -apple-system, sans-serif";

  /**
   * Returns the active font stack: the host's `font-family` attribute if
   * set, otherwise the Atkinson Hyperlegible default.
   */
  private getFontFamily(): string {
    return this.getAttribute('font-family') ?? WebColaCnDGraph.DEFAULT_FONT_FAMILY;
  }

  /**
   * Returns the @import statement(s) needed to load the default font from
   * Google Fonts. Skipped when the host has set a custom `font-family`, since
   * users with their own font shouldn't pay for an unused download.
   */
  private getFontImports(): string {
    if (this.hasAttribute('font-family')) return '';
    return "@import url('https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400;1,700&display=swap');";
  }

  /**
   * Text-sizing constants. Node-label sizes (main + secondary) live in
   * `src/layout/text-extent.ts` so the layout estimator and the renderer
   * agree by construction. The constants below are for non-node text:
   * group labels (DEFAULT/MIN/MAX_FONT_SIZE) and the renderer's inset for
   * text inside node rects (TEXT_PADDING).
   */
  private static readonly DEFAULT_FONT_SIZE = 10;
  private static readonly MIN_FONT_SIZE = 6;
  private static readonly MAX_FONT_SIZE = 16;
  private static readonly TEXT_PADDING = 8;

  /**
   * Configuration constants for screenshot export
   */
  private static readonly SCREENSHOT_SCALE = 3; // 3x for high-def PNG export

  /**
   * Configuration constants for group visualization
   */
  private static readonly DISCONNECTED_NODE_PREFIX = "_d_";
  private static readonly GROUP_BORDER_RADIUS = 8;
  private static readonly GROUP_FILL_OPACITY = 0.10;
  private static readonly GROUP_STROKE_OPACITY = 0.4;
  private static readonly GROUP_STROKE_WIDTH = 1.5;
  private static readonly GROUP_LABEL_PADDING = 20;
  private static readonly GROUP_LABEL_PILL_PADDING_X = 8;
  private static readonly GROUP_LABEL_PILL_PADDING_Y = 3;
  private static readonly GROUP_LABEL_PILL_OFFSET_Y = 4;
  private static readonly DEFAULT_GROUP_COMPACTNESS = 1e-5;

  /**
   * Configuration constants for edge routing
   */
  private static readonly EDGE_ROUTE_MARGIN_DIVISOR = 3;
  private static readonly CURVATURE_BASE_MULTIPLIER = 0.15;
  private static readonly MIN_EDGE_DISTANCE = 10;
  private static readonly MAX_EDGE_OFFSET_RATIO = 0.35;
  private static readonly MAX_EDGE_CURVATURE_RATIO = 0.6;
  private static readonly SELF_LOOP_CURVATURE_SCALE = 0.2;
  private static readonly VIEWBOX_PADDING = 10;

  /**
   * Configuration constants for edge crossing optimization
   */
  private static readonly MAX_CROSSING_OPTIMIZATION_PASSES = 3;
  /**
   * Edge-count gate above which the O(E²) crossing optimizer (and the bend
   * flattener that piggybacks on this threshold) is skipped. Raised from 15 to
   * 50 because at this size the algorithm is still well under the wall-clock
   * budget — at E=50 there are C(50,2)=1225 pairs × ~10 segment-segment tests
   * each (still sub-millisecond on commodity hardware). The hard skip remains
   * past this threshold so worst-case cost stays bounded.
   * See MAX_CROSSING_OPTIMIZATION_BUDGET_MS for the wall-clock safety net.
   */
  private static readonly CROSSING_OPTIMIZATION_EDGE_THRESHOLD = 50;
  /**
   * Wall-clock budget (ms) for the entire optimizeCrossings() pipeline. If
   * detection + resolution exceeds this, we abort and accept the current
   * routing. Acts as a safety net for pathological graphs that fall below
   * the edge-count gate but still take long (e.g. very long polyline routes).
   */
  private static readonly MAX_CROSSING_OPTIMIZATION_BUDGET_MS = 30;
  private static readonly CROSSING_NUDGE_DISTANCE = 12;
  private static readonly PORT_MARGIN_FRACTION = 0.15;
  /**
   * Minimum perimeter distance (px) between adjacent ports on the same node side.
   * Translates to ≈18° angular separation for a node half-dimension of ~30px,
   * which is the threshold above which fanning edges read as visually distinct.
   * Used by computePortMargin() to shrink the side margin when port density is high.
   */
  private static readonly MIN_PORT_PERIMETER_SPACING = 10;
  /**
   * Absolute floor on port-margin (px). Below this, ports start to visually
   * collide with the node's corner radius and arrowheads can splay weirdly.
   * Even on overcrowded nodes, margin is never reduced below this value.
   */
  private static readonly MIN_ABSOLUTE_PORT_MARGIN_PX = 2;
  /**
   * Visible (rendered) inset for group containers, expressed as the inverse
   * of the inflation applied at routing time (see route() — `bounds.inflate(-groupMargin)`).
   * Edges should clip to this visible boundary so arrowheads land on the
   * rendered group rectangle rather than on the inflated WebCola bounds.
   */
  private static readonly GROUP_VISUAL_MARGIN_PX = 10;

  /**
   * Configuration constants for WebCola layout iterations
   * Reduced from previous values (10, 100, 1000, 5) to improve performance
   * and prevent browser timeouts on large graphs
   */
  private static readonly INITIAL_UNCONSTRAINED_ITERATIONS = 10;
  private static readonly INITIAL_USER_CONSTRAINT_ITERATIONS = 50;
  private static readonly INITIAL_ALL_CONSTRAINTS_ITERATIONS = 200;
  private static readonly GRID_SNAP_ITERATIONS = 1; // Reduced from 5 for performance, but kept at 1 for alignment
  private static readonly LOADING_INDICATOR_DELAY_MS = 180;

  /**
   * Base morph transition timing (at speed = 1.0 ≈ 1.1 s total).
   * Exit duration is longer so departing elements have time to fade gracefully.
   * Enter starts after a short delay so exits are underway first.
   * Actual durations are `base × morphSpeed`; default speed is 0.2.
   */
  private static readonly MORPH_BASE_EXIT_DURATION_MS = 1100;
  private static readonly MORPH_BASE_ENTER_DURATION_MS = 1000;
  private static readonly MORPH_BASE_ENTER_DELAY_MS = 200;

  /**
   * Counter for edge routing iterations (for performance tracking)
   */
  private edgeRouteIdx = 0;

  /**
   * Cache for edge routing optimizations
   */
  private edgeRoutingCache: {
    edgesBetweenNodes: Map<string, EdgeWithMetadata[]>;
    alignmentEdges: Set<string>;
    nodeEdgesBySide: Map<string, { top: any[]; bottom: any[]; left: any[]; right: any[] }>;
  } = {
    edgesBetweenNodes: new Map(),
    alignmentEdges: new Set(),
    nodeEdgesBySide: new Map()
  };

  /**
   * Stores computed route arrays between route computation and SVG rendering,
   * enabling post-routing optimization (e.g. crossing reduction).
   */
  private computedRoutes: Map<string, Array<{ x: number; y: number }>> = new Map();

  /**
   * Guard flag to prevent re-entrance during grid routing operations.
   * This prevents infinite loops when gridify is called while already in progress.
   */
  private isGridifyingInProgress: boolean = false;

  // We use these to store state and references.
  private svgNodes : any;
  private svgLinkGroups : any;
  private svgGroups : any;
  private svgGroupLabels: any;
  private svgGroupLabelBgs: any;
  private zoomBehavior: any;
  private storedTransform: any;
  
  /**
   * Line function for grid edges (sharp right angles, no curves)
   */
  private readonly gridLineFunction: d3.Line<{ x: number; y: number }>;
  
  /**
   * Tracks whether the user has manually interacted with zoom/pan.
   * When true, we don't auto-fit the viewport to preserve user's view.
   */
  private userHasManuallyZoomed: boolean = false;
  
  /**
   * Tracks whether this is the initial render (first layout).
   * We always fit viewport on initial render.
   */
  private isInitialRender: boolean = true;
  private loadingShowTimer: number | null = null;
  
  /**
   * Stores the starting coordinates when a node begins dragging so
   * drag end events can report both the previous and new positions.
   */
  private dragStartPositions: Map<string, { x: number; y: number }> = new Map();

  /**
   * Input mode state management for edge creation and modification
   */
  private isInputModeActive: boolean = false;
  private inputModeEnabled: boolean = true;
  private inputModeListenersAttached: boolean = false;
  private readonly handleInputModeKeydown = (event: KeyboardEvent): void => {
    if ((event.metaKey || event.ctrlKey) && !this.isInputModeActive) {
      this.activateInputMode();
    }
  };
  private readonly handleInputModeKeyup = (event: KeyboardEvent): void => {
    if (!event.metaKey && !event.ctrlKey && this.isInputModeActive) {
      this.deactivateInputMode();
    }
  };
  private readonly handleInputModeBlur = (): void => {
    if (this.isInputModeActive) {
      this.deactivateInputMode();
    }
  };
  private edgeCreationState: {
    isCreating: boolean;
    sourceNode: NodeWithMetadata | null;
    temporaryEdge: any;
  } = {
    isCreating: false,
    sourceNode: null,
    temporaryEdge: null
  };

  /**
   * Edge endpoint dragging state for moving edges between nodes
   */
  private edgeDragState: {
    isDragging: boolean;
    edge: EdgeWithMetadata | null;
    endpoint: 'source' | 'target' | null;
    dragMarker: any;
  } = {
    isDragging: false,
    edge: null,
    endpoint: null,
    dragMarker: null
  };

  /**
   * Temporary canvas for text measurement
   */
  private textMeasurementCanvas: HTMLCanvasElement | null = null;

  private get root(): ShadowRoot {
    if (!this.shadowRoot) {
      throw new Error('ShadowRoot not initialized');
    }
    return this.shadowRoot;
  }

  constructor(isInputAllowed: boolean = false) {
    super();

    this.attachShadow({ mode: 'open' });
    this.initializeDOM();
    this.initializeD3();

    // TODO: I'd like to make this better.
    this.lineFunction = d3.line()
      .x((d: any) => d.x)
      .y((d: any) => d.y)
      .curve(d3.curveBasis);

    // Grid line function uses linear interpolation for sharp right-angle turns
    this.gridLineFunction = d3.line()
      .x((d: any) => d.x)
      .y((d: any) => d.y)
      .curve(d3.curveLinear);

    // Initialize input mode keyboard event handlers
    this.inputModeEnabled = isInputAllowed;
    this.initializeInputModeHandlers();
  }

  /**
   * Access the layoutFormat attribute
   */
  private get layoutFormat(): string | null {
    return this.getAttribute('layoutFormat');
  }

  /**
   * Access whether this graph is a graph visualizing an unsat core.
   */
  private get isUnsatCore(): boolean {
    return this.hasAttribute('unsat');
  }

  /**
   * Access transition mode for layout swaps.
   * Supported values:
   * - "morph": exiting elements fade out, entering fade in, continuing slide
   * - "replace": clear and redraw immediately (legacy behavior)
   *
   * Defaults to 'morph' when the attribute is absent.
   */
  private get transitionMode(): WebColaRenderTransitionMode {
    const attrMode = this.getAttribute('transition-mode');
    return attrMode === 'replace' ? 'replace' : 'morph';
  }

  /**
   * Morph speed multiplier (0 – 1).
   *  • 0   = instant (no animation, equivalent to 'replace')
   *  • 0.2 = default (~220 ms exit, ~200 ms enter)
   *  • 1   = slow / dramatic (~1.1 s exit, ~1 s enter)
   *
   * Read from the `morph-speed` attribute; defaults to 0.2.
   */
  private get morphSpeed(): number {
    const raw = parseFloat(this.getAttribute('morph-speed') ?? '');
    if (Number.isFinite(raw)) return Math.max(0, Math.min(raw, 1));
    return 0.2;
  }

  /** Convenience helpers that apply the speed multiplier to base timings. */
  private get morphExitDurationMs(): number {
    return Math.round(WebColaCnDGraph.MORPH_BASE_EXIT_DURATION_MS * this.morphSpeed);
  }
  private get morphEnterDurationMs(): number {
    return Math.round(WebColaCnDGraph.MORPH_BASE_ENTER_DURATION_MS * this.morphSpeed);
  }
  private get morphEnterDelayMs(): number {
    return Math.round(WebColaCnDGraph.MORPH_BASE_ENTER_DELAY_MS * this.morphSpeed);
  }
  /** Duration for the slide animation of continuing nodes (same scale as enter). */
  private get morphSlideDurationMs(): number {
    return Math.round(WebColaCnDGraph.MORPH_BASE_ENTER_DURATION_MS * this.morphSpeed);
  }

  /**
   * Determines if an edge is used for alignment purposes.
   * Alignment edges are identified by IDs starting with "_alignment_".
   * Uses cached set for O(1) lookup performance.
   * 
   * @param edge - Edge object to check
   * @returns True if the edge is an alignment constraint edge
   * 
   * @example
   * ```typescript
   * const alignEdge = { id: "_alignment_nodes_1_2" };
   * const isAlign = this.isAlignmentEdge(alignEdge); // returns true
   * ```
   */
  private isAlignmentEdge(edge: { id: string }): boolean {
    // Use cache if available (during routing), otherwise fall back to string check
    if (this.edgeRoutingCache.alignmentEdges.size > 0) {
      return this.edgeRoutingCache.alignmentEdges.has(edge.id);
    }
    return edge.id.startsWith("_alignment_");
  }

  /**
   * Determines if a node is hidden based on naming convention.
   * Hidden nodes are identified by names starting with underscore.
   * 
   * @param node - Node object to check
   * @returns True if the node should be hidden from display
   */
  private isHiddenNode(node: { name?: string; id?: string }): boolean {
    // Check name first, fall back to id if name is not available
    const identifier = node.name || node.id;
    return identifier ? identifier.startsWith("_") : false;
  }

  /**
   * Determines if a node is an error node.
   * @param node - Node object to check
   * @returns True if the node is in the set of conflicting constraints
   */
  private isErrorNode(node: {name: string, id: string}): boolean {
    // Check if this node appears in any constraint that's in conflictingConstraints
    const conflictingNodes = this.currentLayout.conflictingNodes;

    // Check if this node appear in overlapping nodes
    const overlappingNodes = this.currentLayout.overlappingNodes;

    if (conflictingNodes.length > 0 && overlappingNodes.length > 0) {
      const conflictingNodeIds = conflictingNodes.map(n => n.id);
      const overlappingNodeIds = overlappingNodes.map(n => n.id);
      throw new Error(`Layout cannot have both conflictingConstraints (${conflictingNodeIds}) and overlappingNodes ${overlappingNodeIds}`);
    }

    const errorNodes = [...conflictingNodes, ...overlappingNodes];

    return errorNodes.some((errorNode: LayoutNode) => errorNode.id === node.id); // NOTE: `id` should be unique
  }

  /**
   * Check if a node is considered "small" and needs enhanced visibility
   * Accounts for current zoom level to determine visual size on screen
   * @param node - Node object with dimensions
   * @returns True if the node appears smaller than the threshold on screen
   */
  private isSmallNode(node: any): boolean {
    const minVisualSize = 30; // Minimum visual size threshold in screen pixels
    
    // Get current zoom scale
    let zoomScale = 1;
    if (this.svg && this.svg.node()) {
      try {
        const transform = d3.zoomTransform(this.svg.node());
        zoomScale = transform.k;
      } catch (e) {
        // Fallback to scale 1 if transform is not available
        zoomScale = 1;
      }
    }
    
    // Calculate visual size (coordinate size * zoom scale)
    const visualWidth = ((node as any).visualWidth ?? node.width ?? 0) * zoomScale;
    const visualHeight = ((node as any).visualHeight ?? node.height ?? 0) * zoomScale;
    
    return visualWidth < minVisualSize || visualHeight < minVisualSize;
  }

  /**
   * Update node classes based on current zoom level
   * Called when zoom changes to ensure small error nodes get proper styling
   */
  private updateSmallNodeClasses(): void {
    if (!this.container) return;
    
    // Update all error nodes to check if they should have small-error-node class
    this.container.selectAll('.error-node').each((d: any, i: number, nodes: any[]) => {
      const nodeElement = d3.select(nodes[i]);
      const isSmall = this.isSmallNode(d);
      
      if (isSmall) {
        // Add small-error-node class if not present
        if (!nodeElement.classed('small-error-node')) {
          nodeElement.classed('small-error-node', true);
        }
      } else {
        // Remove small-error-node class if present
        if (nodeElement.classed('small-error-node')) {
          nodeElement.classed('small-error-node', false);
        }
      }
    });
  }

  /**
   * Gets the current zoom scale factor
   * @returns Current zoom scale (k value from transform)
   */
  private getCurrentZoomScale(): number {
    if (this.svg && this.svg.node()) {
      try {
        const transform = d3.zoomTransform(this.svg.node());
        return transform.k;
      } catch (e) {
        return 1;
      }
    }
    return 1;
  }

  private isErrorGroup(_group: {name: string}): boolean {
    // Overlapping groups are now supported and no longer treated as errors.
    return false;
  }

  /**
   * Determines if an edge represents an inferred relationship.
   * Inferred edges are identified by specific properties or naming conventions.
   * 
   * @param edge - Edge object to check
   * @returns True if the edge is an inferred relationship
   * 
   * @example
   * ```typescript
   * const inferredEdge = { isInferred: true };
   * const isInferred = this.isInferredEdge(inferredEdge); // returns true
   * ```
   */
  private isInferredEdge(edge: { id?: string }): boolean {
    const helperPrefix = "_inferred_";

    // Check if the edge contains the helper prefix
    return edge.id ? edge.id.includes(helperPrefix) : false;
  }

  /**
   * Determines if a group represents disconnected nodes.
   * Disconnected node groups are identified by names starting with "_d_".
   * 
   * @param group - Group object to check
   * @returns True if the group contains disconnected nodes
   * 
   * @example
   * ```typescript
   * const disconnectedGroup = { name: "_d_isolated_nodes" };
   * const isDisconnected = this.isDisconnectedGroup(disconnectedGroup); // returns true
   * ```
   */
  private isDisconnectedGroup(group: { name: string }): boolean {
    return group.name.startsWith(WebColaCnDGraph.DISCONNECTED_NODE_PREFIX);
  }

  /**
   * Computes adaptive link length based on actual node dimensions, edge labels, and graph density
   */
  private computeAdaptiveLinkLength(nodes: any[], scaleFactor: number, links?: any[]): number {
    if (!nodes || nodes.length === 0) {
      return 150; // fallback
    }

    // Calculate average node dimensions using visual width/height (not inflated collision bounds)
    let totalWidth = 0;
    let totalHeight = 0;
    let validNodes = 0;

    nodes.forEach(node => {
      if (node && !this.isHiddenNode(node)) {
        totalWidth += (node.visualWidth ?? node.width ?? 100);
        totalHeight += (node.visualHeight ?? node.height ?? 60);
        validNodes++;
      }
    });

    if (validNodes === 0) {
      return 150; // fallback
    }

    const avgWidth = totalWidth / validNodes;
    const avgHeight = totalHeight / validNodes;
    const avgNodeSize = Math.max(avgWidth, avgHeight);

    // Calculate maximum edge label width if links are provided
    let maxLabelWidth = 0;
    if (links && links.length > 0) {
      const fontSize = 12; // Default edge label font size
      links.forEach(link => {
        if (link && link.label) {
          const labelWidth = this.measureTextWidth(link.label, fontSize);
          maxLabelWidth = Math.max(maxLabelWidth, labelWidth);
        }
      });
    }

    // Marker size (arrowhead)
    const markerSize = 15; // Width of the marker as defined in SVG defs

    // Base link length should account for:
    // 1. Average node size
    // 2. Edge label text width
    // 3. Marker (arrowhead) size
    // 4. Additional separation buffer
    const baseSeparation = 50; // minimum separation between nodes
    const labelAndMarkerSpace = maxLabelWidth + markerSize + 20; // 20px buffer
    let baseLinkLength = Math.max(avgNodeSize + baseSeparation + labelAndMarkerSpace, 120);

    // Apply density factor - more nodes = slightly tighter spacing to fit better
    const densityFactor = Math.max(0.7, 1 - Math.log10(validNodes) * 0.1);
    baseLinkLength *= densityFactor;

    // Apply scale factor
    const adjustedScaleFactor = scaleFactor / 5;
    const scaledLinkLength = baseLinkLength / adjustedScaleFactor;

    // Ensure reasonable bounds - prevent tiny edges and excessive spacing
    return Math.max(60, Math.min(scaledLinkLength, 350));
  }

  private getScaledDetails(constraints: any[], scaleFactor: number = DEFAULT_SCALE_FACTOR, nodes?: any[], groups?: any[], links?: any[]) {
    const adjustedScaleFactor = scaleFactor / 5;

    // Calculate adaptive group compactness based on graph structure
    let groupCompactness = this.calculateAdaptiveGroupCompactness(groups || [], nodes?.length || 0, adjustedScaleFactor);

    // Use adaptive link length calculation if nodes are available
    let linkLength: number;
    if (nodes && nodes.length > 0) {
      linkLength = this.computeAdaptiveLinkLength(nodes, scaleFactor, links);
    } else {
      // Fallback to original calculation
      const min_sep = 150;
      const default_node_width = 100;
      linkLength = (min_sep + default_node_width) / adjustedScaleFactor;
    }

    /*
    For each constraint, if it is a separation constraint, adjust the distance by the scale factor.
    */
    function getScaledConstraints(constraints: any[]): any[] {
      return constraints.map(constraint => {
        if (constraint.type === "separation" && typeof constraint.gap === "number") {
          const oldgap = constraint.gap;
          const newgap = oldgap / adjustedScaleFactor; // or * scaleFactor, depending on your UI logic
          //console.log(`Scaling constraint gap from ${oldgap} to ${newgap} with scale factor ${adjustedScaleFactor}`);

          return {
            ...constraint,
            gap: newgap
          };
        }
        return constraint;
      });
    }

    return {
      scaledConstraints: getScaledConstraints(constraints),
      linkLength: linkLength,
      groupCompactness: groupCompactness
    }
  }

  /**
   * Calculate adaptive group compactness based on graph characteristics.
   * 
   * This helps reduce jitter in layouts with complex group structures by:
   * - Increasing compactness for deeply nested groups (stronger group boundaries)
   * - Increasing compactness when there are many groups relative to nodes
   * - Using default compactness for simple group structures
   * 
   * Higher compactness values create stronger attraction between group boundaries,
   * which helps groups stabilize faster and reduces oscillation.
   * 
   * @param groups - Array of group definitions
   * @param nodeCount - Total number of nodes in the graph
   * @param scaleFactor - Current scale factor adjustment
   * @returns Calculated group compactness value
   */
  private calculateAdaptiveGroupCompactness(groups: any[], nodeCount: number, scaleFactor: number): number {
    const DEFAULT_GROUP_COMPACTNESS = WebColaCnDGraph.DEFAULT_GROUP_COMPACTNESS * scaleFactor;
    
    if (!groups || groups.length === 0) {
      return DEFAULT_GROUP_COMPACTNESS;
    }
    
    // Calculate maximum nesting depth
    const maxDepth = this.calculateMaxGroupDepth(groups);
    
    // Calculate group-to-node ratio
    const groupRatio = groups.length / Math.max(nodeCount, 1);
    
    // Base compactness
    let compactness = DEFAULT_GROUP_COMPACTNESS;
    
    // Increase compactness for deeply nested groups (depth > 2)
    // Deeper nesting requires stronger group boundaries to prevent jitter
    if (maxDepth > 2) {
      compactness *= 10; // 10x stronger for deeply nested groups
    } else if (maxDepth > 1) {
      compactness *= 5; // 5x stronger for moderately nested groups
    }
    
    // Increase compactness when there are many groups relative to nodes
    // Many groups can create conflicting constraints that cause oscillation
    if (groupRatio > 0.3) {
      compactness *= 2; // Double strength for high group density
    }
    
    return compactness;
  }

  /**
   * Convert screen viewport bounds to layout-coordinate bounds using zoom transform.
   */
  private getViewportBoundsInLayoutSpace(transform: TransformInfo): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } | undefined {
    const svgContainer = this.shadowRoot?.querySelector('#svg-container') as HTMLElement | null;
    const viewportWidth = svgContainer?.clientWidth || WebColaCnDGraph.DEFAULT_SVG_WIDTH;
    const viewportHeight = svgContainer?.clientHeight || WebColaCnDGraph.DEFAULT_SVG_HEIGHT;

    if (viewportWidth <= 0 || viewportHeight <= 0) {
      return undefined;
    }

    const k = transform.k;
    if (!Number.isFinite(k) || k === 0) {
      return undefined;
    }

    const tx = Number.isFinite(transform.x) ? transform.x : 0;
    const ty = Number.isFinite(transform.y) ? transform.y : 0;

    return {
      minX: (0 - tx) / k,
      maxX: (viewportWidth - tx) / k,
      minY: (0 - ty) / k,
      maxY: (viewportHeight - ty) / k,
    };
  }

  /**
   * Returns true when a transform can be safely reapplied for viewport continuity.
   */
  private hasValidTransform(transform?: TransformInfo): boolean {
    if (!transform) return false;
    return Number.isFinite(transform.k) && transform.k > 0 &&
      Number.isFinite(transform.x) && Number.isFinite(transform.y);
  }

  /**
   * Applies viewport continuity rules before a new render.
   * Temporal transitions should preserve the prior transform even when
   * there are few/no reusable node positions.
   */
  private applyViewportRenderPolicy(hasPriorPositions: boolean, hasPriorTransform: boolean): void {
    if (hasPriorTransform) {
      // Prevent automatic fit from overriding temporal viewport continuity.
      this.isInitialRender = false;
      this.userHasManuallyZoomed = true;
      return;
    }

    if (!hasPriorPositions) {
      this.isInitialRender = true;
      this.userHasManuallyZoomed = false;
    }
  }

  /**
   * Builds the raw prior state used by sequence policies.
   * For temporal transitions we anchor the transform to the live viewport at
   * render-start time when a previous layout is currently displayed.
   */
  private buildPolicyRawState(options?: WebColaLayoutOptions): LayoutState {
    const liveStateAtChange = this.getLayoutState();
    const priorState = options?.priorPositions ?? liveStateAtChange;
    const hasPolicyContext = !!(options?.policy && options.prevInstance && options.currInstance);
    const hasLiveLayout = !!(this.currentLayout?.nodes && this.currentLayout.nodes.length > 0);

    if (
      hasPolicyContext &&
      hasLiveLayout &&
      this.hasValidTransform(liveStateAtChange.transform)
    ) {
      return {
        positions: priorState.positions,
        transform: liveStateAtChange.transform,
      };
    }

    return priorState;
  }

  /**
   * Calculate the maximum nesting depth of groups.
   * Depth 1 = groups with only leaf nodes
   * Depth 2 = groups containing other groups
   * And so on...
   * 
   * @param groups - Array of group definitions
   * @returns Maximum nesting depth
   */
  private calculateMaxGroupDepth(groups: any[]): number {
    if (!groups || groups.length === 0) return 0;
    
    let maxDepth = 1;
    
    for (const group of groups) {
      if (group.groups && Array.isArray(group.groups) && group.groups.length > 0) {
        // This group contains subgroups
        // Safely map indices to groups, filtering out invalid indices
        const validSubgroups = group.groups
          .filter((idx: number) => typeof idx === 'number' && idx >= 0 && idx < groups.length)
          .map((idx: number) => groups[idx])
          .filter((g: any) => g != null);
        
        if (validSubgroups.length > 0) {
          const subgroupDepth = 1 + this.calculateMaxGroupDepth(validSubgroups);
          maxDepth = Math.max(maxDepth, subgroupDepth);
        }
      }
    }
    
    return maxDepth;
  }

  /**
   * Initialize the Shadow DOM structure
   */
  private initializeDOM(): void {
    this.root.innerHTML = `
      <style>
      ${this.getCSS()}
      </style>
      <div id="graph-toolbar">
        <div id="zoom-controls">
          <button id="zoom-in" title="Zoom In" aria-label="Zoom in">+</button>
          <button id="zoom-out" title="Zoom Out" aria-label="Zoom out">−</button>
          <button id="zoom-fit" title="Fit to View" aria-label="Fit graph to view">⤢</button>
        </div>
        <div id="routing-control">
          <label for="routing-mode">Routing:</label>
          <select id="routing-mode" title="Edge routing mode">
            <option value="default">Default</option>
            <option value="grid">Grid</option>
          </select>
        </div>
        <div id="screenshot-control">
          <button id="screenshot-btn" title="Download high-res PNG screenshot" aria-label="Screenshot graph">⬇</button>
        </div>
      </div>
      <div id="svg-container">
      <span id="error-icon" title="This graph is depicting an error state">⚠️</span>
      <div id="loading" role="status" aria-live="polite" aria-atomic="true">
        <span class="loading-dot" aria-hidden="true"></span>
        <span id="loading-progress">Computing layout...</span>
      </div>
      <svg id="svg">
        <defs>
        <marker id="end-arrow" markerWidth="12" markerHeight="8" refX="12" refY="4" orient="auto" markerUnits="userSpaceOnUse">
          <polygon points="0 0, 12 4, 0 8, 3 4" fill="context-stroke" />
        </marker>
        <marker id="start-arrow" markerWidth="12" markerHeight="8" refX="2" refY="4" orient="auto" markerUnits="userSpaceOnUse">
          <polygon points="12 0, 0 4, 12 8, 9 4" fill="context-stroke" />
        </marker>
        </defs>
        <g class="zoomable"></g>
      </svg>
      </div>
      <div id="error" style="display: none; color: red;"></div>
    `;
  }

  /**
   * Initialize D3 selections and zoom behavior
   */
  private initializeD3(): void {
    
    if (!d3) {
      d3 = window.d3;
    }

    this.svg = d3.select(this.root.querySelector('#svg'));
    this.container = this.svg.select('.zoomable');

    if(d3.zoom) {

    // Set up zoom behavior (D3 v4 API - matches your working pattern)
    this.zoomBehavior = d3.zoom()
      .scaleExtent([0.01, 20])
      .on('start', () => {
        // Only mark as user interaction if it's from mouse/touch (not programmatic)
        // d3.event.sourceEvent is null for programmatic zooms
        if (d3.event.sourceEvent) {
          this.userHasManuallyZoomed = true;
        }
      })
      .on('zoom', () => {
        this.container.attr('transform', d3.event.transform);
        // Update zoom control states when zoom changes
        this.updateZoomControlStates();
        // Update small node classes based on new zoom level
        this.updateSmallNodeClasses();
      });

    this.svg.call(this.zoomBehavior);
    
    // Set up zoom control event listeners
    this.initializeZoomControls();
    }
    else {
      console.warn('D3 zoom behavior not available. Ensure D3 v4+ is loaded.');
    }
  }

  /**
   * Initialize zoom control event listeners
   */
  private initializeZoomControls(): void {
    const zoomInButton = this.root.querySelector('#zoom-in') as HTMLButtonElement;
    const zoomOutButton = this.root.querySelector('#zoom-out') as HTMLButtonElement;
    const zoomFitButton = this.root.querySelector('#zoom-fit') as HTMLButtonElement;

    if (zoomInButton) {
      zoomInButton.addEventListener('click', () => {
        this.userHasManuallyZoomed = true; // Mark as user interaction
        this.zoomIn();
      });
    }

    if (zoomOutButton) {
      zoomOutButton.addEventListener('click', () => {
        this.userHasManuallyZoomed = true; // Mark as user interaction
        this.zoomOut();
      });
    }
    
    if (zoomFitButton) {
      zoomFitButton.addEventListener('click', () => {
        this.resetViewToFitContent();
      });
    }

    // Set up routing mode dropdown
    const routingModeSelect = this.root.querySelector('#routing-mode') as HTMLSelectElement;
    if (routingModeSelect) {
      // Set initial value from attribute
      const currentFormat = this.layoutFormat || 'default';
      routingModeSelect.value = currentFormat;
      
      routingModeSelect.addEventListener('change', () => {
        this.handleRoutingModeChange(routingModeSelect.value);
      });
    }

    // Set up screenshot button
    const screenshotButton = this.root.querySelector('#screenshot-btn') as HTMLButtonElement;
    if (screenshotButton) {
      screenshotButton.addEventListener('click', () => {
        this.takeScreenshot();
      });
    }

    // Initial state update
    this.updateZoomControlStates();
  }

  /**
   * Handle routing mode change from dropdown
   */
  private handleRoutingModeChange(mode: string): void {
    // Update the layoutFormat attribute
    this.setAttribute('layoutFormat', mode);
    
    // Trigger re-routing if layout is already rendered
    if (this.currentLayout && this.colaLayout) {
      if (mode === 'grid') {
        // Apply grid routing
        this.gridify(10, 25, 10);
      } else {
        // Apply default routing
        this.routeEdges();
      }
      
      // Dispatch event for external listeners
      this.dispatchEvent(new CustomEvent('routing-mode-changed', {
        detail: { mode }
      }));
    }
  }

  /**
   * Update the routing mode dropdown to match current layoutFormat attribute
   */
  private updateRoutingModeDropdown(): void {
    const routingModeSelect = this.shadowRoot?.querySelector('#routing-mode') as HTMLSelectElement;
    if (routingModeSelect) {
      const currentFormat = this.layoutFormat || 'default';
      routingModeSelect.value = currentFormat;
    }
  }

  /**
   * Initialize keyboard event handlers for input mode activation
   */
  private initializeInputModeHandlers(): void {
    if (this.inputModeEnabled) {
      this.attachInputModeListeners();
    }
  }

  private attachInputModeListeners(): void {
    if (this.inputModeListenersAttached) {
      return;
    }
    document.addEventListener('keydown', this.handleInputModeKeydown);
    document.addEventListener('keyup', this.handleInputModeKeyup);
    window.addEventListener('blur', this.handleInputModeBlur);
    this.inputModeListenersAttached = true;
  }

  private detachInputModeListeners(): void {
    if (!this.inputModeListenersAttached) {
      return;
    }
    document.removeEventListener('keydown', this.handleInputModeKeydown);
    document.removeEventListener('keyup', this.handleInputModeKeyup);
    window.removeEventListener('blur', this.handleInputModeBlur);
    this.inputModeListenersAttached = false;
  }

  /**
   * Activate input mode for edge creation and modification
   */
  private activateInputMode(): void {
    this.isInputModeActive = true;
    
    // Add input-mode class to SVG for styling
    if (this.svg) {
      this.svg.classed('input-mode', true);
    }

    // Disable node dragging and zoom/translate
    this.disableNodeDragging();
    this.disableZoom();

    // Update edge endpoint markers visibility
    this.updateEdgeEndpointMarkers();

    // Dispatch event for external listeners
    this.dispatchEvent(new CustomEvent('input-mode-activated', {
      detail: { active: true }
    }));
  }

  /**
   * Deactivate input mode and restore normal behavior
   */
  private deactivateInputMode(): void {
    this.isInputModeActive = false;
    
    // Remove input-mode class from SVG
    if (this.svg) {
      this.svg.classed('input-mode', false);
    }

    // Clean up any temporary edge creation state
    this.cleanupEdgeCreation();

    // Re-enable node dragging and zoom/translate
    this.enableNodeDragging();
    this.enableZoom();

    // Update edge endpoint markers visibility
    this.updateEdgeEndpointMarkers();

    // Dispatch event for external listeners
    this.dispatchEvent(new CustomEvent('input-mode-deactivated', {
      detail: { active: false }
    }));
  }

  /**
   * Disable node dragging when in input mode
   */
  private disableNodeDragging(): void {
    if (this.svgNodes && this.colaLayout) {
      this.svgNodes.on('.drag', null);
    }
  }

  /**
   * Re-enable node dragging when exiting input mode
   */
  private enableNodeDragging(): void {
    if (this.svgNodes && this.colaLayout && this.colaLayout.drag) {
      const nodeDrag = this.colaLayout.drag();
      this.setupNodeDragHandlers(nodeDrag);
      this.svgNodes.call(nodeDrag);
    }
  }

  /**
   * Disable zoom/translate functionality when in input mode
   */
  private disableZoom(): void {
    if (this.svg && this.zoomBehavior) {
      // Store current transform before disabling
      this.storedTransform = d3.zoomTransform(this.svg.node());
      // Disable zoom events but preserve the behavior
      this.svg.on('.zoom', null);
    }
  }

  /**
   * Re-enable zoom/translate functionality when exiting input mode
   */
  private enableZoom(): void {
    if (this.svg && this.zoomBehavior) {
      // Re-enable zoom behavior
      this.svg.call(this.zoomBehavior);
      // Restore the previous transform if we had one
      if (this.storedTransform) {
        this.svg.call(this.zoomBehavior.transform, this.storedTransform);
      }
    }
  }

  /**
   * Zoom in by a fixed scale factor
   */
  private zoomIn(): void {
    if (this.svg && this.zoomBehavior) {
      this.svg.transition().duration(200).call(
        this.zoomBehavior.scaleBy, 1.5
      );
    }
  }

  /**
   * Zoom out by a fixed scale factor
   */
  private zoomOut(): void {
    if (this.svg && this.zoomBehavior) {
      this.svg.transition().duration(200).call(
        this.zoomBehavior.scaleBy, 1 / 1.5
      );
    }
  }

  /**
   * Update zoom control button states based on current zoom level
   */
  private updateZoomControlStates(): void {
    if (!this.svg || !this.zoomBehavior) return;

    const currentTransform = d3.zoomTransform(this.svg.node());
    const currentScale = currentTransform.k;
    const [minScale, maxScale] = this.zoomBehavior.scaleExtent();

    const zoomInButton = this.root.querySelector('#zoom-in') as HTMLButtonElement;
    const zoomOutButton = this.root.querySelector('#zoom-out') as HTMLButtonElement;

    if (zoomInButton) {
      zoomInButton.disabled = currentScale >= maxScale;
    }
    
    if (zoomOutButton) {
      zoomOutButton.disabled = currentScale <= minScale;
    }
  }

  /**
   * Clean up temporary edge creation state
   */
  private cleanupEdgeCreation(): void {
    // Remove temporary edge if it exists
    if (this.edgeCreationState.temporaryEdge) {
      this.edgeCreationState.temporaryEdge.remove();
    }

    // Reset edge creation state
    this.edgeCreationState = {
      isCreating: false,
      sourceNode: null,
      temporaryEdge: null
    };
  }

  /**
   * Setup drag handlers for nodes
   */
  private setupNodeDragHandlers(nodeDrag: any): void {
    nodeDrag
      .on('start.cnd', (d: any) => {
        // Mark that user has interacted with the layout - prevents auto-fitting
        this.userHasManuallyZoomed = true;
        
        const start = { x: d.x, y: d.y };
        this.dragStartPositions.set(d.id, start);
        this.dispatchEvent(
          new CustomEvent('node-drag-start', {
            detail: { id: d.id, position: start }
          })
        );
      })
      .on('end.cnd', (d: any) => {
        const start = this.dragStartPositions.get(d.id);
        this.dragStartPositions.delete(d.id);
        const detail = {
          id: d.id,
          previous: start,
          current: { x: d.x, y: d.y }
        };
        this.dispatchEvent(new CustomEvent('node-drag-end', { detail }));
      });
  }

  /**
   * Start edge creation from a source node
   */
  private startEdgeCreation(sourceNode: NodeWithMetadata): void {
    if (!this.isInputModeActive) return;

    // Clean up any existing edge creation
    this.cleanupEdgeCreation();

    // Set edge creation state
    this.edgeCreationState.isCreating = true;
    this.edgeCreationState.sourceNode = sourceNode;

    // Create temporary edge line
    this.edgeCreationState.temporaryEdge = this.container
      .append('line')
      .attr('class', 'temporary-edge')
      .attr('x1', sourceNode.x)
      .attr('y1', sourceNode.y)
      .attr('x2', sourceNode.x)
      .attr('y2', sourceNode.y)
      .attr('stroke', '#007bff')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5')
      .attr('opacity', 0.7);

    // Add mousemove listener for temporary edge visualization
    this.svg.on('mousemove.edgecreation', () => {
      if (this.edgeCreationState.isCreating && this.edgeCreationState.temporaryEdge) {
        const [mouseX, mouseY] = d3.mouse(this.container.node());
        this.edgeCreationState.temporaryEdge
          .attr('x2', mouseX)
          .attr('y2', mouseY);
      }
    });
  }

  /**
   * Finish edge creation by connecting to a target node
   */
  private async finishEdgeCreation(targetNode: NodeWithMetadata): Promise<void> {
    if (!this.isInputModeActive || !this.edgeCreationState.isCreating || !this.edgeCreationState.sourceNode) {
      return;
    }

    const sourceNode = this.edgeCreationState.sourceNode;

    // Confirm self-loop edges
    if (sourceNode.id === targetNode.id) {
      const confirmSelfLoop = await this.showConfirmDialog(
        `Are you sure you want to create a self-loop edge on "${sourceNode.label || sourceNode.id}"?`
      );
      if (!confirmSelfLoop) {
        this.cleanupEdgeCreation();
        return;
      }
    }

    // Clean up temporary edge visualization
    this.svg.on('mousemove.edgecreation', null);

    // Show edge label input dialog
    await this.showEdgeLabelInput(sourceNode, targetNode);
  }

  /**
   * Show edge label input dialog and create the edge
   */
  private async showEdgeLabelInput(sourceNode: NodeWithMetadata, targetNode: NodeWithMetadata): Promise<void> {
    const label = await this.showPromptDialog(
      `Enter label for edge from "${sourceNode.label || sourceNode.id}" to "${targetNode.label || targetNode.id}":`,
      ''
    );
    
    if (label !== null) { // User didn't cancel
      await this.createNewEdge(sourceNode, targetNode, label || '');
    }

    // Clean up edge creation state
    this.cleanupEdgeCreation();
  }

  /**
   * Create a new edge between two nodes
   */
  private async createNewEdge(sourceNode: NodeWithMetadata, targetNode: NodeWithMetadata, label: string): Promise<void> {
    if (!this.currentLayout) return;

    // Find node indices in the current layout
    const sourceIndex = this.currentLayout.nodes.findIndex(node => node.id === sourceNode.id);
    const targetIndex = this.currentLayout.nodes.findIndex(node => node.id === targetNode.id);

    if (sourceIndex === -1 || targetIndex === -1) {
      console.error('Could not find node indices for edge creation');
      return;
    }

    // Generate unique edge ID
    const edgeId = `edge_${sourceNode.id}_${targetNode.id}_${Date.now()}`;

    // Create new edge object
    const newEdge: EdgeWithMetadata = {
      id: edgeId,
      source: sourceIndex,
      target: targetIndex,
      label: label,
      relName: label,
      color: '#333',
      isUserCreated: true
    } as EdgeWithMetadata;

    // Add edge to current layout
    this.currentLayout.links.push(newEdge);

    // Update external state with the new edge
    await this.updateExternalStateForNewEdge(sourceNode, targetNode, label);

    // Dispatch event for external listeners
    this.dispatchEvent(new CustomEvent('edge-created', {
      detail: { 
        edge: newEdge,
        sourceNode: sourceNode,
        targetNode: targetNode
      }
    }));

    // Re-render the graph to show the new edge
    this.rerenderGraph();
  }

  /**
   * Update external state for a new edge through the external state management system
   * @param sourceNode - Source node of the edge
   * @param targetNode - Target node of the edge 
   * @param relationName - Name/label of the relation
   */
  private async updateExternalStateForNewEdge(sourceNode: NodeWithMetadata, targetNode: NodeWithMetadata, relationName: string): Promise<void> {
    if (!relationName.trim()) {
      return;
    }

    try {
      // Create a tuple representing the edge/relation
      const tuple: ITuple = {
        atoms: [sourceNode.id, targetNode.id],
        types: [sourceNode.type || 'untyped', targetNode.type || 'untyped']
      };

      console.log(`Dispatching edge creation request: ${relationName}(${sourceNode.id}, ${targetNode.id})`);
      
      // Dispatch edge creation event for React components to handle
      const edgeCreationEvent = new CustomEvent('edge-creation-requested', {
        detail: {
          relationId: relationName,
          sourceNodeId: sourceNode.id,
          targetNodeId: targetNode.id,
          tuple: tuple
        },
        bubbles: true
      });
      this.dispatchEvent(edgeCreationEvent);
    } catch (error) {
      console.error('Failed to update external state for new edge:', error);
    }
  }

  /**
   * Re-render the graph with current layout data.
   *
   * Applies current node positions immediately without restarting the
   * solver.  When a StructuredInputGraph event handler is active, its
   * enforceConstraintsAndRegenerate() will do a full layout solve
   * afterward via renderLayout().  Restarting the old solver here would
   * leave rAF tick callbacks that race with the new solver and corrupt
   * positions / cause visual flicker.
   */
  protected rerenderGraph(): void {
    if (!this.currentLayout || !this.colaLayout) return;

    // Update links in the layout
    this.colaLayout.links(this.currentLayout.links);

    // Re-render links
    this.container.selectAll('.link-group').remove();
    this.renderLinks(this.currentLayout.links, this.colaLayout);

    // Position elements at their current coordinates so the user gets
    // immediate visual feedback.  Do NOT call this.colaLayout.start() —
    // that schedules rAF tick callbacks on the OLD solver which race
    // with the new solver that renderLayout() creates moments later.
    this.updatePositions();
  }

  /**
   * Edit the label of an existing edge
   */
  private async editEdgeLabel(edgeData: EdgeWithMetadata): Promise<void> {
    if (!this.isInputModeActive) return;

    // Use relName for data-instance lookups; fall back to label for display.
    const currentRelName = edgeData.relName || edgeData.label || '';
    const displayLabel = edgeData.label || edgeData.relName || '';
    const result = await this.showEdgeEditDialog(`Edit edge label:`, displayLabel);

    // Handle deletion request
    if (result === 'DELETE') {
      await this.deleteEdge(edgeData);
      return;
    }

    // Handle label change
    if (result !== null && result !== displayLabel) {
      const newLabel = result;

      // Get source and target nodes for data instance update
      const sourceNode = this.getNodeFromEdge(edgeData, 'source');
      const targetNode = this.getNodeFromEdge(edgeData, 'target');

      // Update external state using relation name (not display label)
      await this.updateExternalStateForEdgeModification(sourceNode, targetNode, currentRelName, newLabel);

      // Update edge data
      edgeData.label = newLabel;
      edgeData.relName = newLabel;

      // Dispatch event for external listeners
      this.dispatchEvent(new CustomEvent('edge-modified', {
        detail: { 
          edge: edgeData,
          oldLabel: currentLabel,
          newLabel: newLabel
        }
      }));

      // Re-render to show updated label
      this.rerenderGraph();
    }
  }

  /**
   * Get node from edge data based on source or target
   * @param edgeData - Edge data
   * @param position - 'source' or 'target'
   * @returns Node data or null
   */
  private getNodeFromEdge(edgeData: EdgeWithMetadata, position: 'source' | 'target'): NodeWithMetadata | null {
    if (!this.currentLayout) return null;
    
    const nodeIndex = typeof edgeData[position] === 'number' ? edgeData[position] : edgeData[position].index;
    return this.currentLayout.nodes[nodeIndex] || null;
  }

  /**
   * Update external state for an edge modification through the external state management system
   * @param sourceNode - Source node of the edge
   * @param targetNode - Target node of the edge 
   * @param oldRelationName - Old relation name/label
   * @param newRelationName - New relation name/label
   */
  private async updateExternalStateForEdgeModification(
    sourceNode: NodeWithMetadata | null, 
    targetNode: NodeWithMetadata | null, 
    oldRelationName: string, 
    newRelationName: string
  ): Promise<void> {
    if (!sourceNode || !targetNode) {
      return;
    }

    try {
      // Create tuple for the relation
      const tuple: ITuple = {
        atoms: [sourceNode.id, targetNode.id],
        types: [sourceNode.type || 'untyped', targetNode.type || 'untyped']
      };

      console.log(`Dispatching edge modification request: ${oldRelationName} -> ${newRelationName}`);

      // Dispatch edge modification event for React components to handle
      const edgeModificationEvent = new CustomEvent('edge-modification-requested', {
        detail: {
          oldRelationId: oldRelationName,
          newRelationId: newRelationName,
          sourceNodeId: sourceNode.id,
          targetNodeId: targetNode.id,
          tuple: tuple
        },
        bubbles: true
      });
      this.dispatchEvent(edgeModificationEvent);
    } catch (error) {
      console.error('Failed to update external state for edge modification:', error);
    }
  }

  /**
   * Resolve the effective transition mode for a given render call.
   *
   * Priority order:
   * 1. Explicit `options.transitionMode`
   * 2. Element-level `transition-mode` attribute
   * 3. Auto-detect: if a sequence policy is provided, default to 'morph'; else 'replace'
   */
  private resolveTransitionMode(options?: WebColaLayoutOptions): WebColaRenderTransitionMode {
    if (options?.transitionMode === 'replace') return 'replace';
    if (options?.transitionMode === 'morph') return 'morph';

    const attrMode = this.getAttribute('transition-mode');
    if (attrMode === 'replace') return 'replace';
    if (attrMode === 'morph') return 'morph';

    // Auto: morph when a sequence policy is provided, replace otherwise.
    return options?.policy ? 'morph' : 'replace';
  }

  /**
   * Render layout using WebCola constraint solver.
   *
   * @param instanceLayout - The layout instance to render
   * @param options - Optional; pass a `policy` with `prevInstance` / `currInstance`
   *   for sequence continuity.  Omit entirely for a fresh layout.
   *
   * @example
   * ```typescript
   * // Fresh
   * await graph.renderLayout(layout);
   *
   * // Policy-driven (graph captures its own state)
   * await graph.renderLayout(nextLayout, {
   *   policy: stability,
   *   prevInstance: prev,
   *   currInstance: curr,
   * });
   *
   * // Policy-driven with explicit prior positions
   * await graph.renderLayout(nextLayout, {
   *   policy: stability,
   *   prevInstance: prev,
   *   currInstance: curr,
   *   priorPositions: graph.getLayoutState(),
   * });
   * ```
   */
  public async renderLayout(instanceLayout: InstanceLayout, options?: WebColaLayoutOptions): Promise<void> {

    if (! isInstanceLayout(instanceLayout)) {
      throw new Error('Invalid instance layout provided. Expected an InstanceLayout instance.');
    }

    const transitionMode = this.resolveTransitionMode(options);
    const shouldMorph = transitionMode === 'morph';
    const shouldShowLoadingOverlay = transitionMode === 'replace';

    // ── Resolve effective prior state via policy ────────────────────────
    // If a policy + instance pair is provided, run the policy to decide
    // what prior positions (if any) reach the solver.
    let resolvedState: LayoutState | undefined;
    let useReducedIterations = false;

    if (options?.policy && options.prevInstance && options.currInstance) {
      const rawState = this.buildPolicyRawState(options);
      const viewportBounds = this.getViewportBoundsInLayoutSpace(rawState.transform);
      const result = options.policy.apply({
        priorState: rawState,
        prevInstance: options.prevInstance,
        currInstance: options.currInstance,
        spec: { constraints: { orientation: { relative: [], cyclic: [] }, alignment: [], grouping: { groups: [], subgroups: [] } }, directives: { sizes: [], hiddenAtoms: [], icons: [], projections: [], edgeStyles: [] } } as any,
        viewportBounds,
      });
      resolvedState = result.effectivePriorState;
      useReducedIterations = result.useReducedIterations;
      // Capture by reference only — production users who never call
      // getLastSeedState pay nothing. The policy's effectivePriorState
      // is a freshly constructed object, so retaining the reference is
      // safe; the deep-clone for immutability happens lazily in
      // getLastSeedState below.
      this.lastSeedState = resolvedState ?? null;
    } else if (options?.priorPositions) {
      // Direct prior-positions path (no policy): warm-start the solver from the
      // caller-supplied positions and use reduced iterations so existing nodes
      // barely drift. This makes incremental re-renders (e.g. StructuredInputGraph
      // adding an atom) feel stable — nodes stay where the user left them while
      // the solver handles the new element and re-satisfies constraints.
      resolvedState = options.priorPositions;
      useReducedIterations = true;
    } else if (shouldMorph && this.currentLayout?.nodes?.length) {
      // Default continuity path: when callers re-render without explicitly passing
      // prior state, warm-start from the current on-screen layout so transitions
      // can morph instead of popping to a fresh solve.
      const liveState = this.getLayoutState();
      if (liveState.positions.length > 0) {
        resolvedState = liveState;
        useReducedIterations = true;
      }
    }

    const hasPriorPositions = !!(resolvedState && resolvedState.positions.length > 0);
    const hasPriorTransform = this.hasValidTransform(resolvedState?.transform);
    // Build the options the translator will see.
    // lockUnconstrainedNodes is gated on useReducedIterations so that only
    // stability-mode layouts lock nodes; morph transitions keep nodes free.
    const translatorOptions: WebColaLayoutOptions | undefined = hasPriorPositions
      ? { priorPositions: resolvedState, lockUnconstrainedNodes: useReducedIterations }
      : undefined;

    this.applyViewportRenderPolicy(hasPriorPositions, hasPriorTransform);
    
    // Handle zoom transform based on whether we have prior transform to restore
    if (this.svg && this.zoomBehavior && d3) {
      try {
        if (hasPriorTransform) {
          // Restore the prior transform to maintain visual continuity
          const priorTransform = resolvedState!.transform;
          const transform = d3.zoomIdentity
            .translate(priorTransform.x, priorTransform.y)
            .scale(priorTransform.k);
          this.svg.call(this.zoomBehavior.transform, transform);
        } else if (!hasPriorPositions) {
          // Reset zoom transform to identity for a fresh start (will be adjusted by fitViewportToContent)
          const identity = d3.zoomIdentity;
          this.svg.call(this.zoomBehavior.transform, identity);
        }
      } catch (error) {
        console.warn('Failed to set zoom transform:', error);
      }
    }

    try {

      // Check if D3 and WebCola are available
      if (!d3) {
        throw new Error('D3 library not available. Please ensure D3 v4 is loaded from CDN.');
      }
      if (!cola) {
        if(!window.cola) {

          throw new Error('WebCola library not available. Please ensure vendor/cola.js is loaded.');
        }
        cola = window.cola;
      }

      // Ensure D3 and container are properly initialized
      if (!this.container || !this.svg) {

        this.initializeD3();
      }
      
      // Double-check that container is now available
      if (!this.container) {
        throw new Error('Failed to initialize D3 container. SVG elements may not be available.');
      }

      if (shouldShowLoadingOverlay) {
        this.showLoading();
        this.updateLoadingProgress('Translating layout...');
      } else {
        // Ensure any stale loading state from a prior render is fully cleared.
        this.hideLoading();
      }

      // Get actual container dimensions for responsive layout
      const svgContainer = this.root.querySelector('#svg-container') as HTMLElement;
      const containerRect = svgContainer.getBoundingClientRect();
      const containerWidth = containerRect.width || 800; // fallback to default
      const containerHeight = containerRect.height || 600; // fallback to default

      // Translate to WebCola format with actual container dimensions
      const translator = new WebColaTranslator();
      const webcolaLayout = await translator.translate(instanceLayout, containerWidth, containerHeight, translatorOptions);

      if (shouldShowLoadingOverlay) {
        this.updateLoadingProgress(`Computing layout for ${webcolaLayout.nodes.length} nodes...`);
      }

      // Adaptive iteration counts based on graph size for better performance
      // For small graphs, use default values. For large graphs, reduce iterations.
      const nodeCount = webcolaLayout.nodes.length;
      let unconstrainedIters = WebColaCnDGraph.INITIAL_UNCONSTRAINED_ITERATIONS;
      let userConstraintIters = WebColaCnDGraph.INITIAL_USER_CONSTRAINT_ITERATIONS;
      let allConstraintIters = WebColaCnDGraph.INITIAL_ALL_CONSTRAINTS_ITERATIONS;
      
      // When prior state is provided and the policy requests reduced iterations,
      // minimize solver iterations to preserve node positions.
      // WebCola's unconstrained phase allows nodes to move freely from their initial positions,
      // so minimizing this phase helps preserve the provided positions.
      //
      // The policy controls this via useReducedIterations — a policy can return
      // positions while requesting normal iterations (useReducedIterations: false)
      // to let the solver fully re-converge.
      if (hasPriorPositions && useReducedIterations) {
        // Use minimal iterations to preserve prior positions:
        // - 0 unconstrained: don't let nodes drift from prior positions
        // - 10 user constraint: apply position constraints quickly
        // - 20 all constraints: final constraint satisfaction with overlap avoidance
        unconstrainedIters = 0;
        userConstraintIters = Math.min(10, userConstraintIters);
        allConstraintIters = Math.min(20, allConstraintIters);
        
      }
      
      if (nodeCount > 100) {
        // For large graphs (>100 nodes), reduce iterations more aggressively
        unconstrainedIters = Math.max(hasPriorPositions ? 0 : 5, Math.floor(unconstrainedIters * 0.5));
        userConstraintIters = Math.max(25, Math.floor(userConstraintIters * 0.5));
        allConstraintIters = Math.max(100, Math.floor(allConstraintIters * 0.5));
      } else if (nodeCount > 50) {
        // For medium graphs (>50 nodes), reduce iterations moderately
        unconstrainedIters = Math.max(hasPriorPositions ? 0 : 8, Math.floor(unconstrainedIters * 0.8));
        userConstraintIters = Math.max(40, Math.floor(userConstraintIters * 0.8));
        allConstraintIters = Math.max(150, Math.floor(allConstraintIters * 0.75));
      }


      // Get scaled constraints, link length, and adaptive group compactness
      const { scaledConstraints, linkLength, groupCompactness } = this.getScaledDetails(
        webcolaLayout.constraints, 
        DEFAULT_SCALE_FACTOR, 
        webcolaLayout.nodes,
        webcolaLayout.groups,
        webcolaLayout.links
      );

      if (shouldShowLoadingOverlay) {
        this.updateLoadingProgress('Applying constraints and initializing...');
      }

      // Use a higher convergence threshold when prior state exists.
      // This allows the layout to converge faster, preserving prior positions better.
      const convergenceThreshold = hasPriorPositions ? 0.1 : 1e-3;

      // Create WebCola layout using d3adaptor
      const layout: Layout = cola.d3adaptor(d3)
        .linkDistance(linkLength)
        .convergenceThreshold(convergenceThreshold)
        .avoidOverlaps(true)
        .handleDisconnected(true)
        .nodes(webcolaLayout.nodes)
        .links(webcolaLayout.links)
        .constraints(scaledConstraints)
        .groups(webcolaLayout.groups)
        .groupCompactness(groupCompactness)
        .size([webcolaLayout.FIG_WIDTH, webcolaLayout.FIG_HEIGHT]);

      // ── Morph transition: element-level enter / exit / continue ──────
      // Instead of cross-fading the entire graph (which makes continuing
      // elements blink), we diff the old and new layouts by ID and:
      //   • Exiting elements  → clone to a snapshot layer, fade out in place
      //   • Continuing elements → stay visible; slide from old pos to new pos
      //   • Entering elements  → created normally, then faded in from opacity 0
      if (shouldMorph && this.currentLayout?.nodes?.length) {
        // Capture old positions BEFORE anything is cleared — needed for the
        // slide animation that runs after the solver converges.
        this.morphOldPositions = new Map();
        for (const n of this.currentLayout.nodes) {
          if (n.x != null && n.y != null) {
            this.morphOldPositions.set((n as any).id, { x: n.x, y: n.y });
          }
        }
        this.applyMorphExitSnapshot(webcolaLayout);
      } else {
        this.morphOldPositions = null;
      }

      // Store current layout
      this.currentLayout = webcolaLayout;
      this.colaLayout = layout;

      // For morph transitions: snapshot the entire old graph as a visual
      // overlay BEFORE clearing.  The overlay stays visible while the
      // solver runs behind the scenes, then gets removed when the morph
      // animation starts.
      if (shouldMorph && this.container) {
        this.snapshotOldGraph();
      }

      // Clear the active layer (snapshot layer is separate and keeps visible)
      this.container.selectAll('*').remove();

      this.renderGroups(webcolaLayout.groups, layout);
      this.renderLinks(webcolaLayout.links, layout);
      this.renderNodes(webcolaLayout.nodes, layout);

      // Hide the new elements while the solver runs — the old-graph
      // snapshot keeps the display stable.
      if (shouldMorph && this.container) {
        this.container.attr('opacity', 0);
      }

      // Track iteration progress
      let tickCount = 0;
      const totalIterations = unconstrainedIters + userConstraintIters + allConstraintIters;
      // Suppress intermediate position rendering during initial layout solve.
      // Once the solver converges (end handler), this flag is cleared so that
      // subsequent tick events (e.g. from drag → layout.resume()) still render.
      let isInitialSolve = true;

      // Start the layout with specific iteration counts and proper event handling
      layout
        .on('tick', () => {
          if (isInitialSolve) {
            tickCount++;
            if (tickCount % 20 === 0) {
              const progress = Math.min(95, Math.round((tickCount / totalIterations) * 100));
              if (shouldShowLoadingOverlay) {
                this.updateLoadingProgress(`Computing layout... ${progress}%`);
              }
            }
            // Skip DOM updates during initial solve — the end handler
            // applies final positions once.
            return;
          }

          // Drag-triggered ticks: render position updates normally.
          if (this.layoutFormat === 'default' || !this.layoutFormat || this.layoutFormat === null) {
            this.updatePositions();
          } else if (this.layoutFormat === 'grid') {
            this.gridUpdatePositions();
          }
        })
        .on('end', () => {
          isInitialSolve = false;
          if (shouldShowLoadingOverlay) {
            this.updateLoadingProgress('Finalizing...');
          }

          if (shouldMorph && this.morphOldPositions && this.morphOldPositions.size > 0) {
            // Unhide the real container — solver has converged, nodes
            // are at final positions.
            if (this.container) {
              this.container.attr('opacity', 1);
            }

            // Remove the old-graph overlay and start exit animations.
            this.startMorphExitAnimation();

            // Apply final positions to the real DOM, then hide entering
            // elements before the slide starts.
            if (this.layoutFormat === 'default' || !this.layoutFormat) {
              this.updatePositions();
            } else if (this.layoutFormat === 'grid') {
              this.gridUpdatePositions();
            }
            this.hideEnteringElements();

            // Animate continuing nodes from old → new positions.
            this.animateMorphSlide();
          } else {
            // Non-morph: unhide container, apply final positions, route.
            if (this.container) {
              this.container.attr('opacity', 1);
            }
            if (this.layoutFormat === 'default' || !this.layoutFormat) {
              this.updatePositions();
              this.routeEdges();
            } else if (this.layoutFormat === 'grid') {
              this.gridUpdatePositions();
              this.gridify(10, 25, 10);
            }
          }

          // Check if it's an unsat core layout
          if (this.isUnsatCore) {
            this.showErrorIcon();
          }

          // Dispatch relations-available event after layout is complete
          this.dispatchRelationsAvailableEvent();

          // Dispatch layout-complete event with final node positions
          // This is useful for capturing positions for temporal consistency
          this.dispatchEvent(new CustomEvent('layout-complete', {
            detail: {
              nodePositions: this.getNodePositions()
            }
          }));

          // Update routing dropdown to match current layout format
          this.updateRoutingModeDropdown();

          if (shouldShowLoadingOverlay) {
            this.hideLoading();
          }
        });

      // Start the layout with error handling for D3/WebCola compatibility issues
      try {
        layout.start(
          unconstrainedIters,
          userConstraintIters,
          allConstraintIters,
          WebColaCnDGraph.GRID_SNAP_ITERATIONS
        );
      } catch (layoutError) {
        console.warn('WebCola layout start encountered an error, trying alternative approach:', layoutError);
        // Try starting with default parameters as fallback
        try {
          layout.start();
        } catch (fallbackError) {
          console.error('Both WebCola start methods failed:', fallbackError);
          throw new Error(`WebCola layout failed to start: ${(fallbackError as Error).message}`);
        }
      }

    } catch (error) {
      console.error('Error rendering layout:', error);
      this.showError(`Layout rendering failed: ${(error as Error).message}`);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  Morph transition helpers
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * IDs that were present in the previous layout but absent from the current
   * one.  Populated by `applyMorphExitSnapshot` and consumed by
   * `applyMorphEnterTransition` so it knows what counts as "entering".
   */
  private morphEnteringNodeIds: Set<string> = new Set();
  private morphEnteringEdgeIds: Set<string> = new Set();

  /**
   * Old positions of nodes captured before the morph, keyed by node ID.
   * Used by `animateMorphSlide` to slide continuing nodes from their
   * previous position to their new solver-converged position.
   */
  private morphOldPositions: Map<string, { x: number; y: number }> | null = null;

  /** Active d3.timer for the slide animation — kept so `clear()` can stop it. */
  private morphSlideTimer: any = null;

  /**
   * Create a snapshot layer containing **only** the elements that are being
   * removed (exiting) between the old and new layouts, then fade them out.
   *
   * Elements that continue in the new layout are NOT included in the
   * snapshot — they stay fully visible in the active container and slide
   * to their new positions via the solver.
   */

  /**
   * Clone the entire current graph (`.zoomable` container) into a
   * `.morph-old-graph` overlay.  This dumb visual copy stays on screen
   * while the solver runs behind the scenes so the user sees the old
   * graph until the morph animation starts.
   */
  private snapshotOldGraph(): void {
    if (!this.svg || !this.container) return;

    // Remove any prior snapshot.
    this.svg.selectAll('.morph-old-graph').interrupt().remove();

    const containerNode = this.container.node() as SVGGElement | null;
    if (!containerNode) return;

    const clone = containerNode.cloneNode(true) as SVGGElement;
    clone.setAttribute('class', 'morph-old-graph');
    clone.style.pointerEvents = 'none';

    const svgNode = this.svg.node() as SVGSVGElement | null;
    if (svgNode) {
      svgNode.appendChild(clone);
    }
  }

  private applyMorphExitSnapshot(newLayout: WebColaLayout): void {
    if (!this.svg || !this.container) return;

    // Remove any leftover exit layer from a prior morph.
    this.svg.selectAll('.morph-exit-layer').interrupt().remove();

    const oldNodes = this.currentLayout?.nodes || [];
    const oldLinks = (this.currentLayout as any)?.links || [];
    const newNodeIds = new Set(newLayout.nodes.map((n: any) => n.id));
    const newEdgeIds = new Set(newLayout.links.map((e: any) => e.id));
    const oldNodeIds = new Set(oldNodes.map((n: any) => n.id));
    const oldEdgeIds = new Set(oldLinks.map((e: any) => e.id));

    // Compute entering IDs (used later by applyMorphEnterTransition)
    this.morphEnteringNodeIds = new Set(
      [...newNodeIds].filter(id => !oldNodeIds.has(id))
    );
    this.morphEnteringEdgeIds = new Set(
      [...newEdgeIds].filter(id => !oldEdgeIds.has(id))
    );

    // Compute exiting IDs
    const exitingNodeIds = new Set(
      [...oldNodeIds].filter(id => !newNodeIds.has(id))
    );
    const exitingEdgeIds = new Set(
      [...oldEdgeIds].filter(id => !newEdgeIds.has(id))
    );

    // Nothing to fade out → skip
    if (exitingNodeIds.size === 0 && exitingEdgeIds.size === 0) return;

    const svgNode = this.svg.node() as SVGSVGElement | null;
    if (!svgNode) return;

    // Build a lightweight snapshot group containing only the exiting elements.
    const snapshotGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    snapshotGroup.setAttribute('class', 'morph-exit-layer');
    snapshotGroup.style.pointerEvents = 'none';

    // Preserve the current zoom/pan transform on the snapshot so it aligns
    // visually with the container.
    const containerTransform = this.container.attr('transform');
    if (containerTransform) {
      snapshotGroup.setAttribute('transform', containerTransform);
    }

    // Clone exiting nodes
    if (this.svgNodes) {
      this.svgNodes.each(function(this: SVGGElement, d: any) {
        if (exitingNodeIds.has(d.id)) {
          snapshotGroup.appendChild(this.cloneNode(true));
        }
      });
    }

    // Clone exiting edges (edge exits if its own ID is gone, OR if either
    // endpoint node is gone — because a dangling edge is meaningless).
    if (this.svgLinkGroups) {
      this.svgLinkGroups.each(function(this: SVGGElement, d: any) {
        const edgeId: string = d.id;
        const srcId: string = d.source?.id ?? '';
        const tgtId: string = d.target?.id ?? '';
        if (
          exitingEdgeIds.has(edgeId) ||
          exitingNodeIds.has(srcId) ||
          exitingNodeIds.has(tgtId)
        ) {
          snapshotGroup.appendChild(this.cloneNode(true));
        }
      });
    }

    // Clone exiting group rects
    if (this.svgGroups) {
      this.svgGroups.each(function(this: SVGRectElement, d: any) {
        if (d.id && !newLayout.groups.some((g: any) => g.id === d.id)) {
          snapshotGroup.appendChild(this.cloneNode(true));
        }
      });
    }

    if (snapshotGroup.childElementCount === 0) return;

    svgNode.appendChild(snapshotGroup);

    // Don't animate yet — the snapshot stays visible at full opacity
    // while the solver runs so the user still sees the old graph.
    // Call `startMorphExitAnimation()` from the layout end handler
    // once the solver has converged.
  }

  /**
   * Begin the exit animation on the morph-exit-layer snapshot.
   * Called from the layout 'end' handler after the solver converges,
   * so the old graph remains visible during the solve phase.
   */
  private startMorphExitAnimation(): void {
    if (!this.svg) return;

    // Remove the full old-graph snapshot immediately — the real
    // container is now unhidden and positioned at final coordinates.
    this.svg.selectAll('.morph-old-graph').remove();

    const snapshotSel = this.svg.select('.morph-exit-layer');
    if (snapshotSel.empty()) return;

    const exitDuration = this.morphExitDurationMs;

    // Animate exiting edge paths with stroke-dashoffset (draw-out effect).
    snapshotSel.selectAll('g.link-group, g.inferredLinkGroup, g.alignmentLinkGroup')
      .each(function(this: SVGGElement) {
        const pathEl = this.querySelector('path[data-link-id]') as SVGPathElement | null;
        if (!pathEl) return;
        const totalLength = pathEl.getTotalLength();
        if (!totalLength || totalLength <= 0) return;

        pathEl.setAttribute('stroke-dasharray', String(totalLength));
        pathEl.setAttribute('stroke-dashoffset', '0');

        d3.select(this).selectAll('.linklabel, .arrowhead').attr('opacity', 0);

        d3.select(pathEl)
          .transition()
          .duration(exitDuration)
          .ease(d3.easeCubicIn)
          .attr('stroke-dashoffset', totalLength);
      });

    // Fade out exiting nodes and groups normally.
    snapshotSel.selectAll('g.node, rect.group')
      .transition()
      .duration(exitDuration)
      .ease(d3.easeCubicOut)
      .attr('opacity', 0);

    // Remove the snapshot layer once the transition finishes.
    snapshotSel
      .transition()
      .duration(exitDuration)
      .on('end', function(this: SVGGElement) {
        d3.select(this).remove();
      });
  }

  /**
   * Fade in only the **entering** elements (nodes/edges that did not exist
   * in the previous layout).  Continuing elements stay at full opacity so
   * there is no visual blink — they simply slide to their new position.
   */

  /**
   * Immediately hide all entering nodes and edges (opacity 0) so they are
   * invisible during the morph slide.  `applyMorphEnterTransition` is
   * called later to reveal them with animation.
   */
  private hideEnteringElements(): void {
    const enterNodeIds = this.morphEnteringNodeIds;
    const enterEdgeIds = this.morphEnteringEdgeIds;
    if (enterNodeIds.size === 0 && enterEdgeIds.size === 0) return;

    if (this.svgNodes && enterNodeIds.size > 0) {
      this.svgNodes
        .filter((d: any) => enterNodeIds.has(d.id))
        .attr('opacity', 0);
    }

    if (this.svgLinkGroups && (enterEdgeIds.size > 0 || enterNodeIds.size > 0)) {
      this.svgLinkGroups
        .filter((d: any) => {
          if (enterEdgeIds.has(d.id)) return true;
          const srcId: string = d.source?.id ?? '';
          const tgtId: string = d.target?.id ?? '';
          return enterNodeIds.has(srcId) || enterNodeIds.has(tgtId);
        })
        .attr('opacity', 0);
    }
  }

  private applyMorphEnterTransition(): void {
    const enterNodeIds = this.morphEnteringNodeIds;
    const enterEdgeIds = this.morphEnteringEdgeIds;

    // If there is no diff info (first render, or replace mode), treat
    // everything as entering for a gentle fade-in.
    const hasEntryInfo = enterNodeIds.size > 0 || enterEdgeIds.size > 0;

    if (!hasEntryInfo && this.morphEnteringNodeIds.size === 0 && this.morphEnteringEdgeIds.size === 0) {
      // First render with morph — nothing was "old" so nothing enters;
      // just show everything immediately.
      return;
    }

    // Fade in entering nodes
    if (this.svgNodes && enterNodeIds.size > 0) {
      this.svgNodes
        .filter((d: any) => enterNodeIds.has(d.id))
        .attr('opacity', 0)
        .transition()
        .delay(this.morphEnterDelayMs)
        .duration(this.morphEnterDurationMs)
        .ease(d3.easeCubicOut)
        .attr('opacity', 1);
    }

    // Draw in entering edges — an edge enters if its own ID is new, OR
    // if either endpoint is an entering node.
    // Uses stroke-dashoffset animation so the path visually "draws" from
    // the continuing endpoint toward the entering node.
    if (this.svgLinkGroups && (enterEdgeIds.size > 0 || enterNodeIds.size > 0)) {
      this.svgLinkGroups
        .filter((d: any) => {
          if (enterEdgeIds.has(d.id)) return true;
          const srcId: string = d.source?.id ?? '';
          const tgtId: string = d.target?.id ?? '';
          return enterNodeIds.has(srcId) || enterNodeIds.has(tgtId);
        })
        .each(function(this: SVGGElement) {
          const pathEl = this.querySelector('path[data-link-id]') as SVGPathElement | null;
          if (!pathEl) return;
          const totalLength = pathEl.getTotalLength();
          if (!totalLength || totalLength <= 0) return;

          d3.select(pathEl)
            .attr('stroke-dasharray', totalLength)
            .attr('stroke-dashoffset', totalLength);

          // Also hide the link label and arrowheads during draw-in
          d3.select(this).selectAll('.linklabel, .arrowhead')
            .attr('opacity', 0);
        })
        .attr('opacity', 1) // edge group visible — dash hides the stroke
        .transition()
        .delay(this.morphEnterDelayMs)
        .duration(this.morphEnterDurationMs)
        .ease(d3.easeCubicOut)
        .tween('draw-in', function(this: SVGGElement) {
          const pathEl = this.querySelector('path[data-link-id]') as SVGPathElement | null;
          if (!pathEl) return () => {};
          const totalLength = pathEl.getTotalLength();
          if (!totalLength || totalLength <= 0) return () => {};
          const interpolate = d3.interpolateNumber(totalLength, 0);
          return (t: number) => {
            pathEl.setAttribute('stroke-dashoffset', String(interpolate(t)));
          };
        })
        .on('end', function(this: SVGGElement) {
          // Clean up dash attributes and show labels/arrows
          const pathEl = this.querySelector('path[data-link-id]');
          if (pathEl) {
            pathEl.removeAttribute('stroke-dasharray');
            pathEl.removeAttribute('stroke-dashoffset');
          }
          d3.select(this).selectAll('.linklabel, .arrowhead')
            .attr('opacity', 1);
        });
    }

    // Fade in entering groups
    if (this.svgGroups && this.svgGroupLabels) {
      const newGroupIds = new Set(
        (this.currentLayout?.groups || []).map((g: any) => g.id).filter(Boolean)
      );
      // Groups whose ID was not in the old layout (approximation: if morphEnteringNodeIds
      // contains ANY leaf of the group, the group is visually new).
      // For simplicity we let groups appear instantly — they're background rects
      // and the visual weight is in the nodes/edges.
    }

    // Clear the diff sets so they don't leak into the next render.
    this.morphEnteringNodeIds = new Set();
    this.morphEnteringEdgeIds = new Set();
  }

  /**
   * Animate continuing nodes from their old positions to their new
   * solver-converged positions.
   *
   * Called in the layout 'end' handler after the solver has written final
   * coordinates into `d.x / d.y`.  We:
   *  1. Capture each continuing node's final position.
   *  2. Override `d.x / d.y` to the OLD position and call `updatePositions()`
   *     so the node visually starts where it used to be.
   *  3. Run a `d3.timer` that interpolates `d.x / d.y` from old → final and
   *     calls `updatePositions()` every frame, producing a smooth slide.
   *  4. When done, restore exact final positions and re-run `routeEdges()`
   *     for clean edge paths.
   */
  private animateMorphSlide(): void {
    const oldPositions = this.morphOldPositions;
    if (!oldPositions || oldPositions.size === 0 || !this.currentLayout?.nodes) {
      this.morphOldPositions = null;
      return;
    }

    const nodes = this.currentLayout.nodes as any[];
    const duration = this.morphSlideDurationMs;

    // If speed is effectively 0, skip animation entirely.
    if (duration <= 0) {
      this.morphOldPositions = null;
      return;
    }

    // Identify continuing nodes (present in both old and new)
    const continuingNodes: Array<{
      node: any;
      oldX: number; oldY: number;
      finalX: number; finalY: number;
    }> = [];

    for (const node of nodes) {
      const old = oldPositions.get(node.id);
      if (old) {
        continuingNodes.push({
          node,
          oldX: old.x, oldY: old.y,
          finalX: node.x, finalY: node.y,
        });
      }
    }

    if (continuingNodes.length === 0) {
      this.morphOldPositions = null;
      return;
    }

    // Snap continuing nodes back to their OLD positions for the first frame.
    for (const c of continuingNodes) {
      c.node.x = c.oldX;
      c.node.y = c.oldY;
    }
    this.updatePositions();

    // Cubic ease-out for a natural deceleration feel.
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const t0 = performance.now();

    // Stop any previous slide timer (e.g. if the user navigates quickly).
    if (this.morphSlideTimer) {
      this.morphSlideTimer.stop();
      this.morphSlideTimer = null;
    }

    this.morphSlideTimer = d3.timer(() => {
      const elapsed = performance.now() - t0;
      const progress = Math.min(1, elapsed / duration);
      const eased = ease(progress);

      for (const c of continuingNodes) {
        c.node.x = c.oldX + (c.finalX - c.oldX) * eased;
        c.node.y = c.oldY + (c.finalY - c.oldY) * eased;
      }

      this.updatePositions();

      if (progress >= 1) {
        this.morphSlideTimer.stop();
        this.morphSlideTimer = null;

        // Ensure exact final positions.
        for (const c of continuingNodes) {
          c.node.x = c.finalX;
          c.node.y = c.finalY;
        }
        this.updatePositions();

        // Re-run advanced edge routing now that nodes are at final positions.
        if (this.layoutFormat === 'default' || !this.layoutFormat) {
          this.routeEdges();
        }

        // Now that the slide is done, reveal entering nodes/edges.
        this.applyMorphEnterTransition();

        this.morphOldPositions = null;
      }
    });
  }

  /**
   * Configure how subsequent render calls transition between frames.
   */
  public setTransitionMode(mode: WebColaRenderTransitionMode): void {
    this.setAttribute('transition-mode', mode);
  }

  /**
   * Set the morph animation speed.
   * @param speed 0 (instant) – 1 (full duration, default)
   */
  public setMorphSpeed(speed: number): void {
    this.setAttribute('morph-speed', String(Math.max(0, Math.min(speed, 1))));
  }

  /**
   * Clear the current graph visualization and reset internal state.
   * This is useful when switching between temporal states to ensure a clean slate.
   */
  public clear(): void {
    // Stop any running layout
    if (this.colaLayout) {
      try {
        (this.colaLayout as any).stop?.();
      } catch (e) {
        // Ignore errors when stopping layout
      }
    }

    // Clear the SVG container
    if (this.container) {
      this.container.selectAll('*').remove();
    }
    // Clean up any in-progress morph transitions
    if (this.svg) {
      this.svg.selectAll('.morph-exit-layer').interrupt().remove();
    }
    if (this.morphSlideTimer) {
      this.morphSlideTimer.stop();
      this.morphSlideTimer = null;
    }
    this.morphOldPositions = null;

    // Reset internal state
    this.currentLayout = null as any;
    this.colaLayout = null as any;
    this.svgNodes = null;
    this.svgLinks = null;
    this.svgGroups = null;

    // Clear caches
    this.edgeRoutingCache.edgesBetweenNodes.clear();
    this.edgeRoutingCache.alignmentEdges.clear();
    this.dragStartPositions.clear();
    this.hideLoading();
  }

  /**
   * Get the current positions of all nodes in the layout.
   * Useful for reading coordinates after rendering or drag events.
   * 
   * Note: These positions are in the layout coordinate space, not screen space.
   * For preserving visual continuity across renders, use `getLayoutState()` instead.
   */
  public getNodePositions(): Array<{ id: string; x: number; y: number }> {
    if (!this.currentLayout?.nodes) {
      return [];
    }
    return this.currentLayout.nodes.map((n: any) => ({
      id: n.id,
      x: n.x,
      y: n.y
    }));
  }

  /**
   * Get the current zoom/pan transform state.
   * 
   * @returns The current transform with k (scale), x (translateX), and y (translateY)
   */
  public getCurrentTransform(): TransformInfo {
    if (this.svg && this.svg.node()) {
      try {
        const transform = d3.zoomTransform(this.svg.node());
        return {
          k: transform.k,
          x: transform.x,
          y: transform.y
        };
      } catch (e) {
        // Return identity transform if we can't get the current one
        return { k: 1, x: 0, y: 0 };
      }
    }
    return { k: 1, x: 0, y: 0 };
  }

  /**
   * Get the complete layout state for preservation across renders.
   *
   * When using the policy-driven `renderLayout` API, you generally don't
   * need to call this — the graph captures its own state automatically.
   * Use this only when you need explicit control over prior positions.
   *
   * @returns Complete layout state including positions and transform
   *
   * @example
   * ```typescript
   * // Explicit capture (optional — graph auto-captures when policy is used)
   * const state = graph.getLayoutState();
   * await graph.renderLayout(newLayout, {
   *   policy: stability,
   *   prevInstance: prev,
   *   currInstance: curr,
   *   priorPositions: state,
   * });
   * ```
   */
  public getLayoutState(): LayoutState {
    return {
      positions: this.getNodePositions(),
      transform: this.getCurrentTransform()
    };
  }

  /**
   * Return the seed positions the most-recent policy produced before
   * the solver ran, or `null` if no policy was applied during the last
   * render.
   *
   * Used by the appropriateness-experiment harness to score the policy
   * seed against the prior frame independently of solver output. The
   * gap between seed-scored and output-scored metrics attributes the
   * effect: small gap = policy is the lever; large gap = solver
   * dominated.
   *
   * The stored value is captured by reference during rendering (zero
   * production cost — callers who never invoke this method pay
   * nothing). The deep-clone happens here so the returned snapshot is
   * immutable from the consumer's perspective.
   */
  public getLastSeedState(): LayoutState | null {
    if (!this.lastSeedState) return null;
    return {
      positions: this.lastSeedState.positions.map(p => ({ ...p })),
      transform: { ...this.lastSeedState.transform },
    };
  }

  /**
   * Add a control element to the graph toolbar
   * @param element - The HTML element to add to the toolbar
   */
  public addToolbarControl(element: any): void {
    const toolbar = this.shadowRoot?.querySelector('#graph-toolbar');
    if (toolbar) {
      toolbar.appendChild(element);
    }
  }

  /**
   * Get the graph toolbar element for more advanced control manipulation
   * @returns The toolbar element or null if not found
   */
  public getToolbar(): any {
    return this.shadowRoot?.querySelector('#graph-toolbar') || null;
  }

  /**
   * Render groups using D3 data binding
   */
  private renderGroups(groups: any[], layout: Layout): void {
    if (!this.currentLayout.nodes || this.currentLayout.nodes.length === 0) {
      console.warn("Cannot render groups: nodes not available");
      return;
    }

    this.svgGroups = this.setupGroups(groups, this.currentLayout.nodes, layout);
  }

  /**
   * Renders link elements with proper grouping, styling, and labels.
   * Creates link groups for ALL edges, including alignment edges (which are rendered
   * fully transparent so WebCola can use them as layout constraints without them
   * being visually present). Non-alignment edges receive normal styling and labels.
   * 
   * @param links - Array of edge objects to render
   * @param layout - WebCola layout instance (unused but maintained for API consistency)
   * @returns D3 selection of created link groups
   * 
   * @example
   * ```typescript
   * const linkSelection = this.setupLinks(this.currentLayout.links, layout);
   * ```
   */
  private setupLinks(
    links: Array<EdgeWithMetadata>, 
    layout: Layout
  ) {
    // Create link groups for each edge
    const linkGroups = this.container
      .selectAll(".link-group")
      .data(links)
      .enter()
      .append("g")
      .attr("class", "link-group");

    // Add path elements to each link group with dynamic styling
    this.setupLinkPaths(linkGroups);

    // Add labels to non-alignment links
    this.setupLinkLabels(linkGroups);

    // Add draggable endpoint markers for input mode
    this.setupEdgeEndpointMarkers(linkGroups);

    return linkGroups;
  }

  /**
   * Creates path elements for links with appropriate CSS classes and styling.
   * Assigns different classes based on edge type for targeted styling.
   * 
   * @param linkGroups - D3 selection of link group elements
   */
  private setupLinkPaths(
    linkGroups: d3.Selection<SVGGElement, any, any, unknown>
  ): void {
    linkGroups
      .append("path")
      .attr("class", (d: any) => {
        if (this.isAlignmentEdge(d)) return "alignmentLink";
        if (this.isInferredEdge(d)) return "inferredLink";
        return "link";
      })
      .attr("data-link-id", (d: any) => d.id || "")
      .attr("stroke", (d: any) => this.isAlignmentEdge(d) ? "none" : d.color)
      .attr("fill", "none")
      .attr("opacity", (d: any) => this.isAlignmentEdge(d) ? 0 : null)
      // Use .style() for stroke-width so inline styles override CSS class rules (.link, .inferredLink)
      .style("stroke-width", (d: any) => this.isAlignmentEdge(d) ? "0" : d.weight != null ? `${d.weight}px` : null)
      .attr("stroke-dasharray", (d: any) => this.isAlignmentEdge(d) ? null : this.getEdgeDasharray(d.style))
      .attr("marker-end", (d: any) => {
        if (this.isAlignmentEdge(d)) return "none";
        return "url(#end-arrow)";
      })
      .attr("marker-start", (d: any) => {
        // Add marker-start for bidirectional edges
        if (this.isAlignmentEdge(d) || !d.bidirectional) return "none";
        return  "url(#start-arrow)";
      })
      .on('click.inputmode', (d: any) => {
        if (this.isInputModeActive && !this.isAlignmentEdge(d)) {
          d3.event.stopPropagation();
          // Handle async operation without blocking the event
          this.editEdgeLabel(d).catch(error => {
            console.error('Error editing edge label:', error);
          });
        }
      })
      .style('cursor', () => {
        return this.isInputModeActive ? 'pointer' : 'default';
      });

    // Highlight underlay: a wider, translucent path drawn beneath the main edge.
    // Only created for edges that actually set `highlight`, so non-highlighted
    // edges pay zero extra cost. Inserted as the first child of the link-group
    // so SVG painter's order renders it underneath the main edge.
    linkGroups
      .filter((d: any) => !!d.highlight && !this.isAlignmentEdge(d))
      .insert("path", ":first-child")
      .attr("class", "highlight-underlay")
      .attr("stroke", (d: any) => d.highlight)
      .attr("fill", "none")
      .attr("stroke-linecap", "round")
      .style("stroke-width", (d: any) => {
        const base = d.weight != null ? d.weight : 2;
        return `${base + 8}px`;
      })
      .attr("opacity", 0.45)
      .style("pointer-events", "none");
  }

  private getEdgeDasharray(style?: string): string | null {
    if (!style) {
      return null;
    }

    switch (style.toLowerCase()) {
      case 'dotted':
        return '1,4';
      case 'dashed':
        return '6,4';
      case 'solid':
        return null;
      default:
        return null;
    }
  }

  /**
   * Adds text labels to link groups for non-alignment edges.
   * Labels are positioned at the midpoint of each link path.
   * Respects the showLabel property - if explicitly set to false, no label is rendered.
   * Inferred edges always show their labels.
   * 
   * @param linkGroups - D3 selection of link group elements
   */
  private setupLinkLabels(
    linkGroups: d3.Selection<SVGGElement, any, any, unknown>
  ): void {
    linkGroups
      .filter((d: any) => !this.isAlignmentEdge(d) && (this.isInferredEdge(d) || d.showLabel !== false))
      .append("text")
      .attr("class", "linklabel")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("font-family", this.getFontFamily())
      .attr("pointer-events", "none")
      .text((d: any) => d.label || d.relName || "");
  }

  /**
   * Adds draggable endpoint markers to edges for input mode.
   * These markers allow users to drag edge endpoints to reconnect edges.
   * 
   * @param linkGroups - D3 selection of link group elements
   */
  private setupEdgeEndpointMarkers(
    linkGroups: d3.Selection<SVGGElement, any, any, unknown>
  ): void {
    // Add target endpoint marker (at the arrow end)
    linkGroups
      .filter((d: any) => !this.isAlignmentEdge(d))
      .append("circle")
      .attr("class", "edge-endpoint-marker target-marker")
      .attr("r", 8)
      .attr("fill", "#007bff")
      .attr("stroke", "white")
      .attr("stroke-width", 2)
      .attr("opacity", 0) // Hidden by default
      .attr("cursor", "move")
      .style("pointer-events", "none") // Will be enabled in input mode
      .call(
        d3.drag()
          .on('start', (d: EdgeWithMetadata) => this.startEdgeEndpointDrag(d, 'target'))
          .on('drag', (d: EdgeWithMetadata) => this.dragEdgeEndpoint(d, 'target'))
          .on('end', (d: EdgeWithMetadata) => this.endEdgeEndpointDrag(d, 'target'))
      );

    // Add source endpoint marker (at the start, for bidirectional edges or moving the source)
    linkGroups
      .filter((d: any) => !this.isAlignmentEdge(d))
      .append("circle")
      .attr("class", "edge-endpoint-marker source-marker")
      .attr("r", 8)
      .attr("fill", "#28a745")
      .attr("stroke", "white")
      .attr("stroke-width", 2)
      .attr("opacity", 0) // Hidden by default
      .attr("cursor", "move")
      .style("pointer-events", "none") // Will be enabled in input mode
      .call(
        d3.drag()
          .on('start', (d: EdgeWithMetadata) => this.startEdgeEndpointDrag(d, 'source'))
          .on('drag', (d: EdgeWithMetadata) => this.dragEdgeEndpoint(d, 'source'))
          .on('end', (d: EdgeWithMetadata) => this.endEdgeEndpointDrag(d, 'source'))
      );
  }

  /**
   * Start dragging an edge endpoint
   */
  private startEdgeEndpointDrag(edgeData: EdgeWithMetadata, endpoint: 'source' | 'target'): void {
    d3.event.sourceEvent.stopPropagation();
    
    this.edgeDragState.isDragging = true;
    this.edgeDragState.edge = edgeData;
    this.edgeDragState.endpoint = endpoint;
    
    console.log(`🔵 Started dragging ${endpoint} endpoint of edge:`, edgeData.id);
  }

  /**
   * Drag an edge endpoint - update visual feedback
   */
  private dragEdgeEndpoint(edgeData: EdgeWithMetadata, endpoint: 'source' | 'target'): void {
    if (!this.edgeDragState.isDragging) return;

    const [mouseX, mouseY] = d3.mouse(this.container.node());
    
    // Update the marker position
    const markerClass = endpoint === 'target' ? '.target-marker' : '.source-marker';
    this.container
      .selectAll('.link-group')
      .filter((d: any) => d.id === edgeData.id)
      .select(markerClass)
      .attr('cx', mouseX)
      .attr('cy', mouseY);
  }

  /**
   * End dragging an edge endpoint - reconnect or delete edge
   */
  private async endEdgeEndpointDrag(edgeData: EdgeWithMetadata, endpoint: 'source' | 'target'): Promise<void> {
    if (!this.edgeDragState.isDragging) return;

    const [mouseX, mouseY] = d3.mouse(this.container.node());
    
    // Find the node under the cursor
    const targetNode = this.findNodeAtPosition(mouseX, mouseY);
    
    if (targetNode) {
      console.log(`🔗 Reconnecting ${endpoint} to node:`, targetNode.id);
      await this.reconnectEdge(edgeData, endpoint, targetNode);
    } else {
      console.log(`🗑️ No node found - deleting edge:`, edgeData.id);
      await this.deleteEdge(edgeData);
    }
    
    // Clean up drag state
    this.edgeDragState = {
      isDragging: false,
      edge: null,
      endpoint: null,
      dragMarker: null
    };
    
    // Re-render to show changes
    this.rerenderGraph();
  }

  /**
   * Find a node at the given position
   */
  private findNodeAtPosition(x: number, y: number): NodeWithMetadata | null {
    if (!this.currentLayout?.nodes) return null;
    
    // Check each node to see if the position is within its bounds
    for (const node of this.currentLayout.nodes) {
      const halfWidth = ((node as any).visualWidth ?? node.width ?? 0) / 2;
      const halfHeight = ((node as any).visualHeight ?? node.height ?? 0) / 2;
      
      if (x >= node.x - halfWidth && x <= node.x + halfWidth &&
          y >= node.y - halfHeight && y <= node.y + halfHeight) {
        return node;
      }
    }
    
    return null;
  }

  /**
   * Reconnect an edge to a new node
   */
  private async reconnectEdge(
    edgeData: EdgeWithMetadata,
    endpoint: 'source' | 'target',
    newNode: NodeWithMetadata
  ): Promise<void> {
    const oldSourceNode = this.getNodeFromEdge(edgeData, 'source');
    const oldTargetNode = this.getNodeFromEdge(edgeData, 'target');
    
    if (!oldSourceNode || !oldTargetNode) {
      console.error('Could not find source or target node');
      return;
    }

    // Determine new source and target
    let newSourceNode: NodeWithMetadata;
    let newTargetNode: NodeWithMetadata;
    
    if (endpoint === 'source') {
      newSourceNode = newNode;
      newTargetNode = oldTargetNode;
    } else {
      newSourceNode = oldSourceNode;
      newTargetNode = newNode;
    }

    // Don't allow reconnecting if it results in the same edge
    if (newSourceNode.id === oldSourceNode.id && newTargetNode.id === oldTargetNode.id) {
      console.log('⏭️ Edge already connected to this node, no change needed');
      return;
    }

    // Use relName (the data-instance relation key) rather than label (the display
    // string, which may include n-ary suffixes like "[Person1]").
    const relationName = edgeData.relName || edgeData.label || '';

    if (!relationName.trim()) {
      console.warn('Edge has no relation name, cannot reconnect');
      return;
    }

    // Create tuples for old and new edges
    const oldTuple: ITuple = {
      atoms: [oldSourceNode.id, oldTargetNode.id],
      types: [oldSourceNode.type || 'untyped', oldTargetNode.type || 'untyped']
    };

    const newTuple: ITuple = {
      atoms: [newSourceNode.id, newTargetNode.id],
      types: [newSourceNode.type || 'untyped', newTargetNode.type || 'untyped']
    };

    console.log(`Reconnecting edge: ${relationName} from ${oldSourceNode.id}->${oldTargetNode.id} to ${newSourceNode.id}->${newTargetNode.id}`);

    // Dispatch edge reconnection event
    const edgeReconnectionEvent = new CustomEvent('edge-reconnection-requested', {
      detail: {
        relationId: relationName,
        oldTuple: oldTuple,
        newTuple: newTuple,
        oldSourceNodeId: oldSourceNode.id,
        oldTargetNodeId: oldTargetNode.id,
        newSourceNodeId: newSourceNode.id,
        newTargetNodeId: newTargetNode.id
      },
      bubbles: true
    });
    this.dispatchEvent(edgeReconnectionEvent);

    // Update the edge data in the current layout
    const sourceIndex = this.currentLayout.nodes.findIndex(n => n.id === newSourceNode.id);
    const targetIndex = this.currentLayout.nodes.findIndex(n => n.id === newTargetNode.id);
    
    if (sourceIndex !== -1 && targetIndex !== -1) {
      edgeData.source = sourceIndex;
      edgeData.target = targetIndex;
    }
  }

  /**
   * Delete an edge from the graph
   */
  private async deleteEdge(edgeData: EdgeWithMetadata): Promise<void> {
    const sourceNode = this.getNodeFromEdge(edgeData, 'source');
    const targetNode = this.getNodeFromEdge(edgeData, 'target');

    if (!sourceNode || !targetNode) {
      console.error('Could not find source or target node for edge deletion');
      return;
    }

    // Use relName (the data-instance relation key) rather than label (display string).
    const relationName = edgeData.relName || edgeData.label || '';

    if (!relationName.trim()) {
      console.warn('Edge has no relation name, cannot delete from data instance');
      // Still remove from visualization
      this.removeEdgeFromLayout(edgeData);
      return;
    }

    // For group edges, collect tuples for ALL member nodes in the group.
    // A group edge from KeyNode to GroupA (containing N1, N2, N3) visually
    // represents the tuples (KeyNode,N1), (KeyNode,N2), (KeyNode,N3).
    const tuples: ITuple[] = [];

    if (edgeData.groupId && edgeData.keyNodeId && this.currentLayout) {
      const group = (this.currentLayout.groups || []).find((g: any) => g.id === edgeData.groupId);
      if (group) {
        const allGroups = this.currentLayout.groups || [];
        const memberIndices = this.collectGroupNodeIndices(group, allGroups);
        const keyNode = this.currentLayout.nodes.find((n: NodeWithMetadata) => n.id === edgeData.keyNodeId);

        for (const idx of memberIndices) {
          const memberNode = this.currentLayout.nodes[idx];
          if (memberNode && keyNode) {
            tuples.push({
              atoms: [keyNode.id, memberNode.id],
              types: [keyNode.type || 'untyped', memberNode.type || 'untyped']
            });
          }
        }
      }
    }

    // Fallback: if no group tuples were found, use the direct source/target
    if (tuples.length === 0) {
      tuples.push({
        atoms: [sourceNode.id, targetNode.id],
        types: [sourceNode.type || 'untyped', targetNode.type || 'untyped']
      });
    }

    console.log(`🗑️ Deleting edge: ${relationName} (${tuples.length} tuple(s))`);

    // Dispatch edge deletion event (using modification with empty new name)
    const edgeDeletionEvent = new CustomEvent('edge-modification-requested', {
      detail: {
        oldRelationId: relationName,
        newRelationId: '', // Empty string signals deletion
        sourceNodeId: sourceNode.id,
        targetNodeId: targetNode.id,
        tuples: tuples
      },
      bubbles: true
    });
    this.dispatchEvent(edgeDeletionEvent);

    // Remove from current layout
    this.removeEdgeFromLayout(edgeData);
  }

  /**
   * Remove an edge from the current layout
   */
  private removeEdgeFromLayout(edgeData: EdgeWithMetadata): void {
    if (!this.currentLayout?.links) return;
    
    const index = this.currentLayout.links.findIndex(link => link.id === edgeData.id);
    if (index !== -1) {
      this.currentLayout.links.splice(index, 1);
      console.log(`✅ Edge removed from layout: ${edgeData.id}`);
    }
  }

  /**
   * Sets up SVG group elements with rectangles and labels for WebCola layout.
   * Creates visual containers for node groups with proper styling and interactivity.
   * Handles both regular groups and disconnected node groups with different styling.
   * 
   * @param groups - Array of group objects to render
   * @param nodes - Array of nodes for color lookup via keyNode
   * @param layout - WebCola layout instance for drag behavior
   * @returns D3 selection of created group rectangles
   * 
   * @example
   * ```typescript
   * const groupSelection = this.setupGroups(this.currentLayout.groups, this.currentLayout.nodes, layout);
   * 
   * 
   * 
   * TODO: Could the issue be that groups are NODES and not indices?
   * 
   * Why are we returning anything here?
   * ```
   */
  private setupGroups(
    groups: any[], 
    nodes: Array<NodeWithMetadata>, 
    layout: Layout
  ) {
    // Create group rectangles with dynamic styling
    const groupRects = this.setupGroupRectangles(groups, nodes, layout);

    // Add labels to groups that should display them
    this.svgGroupLabels = this.setupGroupLabels(groups, layout);

    return groupRects;
  }

  /**
   * Creates rectangle elements for groups with proper styling and drag behavior.
   * Handles different group types with appropriate visual styling.
   * 
   * @param groups - Array of group objects to render
   * @param nodes - Array of nodes for color lookup via keyNode
   * @param layout - WebCola layout instance for drag behavior
   * @returns D3 selection of created group rectangles
   */
  private setupGroupRectangles(
    groups: any[], 
    nodes: Array<NodeWithMetadata>, 
    layout: Layout
  ): d3.Selection<SVGRectElement, any, any, unknown> {
    const groupRects = this.container
      .selectAll(".group")
      .data(groups)
      .enter()
      .append("rect")
      .attr("class", (d: any) => {
        if (this.isDisconnectedGroup(d))
          return "disconnectedNode"
        else if (this.isErrorGroup(d)) {
          return "error-group";
        } else {
          return "group";
        }
      })
      .attr("rx", WebColaCnDGraph.GROUP_BORDER_RADIUS)
      .attr("ry", WebColaCnDGraph.GROUP_BORDER_RADIUS)
      .style("fill", (d: any) => {
        // Disconnected groups are transparent
        if (this.isDisconnectedGroup(d)) {
          return "transparent";
        }

        // Use key node color for regular groups
        const targetNode = nodes[d.keyNode];
        return targetNode?.color || "#cccccc";
      })
      .attr("fill-opacity", WebColaCnDGraph.GROUP_FILL_OPACITY)
      .attr("stroke", (d: any) => {
        if (this.isDisconnectedGroup(d)) {
          return "none";
        }
        const targetNode = nodes[d.keyNode];
        return targetNode?.color || "#999999";
      })
      .attr("stroke-width", (d: any) => {
        if (this.isDisconnectedGroup(d) || this.isErrorGroup(d)) return 1;
        return WebColaCnDGraph.GROUP_STROKE_WIDTH;
      })
      .attr("stroke-opacity", (d: any) => {
        if (this.isDisconnectedGroup(d) || this.isErrorGroup(d)) return 1;
        return WebColaCnDGraph.GROUP_STROKE_OPACITY;
      })
      .call((layout as any).drag);


    return groupRects;
  }

  private resolveGroupLeafToNodeIndex(leaf: unknown): number | null {
    if (typeof leaf === 'number' && Number.isInteger(leaf) && leaf >= 0) {
      return leaf;
    }

    if (leaf && typeof leaf === 'object') {
      const nodeLike = leaf as { id?: string; index?: number };
      if (typeof nodeLike.index === 'number' && Number.isInteger(nodeLike.index) && nodeLike.index >= 0) {
        return nodeLike.index;
      }

      if (typeof nodeLike.id === 'string' && this.currentLayout?.nodes) {
        const index = this.currentLayout.nodes.findIndex((node: NodeWithMetadata) => node.id === nodeLike.id);
        return index >= 0 ? index : null;
      }
    }

    return null;
  }

  private collectGroupNodeIndices(group: any, allGroups: any[]): number[] {
    const nodeIndices = new Set<number>();
    const visitedGroupIndices = new Set<number>();

    const collect = (grp: any, groupIndex?: number): void => {
      if (!grp) {
        return;
      }

      if (typeof groupIndex === 'number') {
        if (visitedGroupIndices.has(groupIndex)) {
          return;
        }
        visitedGroupIndices.add(groupIndex);
      }

      if (Array.isArray(grp.leaves)) {
        grp.leaves.forEach((leaf: unknown) => {
          const index = this.resolveGroupLeafToNodeIndex(leaf);
          if (index !== null) {
            nodeIndices.add(index);
          }
        });
      }

      if (!Array.isArray(grp.groups)) {
        return;
      }

      grp.groups.forEach((subgroupIndex: unknown) => {
        if (
          typeof subgroupIndex === 'number'
          && Number.isInteger(subgroupIndex)
          && subgroupIndex >= 0
          && subgroupIndex < allGroups.length
        ) {
          collect(allGroups[subgroupIndex], subgroupIndex);
        }
      });
    };

    const rootIndex = allGroups.indexOf(group);
    collect(group, rootIndex >= 0 ? rootIndex : undefined);

    return Array.from(nodeIndices);
  }

  private getNodeMainLabelFontSize(_node: any): number {
    return MAIN_LABEL_FONT_SIZE;
  }

  private calculateGroupLabelFontSize(group: any, allGroups: any[]): number {
    if (!this.currentLayout?.nodes?.length) {
      return WebColaCnDGraph.DEFAULT_FONT_SIZE;
    }

    const nodeIndices = this.collectGroupNodeIndices(group, allGroups);
    if (nodeIndices.length === 0) {
      return WebColaCnDGraph.DEFAULT_FONT_SIZE;
    }

    const fontSizes = nodeIndices
      .map((index: number) => this.currentLayout.nodes[index])
      .filter((node: NodeWithMetadata | undefined): node is NodeWithMetadata => Boolean(node))
      .map((node: NodeWithMetadata) => this.getNodeMainLabelFontSize(node));

    if (fontSizes.length === 0) {
      return WebColaCnDGraph.DEFAULT_FONT_SIZE;
    }

    const averageFontSize = fontSizes.reduce((sum: number, size: number) => sum + size, 0) / fontSizes.length;
    const clampedFontSize = Math.max(
      WebColaCnDGraph.MIN_FONT_SIZE,
      Math.min(averageFontSize, WebColaCnDGraph.MAX_FONT_SIZE)
    );
    return Math.round(clampedFontSize * 10) / 10;
  }

  /**
   * Adds text labels to groups that should display them.
   * Labels are positioned at the top of each group with proper spacing.
   * 
   * @param groups - Array of group objects to render
   * @param layout - WebCola layout instance for drag behavior
   * @returns D3 selection of created group labels
   */
  private setupGroupLabels(
    groups: any[],
    layout: Layout
  ): d3.Selection<SVGTextElement, any, any, unknown> {
    const allNodes = this.currentLayout?.nodes || [];

    // Pill backings sit between the group rect and the text. Append before
    // the text so document order keeps text on top.
    this.svgGroupLabelBgs = this.container
      .selectAll(".groupLabelBg")
      .data(groups)
      .enter()
      .append("rect")
      .attr("class", "groupLabelBg")
      .attr("rx", 6)
      .attr("ry", 6)
      .attr("fill", this.getCanvasBackground())
      .attr("fill-opacity", 0.92)
      .attr("stroke", (d: any) => {
        const targetNode = allNodes[d.keyNode];
        return targetNode?.color || "#999999";
      })
      .attr("stroke-width", 1)
      .attr("stroke-opacity", 0.3)
      .attr("pointer-events", "none")
      .style("display", "none");

    return this.container
      .selectAll(".groupLabel")
      .data(groups)
      .enter()
      .append("text")
      .attr("class", "groupLabel")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "hanging")
      .attr("font-family", this.getFontFamily())
      .attr("font-size", (d: any) => {
        const computedFontSize = this.calculateGroupLabelFontSize(d, groups);
        d._groupLabelFontSize = computedFontSize;
        return `${computedFontSize}px`;
      })
      .attr("font-weight", "bold")
      .attr("fill", "#333")
      .attr("pointer-events", "none")
      .text((d: any) => {
        const shouldShowGroupLabel = d.showLabel || false;

        if (shouldShowGroupLabel) {
          // Ensure adequate padding for label display
          if (d.padding) {
            d.padding = Math.max(d.padding, WebColaCnDGraph.GROUP_LABEL_PADDING);
          }
          return d.name || "";
        }

        return "";
      }).call((layout as any).drag);
  }

  /**
   * Render links using D3 data binding with enhanced grouping and labeling
   */
  private renderLinks(links: Array<EdgeWithMetadata>, layout: Layout): void {
    // Clear the stale alignment-edge cache so isAlignmentEdge() falls back to the
    // reliable id-prefix check during rendering. On re-renders (e.g. stability policy)
    // the cache still holds IDs from the previous render; new alignment edges with
    // different IDs are not found in it and would be treated as visible real edges.
    // buildEdgeRoutingCaches() will repopulate the cache during routeEdges() as usual.
    this.edgeRoutingCache.alignmentEdges.clear();
    this.svgLinkGroups = this.setupLinks(links, layout);
  }

  /**
   * Sets up SVG node elements with rectangles, icons, and labels for WebCola layout.
   * Creates a complete node visualization with proper centering, styling, and interactivity.
   * 
   * @param nodes - Array of nodes with metadata to visualize
   * @param layout - WebCola layout instance for drag behavior
   * @returns D3 selection of created node groups
   */
  private setupNodes(nodes: Array<NodeWithMetadata>, layout: Layout): d3.Selection<SVGGElement, any, any, unknown> {
    // Create node groups with drag behavior
    const nodeDrag = layout.drag();
    this.setupNodeDragHandlers(nodeDrag);

    const nodeSelection = this.container
      .selectAll(".node")
      .data(nodes)
      .enter()
      .append("g")
      .attr("class", (d: any) => {
        const baseClass = this.isErrorNode(d) ? "error-node" : "node";
        if (this.isErrorNode(d) && this.isSmallNode(d)) {
          return baseClass + " small-error-node";
        }
        return baseClass;
      })
      .call(nodeDrag)
      .on('mousedown.inputmode', (d: any) => {
        if (this.isInputModeActive) {
          d3.event.stopPropagation();
          this.startEdgeCreation(d);
        }
      })
      .on('mouseup.inputmode', (d: any) => {
        if (this.isInputModeActive && this.edgeCreationState.isCreating) {
          d3.event.stopPropagation();
          // Handle async operation without blocking the event
          this.finishEdgeCreation(d).catch(error => {
            console.error('Error finishing edge creation:', error);
          });
        }
      })
    // Show tooltip with node ID only when not in input mode
      .on('mouseover', function(d: any) {
          d3.select(this)
            .append('title')
            .attr('class', 'node-tooltip')
            .text(`ID: ${d.id}`);
        
      })
      .on('mouseout', function() {
        d3.select(this).select('title.node-tooltip').remove();
      });

    // Add rectangle backgrounds for nodes
    this.setupNodeRectangles(nodeSelection);

    // Add icons for nodes that have them
    this.setupNodeIcons(nodeSelection);

    // Add most specific type labels
    this.setupMostSpecificTypeLabels(nodeSelection);

    // Add main node labels with attributes
    this.setupNodeLabels(nodeSelection);

    return nodeSelection;
  }

  /**
   * Creates rectangle backgrounds for nodes with proper centering and styling.
   * Handles hidden nodes and icon-only nodes with transparent fills.
   * 
   * @param nodeSelection - D3 selection of node groups
   */
  private setupNodeRectangles(nodeSelection: d3.Selection<SVGGElement, any, any, unknown>): void {
    nodeSelection
      .append("rect")
      .attr("width", (d: any) => d.visualWidth ?? d.width)
      .attr("height", (d: any) => d.visualHeight ?? d.height)
      .attr("x", (d: any) => -((d.visualWidth ?? d.width)) / 2) // Center on node's x position
      .attr("y", (d: any) => -((d.visualHeight ?? d.height)) / 2) // Center on node's y position
      .attr("stroke", (d: any) => d.color || "black")
      .attr("rx", WebColaCnDGraph.NODE_BORDER_RADIUS)
      .attr("ry", WebColaCnDGraph.NODE_BORDER_RADIUS)
      .attr("stroke-width", WebColaCnDGraph.NODE_STROKE_WIDTH)
      .attr("fill", (d: any) => {
        const isHidden = this.isHiddenNode(d);
        const hasIcon = !! d.icon;
        const showLabels = d.showLabels;

        // Tufte: node fill matches the canvas so only the stroke + label
        // distinguish a node — less ink, more clarity. Transparent for
        // hidden nodes and icon-only nodes (preserve prior behavior).
        return isHidden || (hasIcon && !showLabels) ? "transparent" : this.getCanvasBackground();
      });
  }

  /**
   * Adds icon images to nodes that have icon properties.
   * Handles scaling and positioning based on label visibility.
   * Includes error handling for failed icon loads.
   * 
   * @param nodeSelection - D3 selection of node groups
   */
  private setupNodeIcons(nodeSelection: d3.Selection<SVGGElement, any, any, unknown>): void {
    
    
    nodeSelection
      .filter((d: any) => d.icon) // Only nodes with icons
      .append("image")
      .attr("xlink:href", (d: any) => d.icon)
      .attr("width", (d: any) => {
        const vw = d.visualWidth ?? d.width;
        return d.showLabels
          ? vw * WebColaCnDGraph.SMALL_IMG_SCALE_FACTOR
          : vw;
      })
      .attr("height", (d: any) => {
        const vh = d.visualHeight ?? d.height;
        return d.showLabels
          ? vh * WebColaCnDGraph.SMALL_IMG_SCALE_FACTOR
          : vh;
      })
      .attr("x", (d: any) => {
        const width = d.visualWidth ?? d.width;
        if (d.showLabels) {
          // Position in top-right corner when labels are shown
          return d.x + width - (width * WebColaCnDGraph.SMALL_IMG_SCALE_FACTOR);
        }
        // Center horizontally when no labels
        return d.x - width / 2;
      })
      .attr("y", (d: any) => {
        const height = d.visualHeight ?? d.height;
        // Always align with top edge
        return d.y - height / 2;
      })
      .append("title")
      .text((d: any) => d.label || d.name || d.id || "Node")
      .on("error", function(this: any, event: any, d: any) {

        d3.select(this).attr("xlink:href", "img/default.png");
        console.error(`Failed to load icon for node ${d.id}: ${d.icon}`);
      });
  }

  /**
   * Adds most specific type labels to nodes with proper styling.
   * 
   * @param nodeSelection - D3 selection of node groups
   */
  private setupMostSpecificTypeLabels(nodeSelection: d3.Selection<SVGGElement, any, any, unknown>): void {
    nodeSelection
      .append("text")
      .attr("class", "mostSpecificTypeLabel")
      .style("fill", (d: any) => d.color || "black")
      .text((d: any) => d.mostSpecificType || "");
  }

  /**
   * Gets a canvas context for text measurement
   */
  private getTextMeasurementContext(): CanvasRenderingContext2D {
    if (!this.textMeasurementCanvas) {
      this.textMeasurementCanvas = document.createElement('canvas');
    }
    return this.textMeasurementCanvas.getContext('2d')!;
  }

  /**
   * Measures the width of text at a given font size
   */
  private measureTextWidth(text: string, fontSize: number, fontFamily?: string): number {
    const context = this.getTextMeasurementContext();
    context.font = `${fontSize}px ${fontFamily ?? this.getFontFamily()}`;
    return context.measureText(text).width;
  }



  /**
   * Creates main node labels with attributes using fixed font sizes.
   *
   * Box dimensions are decided upstream by `LayoutInstance.getNodeSizeMap`
   * via `estimateLabelBox`, which sizes each box to fit the same fixed font
   * sizes used here. No per-node font negotiation; if a user pins a too-small
   * `size` directive their text overflows (their explicit choice).
   */
  private setupNodeLabels(nodeSelection: d3.Selection<SVGGElement, any, any, unknown>): void {
    const secondaryLineHeight = SECONDARY_FONT_SIZE * LABEL_LINE_HEIGHT_RATIO;

    nodeSelection
      .append("text")
      .attr("class", "label")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("font-family", this.getFontFamily())
      .attr("fill", "black")
      .each((d: any, i: number, nodes: SVGTextElement[]) => {
        if (this.isHiddenNode(d)) {
          return;
        }

        if (!d.showLabels) {
          return;
        }

        const textElement = d3.select(nodes[i]);

        const displayLabel = d.label || d.name || d.id || "Node";
        const attributeEntries = Object.entries(d.attributes || {})
          .sort(([a], [b]) => a.localeCompare(b));
        const labelEntries = Object.entries(d.labels || {});
        const totalSecondaryEntries = labelEntries.length + attributeEntries.length;

        // Center the entire label block on the node: shift the main label up
        // by half the secondary block's height so the visual centroid sits at d.y.
        const verticalOffset = totalSecondaryEntries > 0
          ? -totalSecondaryEntries * secondaryLineHeight * 0.5
          : 0;

        // Cached for updatePositions, which re-applies dy to each tspan after layout.
        d._labelVerticalOffset = verticalOffset;
        d._labelLineHeight = secondaryLineHeight;

        textElement.attr("font-size", `${MAIN_LABEL_FONT_SIZE}px`);

        textElement
          .append("tspan")
          .attr("x", 0)
          .attr("dy", `${verticalOffset}px`)
          .attr("class", "main-label-tspan")
          .style("font-weight", "bold")
          .style("font-size", `${MAIN_LABEL_FONT_SIZE}px`)
          .text(displayLabel);

        // Skolem-style labels: italic, comma-separated values per key.
        for (const [, values] of labelEntries) {
          const labelText = Array.isArray(values) ? values.join(', ') : String(values);
          textElement
            .append("tspan")
            .attr("x", 0)
            .attr("dy", `${secondaryLineHeight}px`)
            .style("font-size", `${SECONDARY_FONT_SIZE}px`)
            .style("fill", 'black')
            .style("font-style", "italic")
            .text(labelText);
        }

        // Attribute lines: "key: value, value, ..."
        for (const [key, value] of attributeEntries) {
          textElement
            .append("tspan")
            .attr("x", 0)
            .attr("dy", `${secondaryLineHeight}px`)
            .style("font-size", `${SECONDARY_FONT_SIZE}px`)
            .text(`${key}: ${value}`);
        }
      });
  }

  /**
   * Render nodes using D3 data binding with drag behavior
   */
  private renderNodes(nodes: Array<NodeWithMetadata>, layout: Layout): void {
    this.svgNodes = this.setupNodes(nodes, layout);
  }

  /**
   * Resolves a group edge's {source, target} into rendering endpoints.
   *
   * For `_g_` edges the rendered arrow terminates at the group boundary rather
   * than the member-node center.  By construction, `d.groupId` IS the target
   * group: the edge always runs from the external anchor node (source) to a
   * member of that group (target).  The source is always kept as-is — even if
   * it happens to live in a different group, the arrow must still leave from
   * the source node itself, not from the source's group boundary.
   *
   * @param d - Edge data object (must have d.groupId set for group edges).
   * @returns { source, target } where target is replaced by the group object.
   */
  private resolveGroupEdgeEndpoints(d: any): { source: any; target: any } {
    if (!d.groupId) {
      return { source: d.source, target: d.target };
    }

    const groups = this.currentLayout?.groups || [];
    const group = groups.find((g: any) => g.id === d.groupId);

    // DEBUG — log once per unique edge id
    if (!(this as any)._groupEdgeDebugLogged) (this as any)._groupEdgeDebugLogged = new Set();
    if (!(this as any)._groupEdgeDebugLogged.has(d.id)) {
      (this as any)._groupEdgeDebugLogged.add(d.id);
      console.log('[groupEdge DEBUG]', {
        edgeId: d.id,
        groupId: d.groupId,
        keyNodeId: d.keyNodeId,
        sourceId: d.source?.id,
        targetId: d.target?.id,
        sourceIsInt: typeof d.source === 'number',
        targetIsInt: typeof d.target === 'number',
        groupFound: !!group,
        groupId_on_group: group?.id,
        availableGroupIds: groups.map((g: any) => g.id),
      });
    }

    if (!group) return { source: d.source, target: d.target };

    // d.keyNodeId is stamped at edge-construction time (= graphlib edge.v = groupOn).
    // The key node is the external anchor; the other end is the group member.
    let source = d.source;
    let target = d.target;

    if (d.source?.id === d.keyNodeId) {
      target = group;       // source is anchor, target is member → snap target to group
    } else if (d.target?.id === d.keyNodeId) {
      source = group;       // target is anchor, source is member → snap source to group
    } else {
      // keyNodeId didn't match either side — log and leave unmodified
      console.warn('[groupEdge] keyNodeId matched neither side', {
        keyNodeId: d.keyNodeId, sourceId: d.source?.id, targetId: d.target?.id
      });
    }

    // If the group has no bounds yet (first ticks under stability / fast convergence),
    // synthesise temporary bounds from the MEMBER node (not anchor) so the edge
    // points in the right direction.
    const memberNode = source === group ? d.source : target === group ? d.target : null;
    if (memberNode && !group.bounds && memberNode.x != null && cola?.Rectangle) {
      const hw = (memberNode.visualWidth ?? memberNode.width ?? 50) / 2;
      const hh = (memberNode.visualHeight ?? memberNode.height ?? 30) / 2;
      group.bounds = new cola.Rectangle(
        memberNode.x - hw, memberNode.x + hw,
        memberNode.y - hh, memberNode.y + hh
      );
    }

    return { source, target };
  }

  /**
   * Update positions of all visual elements during layout animation.
   * Implements the complete WebCola tick behavior with proper bounds handling,
   * group edge routing, and element layering.
   */

  /**
   * Position only node rects, icons, and labels — skipping groups and edges
   * whose bounds/endpoints may not be initialized yet.
   * Used to show continuing nodes at their old positions while the solver
   * runs, before groups and edge data are available.
   */
  private updateNodePositionsOnly(): void {
    if (!this.svgNodes) return;

    this.svgNodes.select('rect')
      .attr('x', (d: any) => d.x != null ? d.x - (d.visualWidth ?? d.width) / 2 : 0)
      .attr('y', (d: any) => d.y != null ? d.y - (d.visualHeight ?? d.height) / 2 : 0)
      .attr('width', (d: any) => d.visualWidth ?? d.width)
      .attr('height', (d: any) => d.visualHeight ?? d.height);

    this.svgNodes.select('image')
      .attr('x', (d: any) => {
        if (d.x == null) return 0;
        const vw = d.visualWidth ?? d.width;
        return d.showLabels ? d.x + vw / 2 - (vw * WebColaCnDGraph.SMALL_IMG_SCALE_FACTOR) : d.x - vw / 2;
      })
      .attr('y', (d: any) => {
        if (d.y == null) return 0;
        const vh = d.visualHeight ?? d.height;
        return d.y - vh / 2;
      });

    this.svgNodes.select('.mostSpecificTypeLabel')
      .attr('x', (d: any) => d.x != null ? d.x - ((d as any).visualWidth ?? d.width ?? 0) / 2 + 5 : 0)
      .attr('y', (d: any) => d.y != null ? d.y - ((d as any).visualHeight ?? d.height ?? 0) / 2 + 10 : 0);

    this.svgNodes.select('.label')
      .attr('x', (d: any) => d.x ?? 0)
      .attr('y', (d: any) => d.y ?? 0)
      .each((d: any, i: number, nodes: Array<any>) => {
        if (d.x == null) return;
        const verticalOffset = d._labelVerticalOffset || 0;
        const lineHeight = d._labelLineHeight || 12;
        d3.select(nodes[i])
          .selectAll('tspan')
          .attr('x', d.x)
          .attr('dy', (_: any, tspanIdx: number) => tspanIdx === 0 ? `${verticalOffset}px` : `${lineHeight}px`);
      });
  }

  private updatePositions(): void {

    
    // Update group positions and sizes first (lower layer)
    this.svgGroups
      .attr('x', (d: any) => d.bounds.x)
      .attr('y', (d: any) => d.bounds.y )
      .attr('width', (d: any) => d.bounds.width() )
      .attr('height', (d: any) => d.bounds.height() )
      .lower();

    // Update node rectangles using visual dimensions (NOT d.bounds, which WebCola inflates
    // for collision avoidance). Rects should reflect the original visual size.
    this.svgNodes.select('rect')
      .each((d: any) => {
        if (d.bounds) {
          d.innerBounds = d.bounds.inflate(-1);
        }
      })
      .attr('x', (d: any) => d.x - (d.visualWidth ?? d.width) / 2)
      .attr('y', (d: any) => d.y - (d.visualHeight ?? d.height) / 2)
      .attr('width', (d: any) => d.visualWidth ?? d.width)
      .attr('height', (d: any) => d.visualHeight ?? d.height);

    // Update node icons with proper positioning
    this.svgNodes.select('image')
      .attr('x', (d: any) => {
        const vw = d.visualWidth ?? d.width;
        if (d.showLabels) {
          // Move to the top-right corner
          return d.x + vw / 2 - (vw * WebColaCnDGraph.SMALL_IMG_SCALE_FACTOR);
        } else {
          // Align with visual left edge
          return d.x - vw / 2;
        }
      })
      .attr('y', (d: any) => {
        const vh = d.visualHeight ?? d.height;
        if (d.showLabels) {
          // Align with the top edge
          return d.y - vh / 2;
        } else {
          // Align with visual top edge
          return d.y - vh / 2;
        }
      });

    // Update most specific type labels — positioned at top-left of the node
    this.svgNodes.select('.mostSpecificTypeLabel')
      .attr('x', (d: NodeWithMetadata) => d.x - ((d as any).visualWidth ?? d.width ?? 0) / 2 + 5)
      .attr('y', (d: NodeWithMetadata) => d.y - ((d as any).visualHeight ?? d.height ?? 0) / 2 + 10)
      .raise();

    // Update main node labels with tspan positioning
    this.svgNodes.select('.label')
      .attr('x', (d: NodeWithMetadata) => d.x)
      .attr('y', (d: NodeWithMetadata) => d.y)
      .each((d: any, i: number, nodes: Array<any>) => {
        const verticalOffset = d._labelVerticalOffset || 0;
        const lineHeight = d._labelLineHeight || 12;
        d3.select(nodes[i])
          .selectAll('tspan')
          .attr('x', d.x)
          .attr('dy', (tspanData: any, tspanIdx: number) => {
            if (tspanIdx === 0) {
              return `${verticalOffset}px`;
            }
            return `${lineHeight}px`;
          });
      })
      .raise();

    // Update link paths with stable anchor-based routing to prevent jitter during dragging.
    // Select the main path (identified by data-link-id) so any highlight underlay sibling
    // is not picked up here — markers and .raise() apply only to the main edge.
    this.svgLinkGroups.select('path[data-link-id]')
      .attr('d', (d: EdgeWithMetadata) => {
        // Resolve group-edge endpoints (group boundary instead of member node center).
        const { source, target } = this.resolveGroupEdgeEndpoints(d);

        // Use stable anchor-based edge routing to prevent jitter during dragging
        // This approach selects consistent edge anchor points based on dominant direction
        // rather than computing dynamic ray intersections that can jump erratically
        const route = this.getStableEdgePath(source, target, d);
        return this.lineFunction(route);
      })
      .attr('marker-end', (d: EdgeWithMetadata) => {
        if (this.isAlignmentEdge(d)) return 'none';
        return 'url(#end-arrow)';
      })
      .attr('marker-start', (d: EdgeWithMetadata) => {
        // Add marker-start for bidirectional edges
        if (this.isAlignmentEdge(d) || !d.bidirectional) return 'none';
        return 'url(#start-arrow)';
      })
      .raise();

    // Mirror the main path's `d` onto the highlight underlay sibling.
    // Cheap: one getAttribute + setAttribute per highlighted edge per tick.
    this.svgLinkGroups.select('path.highlight-underlay')
      .attr('d', function () {
        const parent = (this as SVGPathElement).parentNode as Element | null;
        const main = parent?.querySelector('path[data-link-id]');
        return main?.getAttribute('d') ?? null;
      });

    // Update link labels using path midpoint calculation
    this.svgLinkGroups.select('.linklabel')
      .attr('x', (d: EdgeWithMetadata) => {
        const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${d.id}"]`) as SVGPathElement;
        return pathElement ? this.calculateNewPosition(pathElement, 'x') : (d.source.x + d.target.x) / 2;
      })
      .attr('y', (d: EdgeWithMetadata) => {
        const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${d.id}"]`) as SVGPathElement;
        return pathElement ? this.calculateNewPosition(pathElement, 'y') : (d.source.y + d.target.y) / 2;
      })
      .style('font-size', () => {
        // Zoom-aware font sizing: scale up slightly when zoomed out
        const zoomScale = this.getCurrentZoomScale();
        const baseFontSize = 12;
        // When zoomed out (scale < 1), increase font size slightly to maintain readability
        // When zoomed in (scale > 1), keep base size
        const adjustedSize = zoomScale < 1 ? baseFontSize / Math.sqrt(zoomScale) : baseFontSize;
        return `${Math.min(adjustedSize, 16)}px`; // Cap at 16px
      })
      .raise();

    // Update edge endpoint markers for input mode
    this.updateEdgeEndpointMarkers();

    // Update group labels (center top of each group)
    this.svgGroupLabels
      .attr('x', (d: any) => {
        if (!d.bounds) return 0;
        return d.bounds.x + (d.bounds.width() / 2);
      })
      .attr('y', (d: any) => {
        if (!d.bounds) return 0;
        const labelFontSize = d._groupLabelFontSize || WebColaCnDGraph.DEFAULT_FONT_SIZE;
        return d.bounds.y + WebColaCnDGraph.GROUP_LABEL_PILL_OFFSET_Y + Math.max(4, labelFontSize * 0.35);
      })
      .attr('text-anchor', 'middle')
      .each(function (d: any) {
        const text = this as SVGTextElement;
        if (text.textContent && d.bounds) {
          const bbox = text.getBBox();
          d._labelBBox = { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
        } else {
          d._labelBBox = null;
        }
      });

    // Position the pill backing rect to wrap the label text.
    if (this.svgGroupLabelBgs) {
      this.svgGroupLabelBgs
        .style('display', (d: any) => (d._labelBBox ? null : 'none'))
        .attr('x', (d: any) => d._labelBBox ? d._labelBBox.x - WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_X : 0)
        .attr('y', (d: any) => d._labelBBox ? d._labelBBox.y - WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_Y : 0)
        .attr('width', (d: any) => d._labelBBox ? d._labelBBox.width + WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_X * 2 : 0)
        .attr('height', (d: any) => d._labelBBox ? d._labelBBox.height + WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_Y * 2 : 0);
      this.svgGroupLabelBgs.raise();
    }
    this.svgGroupLabels.raise();

    // Ensure proper layering - raise important elements
    this.svgLinkGroups.selectAll('marker').raise();
    this.svgLinkGroups.selectAll('.linklabel').raise();
    this.svgNodes.selectAll('.error-node').raise();
  }

  /**
   * Update positions of edge endpoint markers
   * Positions them at the arrow/marker positions of edges
   */
  private updateEdgeEndpointMarkers(): void {
    if (!this.svgLinkGroups) return;

    // Update target markers (at the arrow end)
    this.svgLinkGroups.select('.target-marker')
      .attr('cx', (d: EdgeWithMetadata) => {
        const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${d.id}"]`) as SVGPathElement;
        if (pathElement) {
          const pathLength = pathElement.getTotalLength();
          const point = pathElement.getPointAtLength(pathLength);
          return point.x;
        }
        return d.target.x || 0;
      })
      .attr('cy', (d: EdgeWithMetadata) => {
        const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${d.id}"]`) as SVGPathElement;
        if (pathElement) {
          const pathLength = pathElement.getTotalLength();
          const point = pathElement.getPointAtLength(pathLength);
          return point.y;
        }
        return d.target.y || 0;
      })
      .attr('opacity', this.isInputModeActive ? 0.8 : 0)
      .style('pointer-events', this.isInputModeActive ? 'all' : 'none')
      .raise(); // Always on top

    // Update source markers (at the start)
    this.svgLinkGroups.select('.source-marker')
      .attr('cx', (d: EdgeWithMetadata) => {
        const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${d.id}"]`) as SVGPathElement;
        if (pathElement) {
          const point = pathElement.getPointAtLength(0);
          return point.x;
        }
        return d.source.x || 0;
      })
      .attr('cy', (d: EdgeWithMetadata) => {
        const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${d.id}"]`) as SVGPathElement;
        if (pathElement) {
          const point = pathElement.getPointAtLength(0);
          return point.y;
        }
        return d.source.y || 0;
      })
      .attr('opacity', this.isInputModeActive ? 0.8 : 0)
      .style('pointer-events', this.isInputModeActive ? 'all' : 'none')
      .raise(); // Always on top
  }

  private gridUpdatePositions() {

    // Force recompute node bounds from current positions
    // This is critical for grid mode to work correctly with all node types
    this.ensureNodeBounds(true);
    
    const node = this.container.selectAll(".node");
    const mostSpecificTypeLabel = this.container.selectAll(".mostSpecificTypeLabel");
    const label = this.container.selectAll(".label");
    const group = this.container.selectAll(".group");
    const groupLabel = this.container.selectAll(".groupLabel");

    // UPDATE NODES AND NODE LABELS
    node.select("rect")
        .each(function (d: any) { d.innerBounds = d.bounds.inflate(-1); })
        .attr("x", function (d: any) { return d.bounds.x; })
        .attr("y", function (d: any) { return d.bounds.y; })
        .attr("width", function (d: any) { return d.bounds.width(); })
        .attr("height", function (d: any) { return d.bounds.height(); });
    

    node.select("image")
        .attr("x", function (d: any) {
            const vw = d.visualWidth ?? d.width;
            if (d.showLabels) {
                // Move to the top-right corner
                return d.x + (vw / 2) - (vw * WebColaCnDGraph.SMALL_IMG_SCALE_FACTOR);
            } else {
                // Align with d.bounds.x
                return d.bounds.x;
            }
        })
        .attr("y", function (d: any) {
            const vh = d.visualHeight ?? d.height;
            if (d.showLabels) {
                // Align with the top edge
                return d.y - vh / 2;
            } else {
                // Align with d.bounds.y
                return d.bounds.y;
            }
        })

    mostSpecificTypeLabel
        .attr("x", function (d: any) { return d.bounds.x + 5; })
        .attr("y", function (d: any) { return d.bounds.y + 10; })
        .raise();

    label
        .attr("x", (d: any) => d.x)
        .attr("y", (d: any) => d.y)
        .each(function (d: any) {
            var y = 0; // Initialize y offset for tspans
            d3.select(this).selectAll("tspan")
                .attr("x", d.x) // Align tspans with the node's x position
                .attr("dy", function () {
                    y += 1; // Increment y for each tspan to create line spacing
                    return y === 1 ? "0em" : "1em"; // Keep the first tspan in place, move others down
                });
        })
        .raise();

    // UPDATE GROUPS AND GROUP LABELS
    group.attr("x", function (d: any) { return d.bounds.x; })
        .attr("y", function (d: any) { return d.bounds.y; })
        .attr("width", function (d: any) { return d.bounds.width(); })
        .attr("height", function (d: any) { return d.bounds.height(); })
        .lower();

    // Render group labels
    groupLabel.attr("x", function (d: any) { return d.bounds.x + d.bounds.width() / 2; }) // Center horizontally
        .attr("y", function (d: any) {
          const labelFontSize = d._groupLabelFontSize || WebColaCnDGraph.DEFAULT_FONT_SIZE;
          return d.bounds.y + WebColaCnDGraph.GROUP_LABEL_PILL_OFFSET_Y + Math.max(4, labelFontSize * 0.35);
        })
        .attr("text-anchor", "middle")
        .each(function (d: any) {
          const text = this as SVGTextElement;
          if (text.textContent && d.bounds) {
            const bbox = text.getBBox();
            d._labelBBox = { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
          } else {
            d._labelBBox = null;
          }
        });

    const groupLabelBg = this.container.selectAll(".groupLabelBg");
    groupLabelBg
      .style("display", (d: any) => (d._labelBBox ? null : "none"))
      .attr("x", (d: any) => d._labelBBox ? d._labelBBox.x - WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_X : 0)
      .attr("y", (d: any) => d._labelBBox ? d._labelBBox.y - WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_Y : 0)
      .attr("width", (d: any) => d._labelBBox ? d._labelBBox.width + WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_X * 2 : 0)
      .attr("height", (d: any) => d._labelBBox ? d._labelBBox.height + WebColaCnDGraph.GROUP_LABEL_PILL_PADDING_Y * 2 : 0)
      .raise();
    groupLabel.raise();

    // UPDATE EDGES - Use orthogonal routing for grid mode
    // Maintain grid-like paths during drag for better visual consistency
    const linkGroups = this.container.selectAll(".link-group");
    linkGroups.select("path")
        .attr("d", (d: any) => {
            // Handle self-loops specially - they can't use orthogonal routing
            if (d.source?.id === d.target?.id) {
                const route = this.createGridSelfLoopRoute(d);
                return this.gridLineFunction(route);
            }

            // Resolve group-edge endpoints: for _g_ edges the arrow should reach
            // the group boundary, not the member node center.
            const { source, target } = this.resolveGroupEdgeEndpoints(d);
            
            // Determine start/end coordinates.
            // For group endpoints (which have .bounds but no .x/.y), use bounds centre.
            const getCenter = (endpoint: any): { x: number; y: number } => {
              if (endpoint.bounds) {
                if (typeof endpoint.bounds.cx === 'function') {
                  return { x: endpoint.bounds.cx(), y: endpoint.bounds.cy() };
                }
                return {
                  x: (endpoint.bounds.x + endpoint.bounds.X) / 2,
                  y: (endpoint.bounds.y + endpoint.bounds.Y) / 2
                };
              }
              return { x: endpoint.x ?? 0, y: endpoint.y ?? 0 };
            };

            const src = getCenter(source);
            const tgt = getCenter(target);
            const dx = tgt.x - src.x;
            const dy = tgt.y - src.y;
            
            // Choose routing based on which direction is dominant
            if (Math.abs(dx) > Math.abs(dy)) {
                // Horizontal first, then vertical
                const midX = src.x + dx / 2;
                return this.gridLineFunction([
                    { x: src.x, y: src.y },
                    { x: midX, y: src.y },
                    { x: midX, y: tgt.y },
                    { x: tgt.x, y: tgt.y }
                ]);
            } else {
                // Vertical first, then horizontal
                const midY = src.y + dy / 2;
                return this.gridLineFunction([
                    { x: src.x, y: src.y },
                    { x: src.x, y: midY },
                    { x: tgt.x, y: midY },
                    { x: tgt.x, y: tgt.y }
                ]);
            }
        });
    
    // Update link labels to follow edges using path midpoint
    linkGroups.select("text.linklabel")
        .attr("x", (d: any) => {
            const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${d.id}"]`) as SVGPathElement;
            if (pathElement) {
                const pathLength = pathElement.getTotalLength();
                const midpoint = pathElement.getPointAtLength(pathLength / 2);
                return midpoint.x;
            }
            // Use node.x/y as primary source (same as default mode)
            const sourceX = d.source?.x ?? d.source?.bounds?.cx() ?? 0;
            const targetX = d.target?.x ?? d.target?.bounds?.cx() ?? 0;
            return (sourceX + targetX) / 2;
        })
        .attr("y", (d: any) => {
            const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${d.id}"]`) as SVGPathElement;
            if (pathElement) {
                const pathLength = pathElement.getTotalLength();
                const midpoint = pathElement.getPointAtLength(pathLength / 2);
                return midpoint.y;
            }
            // Use node.x/y as primary source (same as default mode)
            const sourceY = d.source?.y ?? d.source?.bounds?.cy() ?? 0;
            const targetY = d.target?.y ?? d.target?.bounds?.cy() ?? 0;
            return (sourceY + targetY) / 2;
        })
        .raise();
  }

  /**
   * Advanced edge routing with curvature calculation and overlap handling.
   * Implements sophisticated routing for multiple edges between nodes, self-loops,
   * and group edges with proper collision detection and label positioning.
   */
  private routeEdges(): void {
    try {
      // Force-recompute bounds using visual dimensions (visualWidth/visualHeight) before
      // routing. After layout converges, WebCola has set node.bounds to the inflated
      // collision size (width/height + padding). Without forceRecompute=true the stale
      // check would pass (center still matches x,y) and leave inflated bounds in place,
      // causing edges to stop short of the visual node boundary.
      this.ensureNodeBounds(true);

      // Prepare edge routing with margin
      if (typeof (this.colaLayout as any)?.prepareEdgeRouting === 'function') {
        (this.colaLayout as any).prepareEdgeRouting(
          WebColaCnDGraph.VIEWBOX_PADDING / WebColaCnDGraph.EDGE_ROUTE_MARGIN_DIVISOR
        );
      }

      // Build caches for optimization before routing edges
      this.buildEdgeRoutingCaches();

      // Sort edge ports by angle to minimize crossings at shared nodes
      this.sortEdgePortsByAngle();

      // Compute routes for all edges (stored in computedRoutes map)
      this.computeAllRoutes();

      // Post-routing crossing optimization
      this.optimizeCrossings();

      // Apply computed routes to SVG
      this.applyRoutesToSVG();

      // Update link labels with proper positioning
      this.updateLinkLabelsAfterRouting();

      // Auto-fit viewport to content
      this.fitViewportToContent();
    } catch (error) {
      console.error('Error in edge routing:', error);
      this.showError(`Edge routing failed: ${(error as Error).message}`);
    }
  }

  /**
   * Ensures all nodes have bounds (Rectangle) objects computed.
   * This is necessary for edge routing to work correctly.
   * 
   * When using prior positions with minimal iterations, WebCola may not
   * have computed bounds for nodes. This method manually creates Rectangle
   * bounds based on node x, y, width, height properties.
   * 
   * @param forceRecompute - If true, always recompute bounds even if they exist
   */
  private ensureNodeBounds(forceRecompute: boolean = false): void {
    if (!this.currentLayout?.nodes || !cola?.Rectangle) return;

    for (const node of this.currentLayout.nodes) {
      // Skip if bounds already exist and are valid, unless forceRecompute is true
      if (!forceRecompute && node.bounds && typeof node.bounds.rayIntersection === 'function') {
        // Check if bounds are still in sync with position
        const currentCx = node.bounds.cx();
        const currentCy = node.bounds.cy();
        const tolerance = 1; // Allow small floating point differences
        if (Math.abs(currentCx - (node.x || 0)) < tolerance && 
            Math.abs(currentCy - (node.y || 0)) < tolerance) {
          continue;
        }
      }

      // Compute bounds from node position and visual dimensions (not the inflated
      // collision dimensions stored in node.width/height).
      // Rectangle constructor: (x, X, y, Y) where x,y is top-left and X,Y is bottom-right
      const halfWidth = ((node as any).visualWidth ?? node.width ?? 50) / 2;
      const halfHeight = ((node as any).visualHeight ?? node.height ?? 30) / 2;
      const x = (node.x || 0) - halfWidth;
      const X = (node.x || 0) + halfWidth;
      const y = (node.y || 0) - halfHeight;
      const Y = (node.y || 0) + halfHeight;

      node.bounds = new cola.Rectangle(x, X, y, Y);
      node.innerBounds = node.bounds.inflate(-1);
    }
  }

  /**
   * Builds caches for edge routing optimization.
   * Pre-computes edge relationships and alignment edge sets to avoid redundant calculations.
   */
  private buildEdgeRoutingCaches(): void {
    // Clear existing caches
    this.edgeRoutingCache.edgesBetweenNodes.clear();
    this.edgeRoutingCache.alignmentEdges.clear();
    this.edgeRoutingCache.nodeEdgesBySide.clear();

    if (!this.currentLayout?.links) return;

    // Build alignment edge set for O(1) lookups
    this.currentLayout.links.forEach((edge: EdgeWithMetadata) => {
      if (edge.id?.startsWith("_alignment_")) {
        this.edgeRoutingCache.alignmentEdges.add(edge.id);
      }
    });

    // Build edges-between-nodes cache and node-edges-by-side cache
    this.currentLayout.links.forEach((edge: EdgeWithMetadata) => {
      if (this.isAlignmentEdge(edge)) return; // Skip alignment edges

      const sourceId = edge.source.id;
      const targetId = edge.target.id;

      // Create sorted key to handle bidirectional lookups
      const key = this.getNodePairKey(sourceId, targetId);

      if (!this.edgeRoutingCache.edgesBetweenNodes.has(key)) {
        this.edgeRoutingCache.edgesBetweenNodes.set(key, []);
      }
      this.edgeRoutingCache.edgesBetweenNodes.get(key)!.push(edge);

      // Skip self-loops and group edges for port ordering
      if (sourceId === targetId || edge.id?.startsWith('_g_')) return;

      // Determine which side of source/target this edge exits/enters
      const sourceCx = edge.source.x || 0;
      const sourceCy = edge.source.y || 0;
      const targetCx = edge.target.x || 0;
      const targetCy = edge.target.y || 0;
      const dx = targetCx - sourceCx;
      const dy = targetCy - sourceCy;
      const angle = Math.atan2(dy, dx);
      const sourceDirRaw = this.getDominantDirection(angle);
      const targetDirRaw = this.getDominantDirection(angle + Math.PI); // Reverse direction for target
      // Map getDominantDirection's 'up'/'down' to our 'top'/'bottom' side names
      const dirToSide = (d: string | null): 'top' | 'bottom' | 'left' | 'right' | null => {
        if (d === 'up') return 'top';
        if (d === 'down') return 'bottom';
        if (d === 'left' || d === 'right') return d;
        return null;
      };
      const sourceSide = dirToSide(sourceDirRaw);
      const targetSide = dirToSide(targetDirRaw);

      if (sourceSide) {
        if (!this.edgeRoutingCache.nodeEdgesBySide.has(sourceId)) {
          this.edgeRoutingCache.nodeEdgesBySide.set(sourceId, { top: [], bottom: [], left: [], right: [] });
        }
        this.edgeRoutingCache.nodeEdgesBySide.get(sourceId)![sourceSide].push(
          { edge, role: 'source', remoteX: targetCx, remoteY: targetCy }
        );
      }
      if (targetSide) {
        if (!this.edgeRoutingCache.nodeEdgesBySide.has(targetId)) {
          this.edgeRoutingCache.nodeEdgesBySide.set(targetId, { top: [], bottom: [], left: [], right: [] });
        }
        this.edgeRoutingCache.nodeEdgesBySide.get(targetId)![targetSide].push(
          { edge, role: 'target', remoteX: sourceCx, remoteY: sourceCy }
        );
      }
    });
  }

  /**
   * Sorts edges at each node side by the angular position of their remote endpoint.
   * This ensures edges don't cross each other unnecessarily where they leave/enter a node.
   *
   * For top/bottom sides: sorts left-to-right by remote node's x-coordinate.
   * For left/right sides: sorts top-to-bottom by remote node's y-coordinate.
   *
   * Stamps _sourcePortIndex, _sourcePortCount, _targetPortIndex, _targetPortCount
   * on each edge data object for use during offset calculation.
   */
  private sortEdgePortsByAngle(): void {
    const tieBreak = (a: any, b: any): number => {
      const ida = String(a.edge?.id ?? '');
      const idb = String(b.edge?.id ?? '');
      if (ida < idb) return -1;
      if (ida > idb) return 1;
      return 0;
    };
    for (const [, sides] of this.edgeRoutingCache.nodeEdgesBySide) {
      // Top/bottom: sort by remote x (left to right), edge id as tie-breaker
      for (const side of ['top', 'bottom'] as const) {
        const entries = sides[side];
        if (entries.length <= 1) continue;
        entries.sort((a: any, b: any) => (a.remoteX - b.remoteX) || tieBreak(a, b));
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (entry.role === 'source') {
            entry.edge._sourcePortIndex = i;
            entry.edge._sourcePortCount = entries.length;
          } else {
            entry.edge._targetPortIndex = i;
            entry.edge._targetPortCount = entries.length;
          }
        }
      }
      // Left/right: sort by remote y (top to bottom), edge id as tie-breaker
      for (const side of ['left', 'right'] as const) {
        const entries = sides[side];
        if (entries.length <= 1) continue;
        entries.sort((a: any, b: any) => (a.remoteY - b.remoteY) || tieBreak(a, b));
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (entry.role === 'source') {
            entry.edge._sourcePortIndex = i;
            entry.edge._sourcePortCount = entries.length;
          } else {
            entry.edge._targetPortIndex = i;
            entry.edge._targetPortCount = entries.length;
          }
        }
      }
    }
  }

  /**
   * Creates a consistent cache key for a node pair (order-independent).
   * 
   * @param sourceId - ID of the source node
   * @param targetId - ID of the target node
   * @returns Cache key string in format "id1:id2" where id1 < id2 lexicographically
   */
  private getNodePairKey(sourceId: string, targetId: string): string {
    return sourceId < targetId ? `${sourceId}:${targetId}` : `${targetId}:${sourceId}`;
  }

  private route(nodes: any[] = [], groups: any[] = [], margin: number, groupMargin: number): GridRouter<any> {
    nodes.forEach((d: any) => {
      const bounds = d.bounds || d.innerBounds || this.createFallbackBounds(d);
      d.routerNode = {
        name: d.name,
        bounds
      };
    });
    groups.forEach((d: any) => {
      if (!d.bounds) {
        console.warn("Grid routing group missing bounds; routing may be degraded.", d);
      }
      d.routerNode = {
        bounds: d.bounds?.inflate(-groupMargin) ?? d.bounds,
        children: (typeof d.groups !== 'undefined' ? d.groups.map((c: any) => nodes.length + c.id) : [])
          .concat(typeof d.leaves !== 'undefined' ? d.leaves.map((c: any) => c.index) : [])
      };
    });
    let gridRouterNodes = nodes.concat(groups).map((d: any, i: number) => {
      if (!d.routerNode) {
        return null;
      }
      d.routerNode.id = i;
      return d.routerNode;
    }).filter(Boolean);
    // NOTE: Router nodes are nodes needed for grid routing, which include both nodes and groups
    return new cola.GridRouter(gridRouterNodes, {
        getChildren: (v: any) => v.children,
        getBounds: (v: any) => v.bounds
    }, margin - groupMargin);
  }

  private gridify(nudgeGap: number, margin: number, groupMargin: number): void {
    // Guard against re-entrance - prevents infinite loops when gridify is called
    // while already in progress (e.g., from event handlers or callbacks)
    if (this.isGridifyingInProgress) {
      console.warn('[gridify] Already in progress, skipping re-entrant call');
      return;
    }

    this.isGridifyingInProgress = true;

    try {
      this.gridifyInternal(nudgeGap, margin, groupMargin);
    } catch (e) {
      console.log("Error routing edges in GridRouter");
      console.error(e);

      // Fall back to simple orthogonal routing on error
      try {
        this.fallbackGridRouting(this.currentLayout?.links ?? []);
      } catch (fallbackError) {
        console.error("Fallback grid routing also failed:", fallbackError);
      }

      let runtimeMessages = document.getElementById("runtime_messages") as HTMLElement;
      if (runtimeMessages) {
        let dismissableAlert = document.createElement("div");
        dismissableAlert.className = "alert alert-danger alert-dismissible fade show";
        dismissableAlert.setAttribute("role", "alert");
        dismissableAlert.innerHTML = `Runtime (WebCola) error when gridifying edges. You may have to click and drag these nodes slightly to un-stick layout.`;

        // Make sure we don't have duplicate alerts
        let existingAlerts = runtimeMessages.querySelectorAll(".alert");
        existingAlerts.forEach(alert => {
            if (alert.innerHTML === dismissableAlert.innerHTML) {
                alert.remove();
            }
        });

        runtimeMessages.appendChild(dismissableAlert);
      }
    } finally {
      // Always reset the guard flag, even if an error occurred
      this.isGridifyingInProgress = false;
    }
  }

  /**
   * Internal implementation of gridify, separated to ensure proper cleanup in finally block.
   */
  private gridifyInternal(nudgeGap: number, margin: number, groupMargin: number): void {
      const nodes = this.currentLayout?.nodes ?? [];
      const groups = this.currentLayout?.groups ?? [];
      const edges = this.currentLayout?.links ?? [];

      if (nodes.length === 0) {
        console.warn("No nodes available for GridRouter; skipping gridify.");
        return;
      }

      if (edges.length === 0) {
        console.warn("No edges to route in GridRouter");
        return;
      }

      // Debug: log node positions BEFORE ensureNodeBounds
      console.log('[gridify] Node positions BEFORE ensureNodeBounds:');
      nodes.slice(0, 3).forEach((n: any) => {
        console.log(`  ${n.id}: x=${n.x?.toFixed(2)}, y=${n.y?.toFixed(2)}, bounds.cx=${n.bounds?.cx?.()?.toFixed(2)}, bounds.x=${n.bounds?.x?.toFixed(2)}`);
      });

      // Force recompute all node bounds from current positions
      // This ensures bounds are always in sync with node x/y coordinates
      // Note: ensureNodeBounds provides default width/height (50/30) for nodes without explicit dimensions
      this.ensureNodeBounds(true);

      // Safety check: ensure all nodes have valid numeric positions AFTER bounds setup
      // Only check x/y positions - width/height are now guaranteed by ensureNodeBounds defaults
      // Invalid positions (NaN, Infinity) can cause GridRouter to hang
      const invalidNodes = nodes.filter((n: any) => 
        !Number.isFinite(n.x) || !Number.isFinite(n.y)
      );
      if (invalidNodes.length > 0) {
        console.warn('[gridify] Found nodes with invalid positions, falling back to default routing:', 
          invalidNodes.map((n: any) => ({ id: n.id, x: n.x, y: n.y })));
        // Fall back to simple orthogonal routing without GridRouter
        this.fallbackGridRouting(edges);
        return;
      }

      // Debug: log node positions AFTER ensureNodeBounds  
      console.log('[gridify] Node positions AFTER ensureNodeBounds:');
      nodes.slice(0, 3).forEach((n: any) => {
        console.log(`  ${n.id}: x=${n.x?.toFixed(2)}, y=${n.y?.toFixed(2)}, bounds.cx=${n.bounds?.cx?.()?.toFixed(2)}, bounds.x=${n.bounds?.x?.toFixed(2)}`);
      });

      // Build/refresh port assignments so parallel edges use distinct entry/exit
      // points. Without this, multiple A→B edges share the same orthogonal route.
      this.buildEdgeRoutingCaches();
      this.sortEdgePortsByAngle();

      // Create the grid router
      const gridrouter = this.route(nodes, groups, margin, groupMargin);

      // Route all edges using the GridRouter
      let routes: any[] = [];
      
      // Filter edges for GridRouter:
      // 1. Must have routerNode on both source and target
      // 2. Must NOT be self-loops (source === target) - GridRouter can't handle these
      const routableEdges = edges.filter((edge: any) => {
        const hasRouterNodes = edge?.source?.routerNode && edge?.target?.routerNode;
        const isSelfLoop = edge?.source?.id === edge?.target?.id;
        return hasRouterNodes && !isSelfLoop;
      });
      
      // Identify self-loop edges for special handling
      const selfLoopEdges = edges.filter((edge: any) => edge?.source?.id === edge?.target?.id);
      
      // Debug: log routing info
      console.log('[gridify] Total edges:', edges.length, 'Routable:', routableEdges.length, 'Self-loops:', selfLoopEdges.length);
      if (routableEdges.length + selfLoopEdges.length !== edges.length) {
        const unroutableEdges = edges.filter((edge: any) => 
          (!edge?.source?.routerNode || !edge?.target?.routerNode) && 
          edge?.source?.id !== edge?.target?.id
        );
        unroutableEdges.forEach((e: any) => {
          console.warn('[gridify] Unroutable edge:', e.id, 
            'source routerNode:', !!e?.source?.routerNode,
            'target routerNode:', !!e?.target?.routerNode,
            'source:', e?.source?.id, 'x:', e?.source?.x, 'y:', e?.source?.y,
            'target:', e?.target?.id, 'x:', e?.target?.x, 'y:', e?.target?.y);
        });
      }
      
      routes = gridrouter.routeEdges(
        routableEdges,
        nudgeGap,
        function (e: any) { return e.source.routerNode.id; },
        function (e: any) { return e.target.routerNode.id; }
      );

      const routesByEdgeId = new Map<string, any>();
      // Tier 1.4: only run bend flattening on graphs below the same threshold as
      // crossing optimization — keeps cost zero on dense graphs (matching policy
      // at CROSSING_OPTIMIZATION_EDGE_THRESHOLD).
      const shouldFlattenBends = routableEdges.length <= WebColaCnDGraph.CROSSING_OPTIMIZATION_EDGE_THRESHOLD;
      routableEdges.forEach((edge: any, index: number) => {
        const route = routes[index];
        if (edge?.id && route) {
          let processed = this.adjustGridRouteForEdge(edge, route);
          if (shouldFlattenBends) {
            processed = this.flattenGridRouteBends(processed, nodes, edge);
          }
          routesByEdgeId.set(edge.id, processed);
        }
      });

      // Note: port distribution for parallel edges is applied at draw time
      // inside adjustGridRouteForArrowPositioning, after rectangle clipping —
      // doing it here would be overwritten by that pass.

      console.log('[gridify] Routes generated:', routesByEdgeId.size, 'out of', routableEdges.length);

      const linkGroups = this.container.selectAll(".link-group").data(edges, (d: any) => d.id ?? d);

      linkGroups.select("path")
        .attr("d", (edgeData: any) => {
          // Handle self-loops specially - GridRouter can't route these
          if (edgeData.source?.id === edgeData.target?.id) {
            // Orthogonal "out and back" hook to match grid mode's rectilinear style
            const route = this.createGridSelfLoopRoute(edgeData);
            return this.gridLineFunction(route);
          }
          
          const route = routesByEdgeId.get(edgeData.id);
          if (!route) {
            // Create orthogonal fallback path for unroutable edges
            // Use node.x/y as primary source (same as default mode) with bounds as fallback
            const sourceX = edgeData.source?.x ?? edgeData.source?.bounds?.cx() ?? 0;
            const sourceY = edgeData.source?.y ?? edgeData.source?.bounds?.cy() ?? 0;
            const targetX = edgeData.target?.x ?? edgeData.target?.bounds?.cx() ?? 0;
            const targetY = edgeData.target?.y ?? edgeData.target?.bounds?.cy() ?? 0;
            
            console.log('[gridify] Fallback path for edge:', edgeData.id, 
              'from', edgeData.source?.id, '(', sourceX, ',', sourceY, ')',
              'to', edgeData.target?.id, '(', targetX, ',', targetY, ')');
            
            const dx = targetX - sourceX;
            const dy = targetY - sourceY;
            
            // Choose routing based on dominant direction
            if (Math.abs(dx) > Math.abs(dy)) {
              const midX = sourceX + dx / 2;
              return this.gridLineFunction([
                { x: sourceX, y: sourceY },
                { x: midX, y: sourceY },
                { x: midX, y: targetY },
                { x: targetX, y: targetY }
              ]);
            } else {
              const midY = sourceY + dy / 2;
              return this.gridLineFunction([
                { x: sourceX, y: sourceY },
                { x: sourceX, y: midY },
                { x: targetX, y: midY },
                { x: targetX, y: targetY }
              ]);
            }
          }
          const cornerradius = 5;
          const arrowwidth = 3;
          const arrowheight = 7;
          
          // Get the route path from GridRouter
          const p = cola.GridRouter.getRoutePath(route, cornerradius, arrowwidth, arrowheight);
          
          // Adjust the route to properly terminate at node boundaries for arrow positioning
          // The GridRouter path may extend beyond the visual node bounds
          const adjustedPath = this.adjustGridRouteForArrowPositioning(edgeData, p.routepath, route);
          return adjustedPath || p.routepath;
        });

      // NOTE: Do NOT update node positions here!
      // gridify() should only reroute edges, not move nodes.
      // Node positions are set by the layout algorithm (cola) and should not change when switching routing modes.
      // The edges use the same node.x/y coordinates as default mode, ensuring consistency.
      
      // NOTE: Do NOT update group or label positions either - keep them consistent with default mode.
      
      // Position link labels at route midpoints
      this.gridUpdateLinkLabels(edges, routesByEdgeId);

      this.fitViewportToContent();

      // Dispatch event that relations are available
      this.dispatchEvent(new Event('relationsAvailable', ));
  }

  /**
   * Fallback grid routing that uses simple orthogonal paths without the GridRouter.
   * This is used when GridRouter fails or when nodes have invalid positions.
   */
  private fallbackGridRouting(edges: any[]): void {
    const linkGroups = this.container.selectAll(".link-group").data(edges, (d: any) => d.id ?? d);

    linkGroups.select("path")
      .attr("d", (edgeData: any) => {
        // Handle self-loops specially
        if (edgeData.source?.id === edgeData.target?.id) {
          const route = this.createGridSelfLoopRoute(edgeData);
          return this.gridLineFunction(route);
        }
        
        // Create orthogonal fallback path
        const sourceX = edgeData.source?.x ?? edgeData.source?.bounds?.cx() ?? 0;
        const sourceY = edgeData.source?.y ?? edgeData.source?.bounds?.cy() ?? 0;
        const targetX = edgeData.target?.x ?? edgeData.target?.bounds?.cx() ?? 0;
        const targetY = edgeData.target?.y ?? edgeData.target?.bounds?.cy() ?? 0;
        
        const dx = targetX - sourceX;
        const dy = targetY - sourceY;
        
        // Choose routing based on dominant direction
        if (Math.abs(dx) > Math.abs(dy)) {
          const midX = sourceX + dx / 2;
          return this.gridLineFunction([
            { x: sourceX, y: sourceY },
            { x: midX, y: sourceY },
            { x: midX, y: targetY },
            { x: targetX, y: targetY }
          ]);
        } else {
          const midY = sourceY + dy / 2;
          return this.gridLineFunction([
            { x: sourceX, y: sourceY },
            { x: sourceX, y: midY },
            { x: targetX, y: midY },
            { x: targetX, y: targetY }
          ]);
        }
      });

    this.fitViewportToContent();
  }

  private gridUpdateLinkLabels(edges: any[], routesByEdgeId: Map<string, any>) {
    const linkGroups = this.container.selectAll(".link-group");

    linkGroups
      .filter((d: any) => !this.isAlignmentEdge(d))
      .select("text.linklabel")
      .attr("x", (edgeData: any) => {
        // Use the actual rendered path element to find the true midpoint
        const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${edgeData.id}"]`) as SVGPathElement;
        if (pathElement) {
          try {
            const pathLength = pathElement.getTotalLength();
            const midpoint = pathElement.getPointAtLength(pathLength / 2);
            return midpoint.x;
          } catch (e) {
            // Fallback if path is not yet rendered
          }
        }
        const midpoint = this.getGridRouteMidpoint(edgeData, routesByEdgeId);
        // Use node.x/y as primary source (same as default mode)
        return midpoint?.x ?? (edgeData.source?.x ?? edgeData.source?.bounds?.cx() ?? 0);
      })
      .attr("y", (edgeData: any) => {
        // Use the actual rendered path element to find the true midpoint
        const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${edgeData.id}"]`) as SVGPathElement;
        if (pathElement) {
          try {
            const pathLength = pathElement.getTotalLength();
            const midpoint = pathElement.getPointAtLength(pathLength / 2);
            return midpoint.y;
          } catch (e) {
            // Fallback if path is not yet rendered
          }
        }
        const midpoint = this.getGridRouteMidpoint(edgeData, routesByEdgeId);
        // Use node.x/y as primary source (same as default mode)
        return midpoint?.y ?? (edgeData.source?.y ?? edgeData.source?.bounds?.cy() ?? 0);
      })
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle");
  }

  private getGridRouteMidpoint(edgeData: any, routesByEdgeId: Map<string, any>) {
    const route = routesByEdgeId.get(edgeData.id);
    if (!route) {
      // Use node.x/y as primary source (same as default mode) with bounds as fallback
      const sourceX = edgeData.source?.x ?? edgeData.source?.bounds?.cx() ?? 0;
      const sourceY = edgeData.source?.y ?? edgeData.source?.bounds?.cy() ?? 0;
      const targetX = edgeData.target?.x ?? edgeData.target?.bounds?.cx() ?? 0;
      const targetY = edgeData.target?.y ?? edgeData.target?.bounds?.cy() ?? 0;
      return {
        x: (sourceX + targetX) / 2,
        y: (sourceY + targetY) / 2
      };
    }

    // Build array of all points in the route
    const points: Array<{ x: number; y: number }> = [];
    route.forEach((segment: any) => {
      if (points.length === 0 && segment.length > 0) {
        points.push(segment[0]);
      }
      if (segment.length > 1) {
        points.push(segment[1]);
      }
    });

    if (points.length < 2) {
      return null;
    }

    // Calculate total path length
    let totalLength = 0;
    const segmentLengths: number[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      const dx = points[i + 1].x - points[i].x;
      const dy = points[i + 1].y - points[i].y;
      const length = Math.sqrt(dx * dx + dy * dy);
      segmentLengths.push(length);
      totalLength += length;
    }

    // Find the segment containing the midpoint
    const targetLength = totalLength / 2;
    let accumulatedLength = 0;
    for (let i = 0; i < segmentLengths.length; i++) {
      const segmentLength = segmentLengths[i];
      if (accumulatedLength + segmentLength >= targetLength) {
        // Midpoint is in this segment
        const remainingLength = targetLength - accumulatedLength;
        const t = segmentLength > 0 ? remainingLength / segmentLength : 0;
        return {
          x: points[i].x + t * (points[i + 1].x - points[i].x),
          y: points[i].y + t * (points[i + 1].y - points[i].y)
        };
      }
      accumulatedLength += segmentLength;
    }

    // Fallback to geometric midpoint of all points
    const midIndex = Math.floor(points.length / 2);
    return points[midIndex];
  }

  private adjustGridRouteForEdge(edgeData: any, route: any[]) {
    if (!edgeData?.id?.startsWith('_g_')) {
      return route;
    }

    const points = this.gridRouteToPoints(route);
    if (points.length < 2) {
      return route;
    }

    const adjustedPoints = this.routeGroupEdge(edgeData, points);
    return this.pointsToGridRoute(adjustedPoints);
  }

  /**
   * Adjust grid route path for proper arrow positioning at node boundaries.
   * Ensures the path terminates at the node boundary rather than center.
   */
  private adjustGridRouteForArrowPositioning(edgeData: any, routePath: string, route: any[]): string | null {
    if (!routePath || !edgeData.source || !edgeData.target) {
      return null;
    }

    try {
      // Extract points from the route
      const points = this.gridRouteToPoints(route);
      if (points.length < 2) {
        return null;
      }

      const source = edgeData.source;
      const target = edgeData.target;

      // Use the *visible* rectangle (visualWidth/visualHeight for plain nodes,
      // inset bounds for groups) so the arrowhead lands on the rendered border
      // rather than on WebCola's inflated collision rectangle.
      const sourceBounds = this.getVisibleBounds(source) ?? source.bounds ?? {
        x: source.x - (source.width || 0) / 2,
        y: source.y - (source.height || 0) / 2,
        width: () => source.width || 0,
        height: () => source.height || 0
      };
      const targetBounds = this.getVisibleBounds(target) ?? target.bounds ?? {
        x: target.x - (target.width || 0) / 2,
        y: target.y - (target.height || 0) / 2,
        width: () => target.width || 0,
        height: () => target.height || 0
      };

      // Check if nodes are near-touching and determine which direction
      const NEAR_TOUCH_THRESHOLD = 5;
      const touchDirection = this.getTouchDirection(sourceBounds, targetBounds, NEAR_TOUCH_THRESHOLD);

      if (touchDirection !== 'none') {
        // Nodes are near-touching, route edge from perpendicular sides
        const { sourcePoint, targetPoint, middlePoints } = this.computePerpendicularRoute(
          sourceBounds, targetBounds, touchDirection
        );
        
        // Build new path: source anchor -> middle waypoints -> target anchor
        const newPoints = [sourcePoint, ...middlePoints, targetPoint];
        return this.gridLineFunction(newPoints);
      }

      // Standard case: clip endpoints to the visible boundary while preserving
      // orthogonality of the entry/exit segments. GridRouter outputs axis-aligned
      // segments, so we just snap route[0]/route[last] along the egress axis to
      // the visible side. Falls back to a center-line rectangle intersection if
      // the original segment isn't orthogonal (defensive — shouldn't happen).
      points[0] = this.clipEndpointToVisibleBoundary(points[0], points[1], sourceBounds);
      points[points.length - 1] = this.clipEndpointToVisibleBoundary(
        points[points.length - 1],
        points[points.length - 2],
        targetBounds
      );

      // Distribute parallel edges across distinct ports on the visible boundary
      // and re-orthogonalize the entry/exit segments. Must run AFTER rectangle
      // clipping so the port shift isn't overwritten.
      const portAdjusted = this.applyPortBasedEndpointsOrthogonal(edgeData, points);

      // Convert points back to SVG path using grid line function for sharp turns
      return this.gridLineFunction(portAdjusted);
    } catch (error) {
      console.warn('Error adjusting grid route for arrow positioning:', error);
      return null;
    }
  }

  /**
   * Snap an endpoint of an orthogonal route to the visible-boundary side it
   * already exits, preserving axis alignment with the neighboring waypoint.
   *
   * For a route like [(245, 92), (245, -120), …], the first segment is
   * vertical (same x), so route[0] is on the top or bottom of source. We pick
   * whichever side route[0] is closer to and snap that coordinate to the
   * visible boundary while keeping the perpendicular coordinate intact —
   * preserving orthogonality automatically.
   *
   * Falls back to a center-line rectangle intersection if the original
   * endpoint→neighbor segment isn't axis-aligned.
   */
  private clipEndpointToVisibleBoundary(
    endpoint: { x: number; y: number },
    neighbor: { x: number; y: number },
    bounds: any
  ): { x: number; y: number } {
    const cx = bounds.x + bounds.width() / 2;
    const cy = bounds.y + bounds.height() / 2;
    const bx = bounds.x;
    const bX = typeof bounds.X === 'number' ? bounds.X : bounds.x + bounds.width();
    const by = bounds.y;
    const bY = typeof bounds.Y === 'number' ? bounds.Y : bounds.y + bounds.height();
    const eps = 0.5;

    const dx = neighbor.x - endpoint.x;
    const dy = neighbor.y - endpoint.y;

    if (Math.abs(dx) < eps && Math.abs(dy) >= eps) {
      // Vertical segment — endpoint is on top or bottom.
      const targetY = endpoint.y > cy ? bY : by;
      return { x: endpoint.x, y: targetY };
    }
    if (Math.abs(dy) < eps && Math.abs(dx) >= eps) {
      // Horizontal segment — endpoint is on left or right.
      const targetX = endpoint.x > cx ? bX : bx;
      return { x: targetX, y: endpoint.y };
    }

    // Fallback: defensive rectangle intersection from center to neighbor.
    const intersection = this.getRectangleIntersection(cx, cy, neighbor.x, neighbor.y, bounds);
    return intersection ?? endpoint;
  }

  /**
   * Determines which direction nodes are touching/near.
   * Returns 'horizontal' if touching left-right, 'vertical' if touching top-bottom, 'none' otherwise.
   */
  private getTouchDirection(a: any, b: any, threshold: number): 'horizontal' | 'vertical' | 'none' {
    const aLeft = a.x;
    const aRight = a.x + a.width();
    const aTop = a.y;
    const aBottom = a.y + a.height();

    const bLeft = b.x;
    const bRight = b.x + b.width();
    const bTop = b.y;
    const bBottom = b.y + b.height();

    // Check horizontal gap (left-right touching)
    const xGap = Math.max(0, Math.max(bLeft - aRight, aLeft - bRight));
    // Check vertical gap (top-bottom touching)
    const yGap = Math.max(0, Math.max(bTop - aBottom, aTop - bBottom));

    // Check if there's vertical overlap (needed for horizontal adjacency)
    const verticalOverlap = !(aBottom < bTop || bBottom < aTop);
    // Check if there's horizontal overlap (needed for vertical adjacency)
    const horizontalOverlap = !(aRight < bLeft || bRight < aLeft);

    // Nodes are horizontally adjacent (touching left-right) if:
    // - Small horizontal gap AND vertical overlap
    if (xGap <= threshold && verticalOverlap) {
      return 'horizontal';
    }

    // Nodes are vertically adjacent (touching top-bottom) if:
    // - Small vertical gap AND horizontal overlap
    if (yGap <= threshold && horizontalOverlap) {
      return 'vertical';
    }

    return 'none';
  }

  /**
   * Computes anchor points and waypoints for edges between near-touching nodes.
   * Routes the edge from perpendicular sides to avoid being hidden.
   */
  private computePerpendicularRoute(
    sourceBounds: any,
    targetBounds: any,
    touchDirection: 'horizontal' | 'vertical'
  ): { sourcePoint: { x: number; y: number }; targetPoint: { x: number; y: number }; middlePoints: Array<{ x: number; y: number }> } {
    const sw = sourceBounds.width();
    const sh = sourceBounds.height();
    const tw = targetBounds.width();
    const th = targetBounds.height();

    const sCenterX = sourceBounds.x + sw / 2;
    const sCenterY = sourceBounds.y + sh / 2;
    const tCenterX = targetBounds.x + tw / 2;
    const tCenterY = targetBounds.y + th / 2;

    // Offset for the routing bend (go around the touching area)
    const ROUTE_OFFSET = 15;

    if (touchDirection === 'horizontal') {
      // Nodes are side-by-side (left-right), route from top or bottom
      // Determine whether to go above or below based on available space
      const goTop = sCenterY <= tCenterY;
      
      if (goTop) {
        // Route above both nodes
        const routeY = Math.min(sourceBounds.y, targetBounds.y) - ROUTE_OFFSET;
        return {
          sourcePoint: { x: sCenterX, y: sourceBounds.y }, // top of source
          targetPoint: { x: tCenterX, y: targetBounds.y }, // top of target
          middlePoints: [
            { x: sCenterX, y: routeY },
            { x: tCenterX, y: routeY }
          ]
        };
      } else {
        // Route below both nodes
        const routeY = Math.max(sourceBounds.y + sh, targetBounds.y + th) + ROUTE_OFFSET;
        return {
          sourcePoint: { x: sCenterX, y: sourceBounds.y + sh }, // bottom of source
          targetPoint: { x: tCenterX, y: targetBounds.y + th }, // bottom of target
          middlePoints: [
            { x: sCenterX, y: routeY },
            { x: tCenterX, y: routeY }
          ]
        };
      }
    } else {
      // Nodes are stacked (top-bottom), route from left or right
      // Determine whether to go left or right based on available space
      const goLeft = sCenterX <= tCenterX;

      if (goLeft) {
        // Route to the left of both nodes
        const routeX = Math.min(sourceBounds.x, targetBounds.x) - ROUTE_OFFSET;
        return {
          sourcePoint: { x: sourceBounds.x, y: sCenterY }, // left of source
          targetPoint: { x: targetBounds.x, y: tCenterY }, // left of target
          middlePoints: [
            { x: routeX, y: sCenterY },
            { x: routeX, y: tCenterY }
          ]
        };
      } else {
        // Route to the right of both nodes
        const routeX = Math.max(sourceBounds.x + sw, targetBounds.x + tw) + ROUTE_OFFSET;
        return {
          sourcePoint: { x: sourceBounds.x + sw, y: sCenterY }, // right of source
          targetPoint: { x: targetBounds.x + tw, y: tCenterY }, // right of target
          middlePoints: [
            { x: routeX, y: sCenterY },
            { x: routeX, y: tCenterY }
          ]
        };
      }
    }
  }

  /**
   * Returns true if two axis-aligned rectangles are within `threshold` pixels of each other.
   */
  private areBoundsNear(a: any, b: any, threshold: number): boolean {
    const aLeft = a.x;
    const aRight = a.x + a.width();
    const aTop = a.y;
    const aBottom = a.y + a.height();

    const bLeft = b.x;
    const bRight = b.x + b.width();
    const bTop = b.y;
    const bBottom = b.y + b.height();

    const xGap = Math.max(0, Math.max(bLeft - aRight, aLeft - bRight));
    const yGap = Math.max(0, Math.max(bTop - aBottom, aTop - bBottom));

    const dist = Math.sqrt(xGap * xGap + yGap * yGap);
    return dist <= threshold;
  }

  /**
   * Chooses a boundary point (midpoint of left/right/top/bottom) on `bounds` that is farthest from `other` point.
   */
  private chooseBoundaryPoint(cx: number, cy: number, bounds: any, other: { x: number; y: number }): { x: number; y: number } {
    const w = Math.max(1, bounds.width());
    const h = Math.max(1, bounds.height());
    const candidates = [
      { x: bounds.x, y: bounds.y + h / 2 }, // left
      { x: bounds.x + w, y: bounds.y + h / 2 }, // right
      { x: bounds.x + w / 2, y: bounds.y }, // top
      { x: bounds.x + w / 2, y: bounds.y + h } // bottom
    ];

    let best = candidates[0];
    let bestDist = -Infinity;
    for (const c of candidates) {
      const dx = c.x - other.x;
      const dy = c.y - other.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d > bestDist) {
        bestDist = d;
        best = c;
      }
    }
    return best;
  }

  /**
   * Normalizes node bounds to a consistent format with x, y and width/height as functions.
   */
  private normalizeNodeBounds(node: any): { x: number; y: number; width: () => number; height: () => number } {
    // Use visual dimensions for rendering bounds (not the inflated collision width/height)
    const vw = node.visualWidth ?? node.width ?? 50;
    const vh = node.visualHeight ?? node.height ?? 30;
    const bounds = node.bounds || {
      x: node.x - vw / 2,
      y: node.y - vh / 2,
      width: () => vw,
      height: () => vh
    };
    
    return {
      x: typeof bounds.x === 'number' ? bounds.x : (bounds.X !== undefined ? bounds.x : node.x - vw / 2),
      y: typeof bounds.y === 'number' ? bounds.y : node.y - vh / 2,
      width: () => typeof bounds.width === 'function' ? bounds.width() : (bounds.X !== undefined ? bounds.X - bounds.x : vw),
      height: () => typeof bounds.height === 'function' ? bounds.height() : (bounds.Y !== undefined ? bounds.Y - bounds.y : vh)
    };
  }

  /**
   * Checks if a line segment from p1 to p2 intersects with a rectangle.
   * Used to detect if an edge would pass through an intermediate node.
   */
  private lineIntersectsRect(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    rect: { x: number; y: number; width: () => number; height: () => number }
  ): boolean {
    const left = rect.x;
    const right = rect.x + rect.width();
    const top = rect.y;
    const bottom = rect.y + rect.height();

    // Check if line segment from p1 to p2 intersects the rectangle
    // Using Cohen-Sutherland style approach

    // First, check if the segment's bounding box doesn't intersect rect at all
    const minX = Math.min(p1.x, p2.x);
    const maxX = Math.max(p1.x, p2.x);
    const minY = Math.min(p1.y, p2.y);
    const maxY = Math.max(p1.y, p2.y);

    if (maxX < left || minX > right || maxY < top || minY > bottom) {
      return false;
    }

    // Check if either endpoint is inside the rectangle
    const p1Inside = p1.x >= left && p1.x <= right && p1.y >= top && p1.y <= bottom;
    const p2Inside = p2.x >= left && p2.x <= right && p2.y >= top && p2.y <= bottom;
    if (p1Inside || p2Inside) {
      return true;
    }

    // Check intersection with each edge of the rectangle
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    // Helper to check line-segment intersection
    const intersectsHorizontal = (y: number, xMin: number, xMax: number): boolean => {
      if (dy === 0) return false;
      const t = (y - p1.y) / dy;
      if (t < 0 || t > 1) return false;
      const x = p1.x + t * dx;
      return x >= xMin && x <= xMax;
    };

    const intersectsVertical = (x: number, yMin: number, yMax: number): boolean => {
      if (dx === 0) return false;
      const t = (x - p1.x) / dx;
      if (t < 0 || t > 1) return false;
      const y = p1.y + t * dy;
      return y >= yMin && y <= yMax;
    };

    return (
      intersectsHorizontal(top, left, right) ||
      intersectsHorizontal(bottom, left, right) ||
      intersectsVertical(left, top, bottom) ||
      intersectsVertical(right, top, bottom)
    );
  }

  /**
   * Finds all nodes that lie between source and target and would block a direct edge.
   * Returns them sorted by position along the source→target axis.
   */
  private findBlockingNodes(
    source: any,
    target: any,
    sourceId: string,
    targetId: string
  ): Array<{ node: any; bounds: { x: number; y: number; width: () => number; height: () => number } }> {
    if (!this.currentLayout?.nodes) {
      return [];
    }

    const sourceBounds = this.normalizeNodeBounds(source);
    const targetBounds = this.normalizeNodeBounds(target);

    // Compute centers
    const sourceCenter = {
      x: sourceBounds.x + sourceBounds.width() / 2,
      y: sourceBounds.y + sourceBounds.height() / 2
    };
    const targetCenter = {
      x: targetBounds.x + targetBounds.width() / 2,
      y: targetBounds.y + targetBounds.height() / 2
    };

    const blocking: Array<{ node: any; bounds: any; distance: number }> = [];

    for (const node of this.currentLayout.nodes) {
      // Skip source and target
      if (node.id === sourceId || node.id === targetId) {
        continue;
      }

      const nodeBounds = this.normalizeNodeBounds(node);

      // Check if a straight line from source center to target center would pass through this node
      if (this.lineIntersectsRect(sourceCenter, targetCenter, nodeBounds)) {
        // Calculate distance from source for sorting
        const nodeCenter = {
          x: nodeBounds.x + nodeBounds.width() / 2,
          y: nodeBounds.y + nodeBounds.height() / 2
        };
        const distance = Math.sqrt(
          Math.pow(nodeCenter.x - sourceCenter.x, 2) +
          Math.pow(nodeCenter.y - sourceCenter.y, 2)
        );
        blocking.push({ node, bounds: nodeBounds, distance });
      }
    }

    // Sort by distance from source
    blocking.sort((a, b) => a.distance - b.distance);
    return blocking.map(b => ({ node: b.node, bounds: b.bounds }));
  }

  /**
   * Computes a route that goes around blocking intermediate nodes.
   * Used when there are nodes between source and target that would hide the edge.
   */
  private computeRouteAroundBlockingNodes(
    sourceBounds: { x: number; y: number; width: () => number; height: () => number },
    targetBounds: { x: number; y: number; width: () => number; height: () => number },
    blockingNodes: Array<{ node: any; bounds: { x: number; y: number; width: () => number; height: () => number } }>
  ): { sourcePoint: { x: number; y: number }; targetPoint: { x: number; y: number }; middlePoints: Array<{ x: number; y: number }> } {
    const ROUTE_OFFSET = 15;

    // Compute the bounding box that contains source, target, and all blocking nodes
    let minX = Math.min(sourceBounds.x, targetBounds.x);
    let maxX = Math.max(sourceBounds.x + sourceBounds.width(), targetBounds.x + targetBounds.width());
    let minY = Math.min(sourceBounds.y, targetBounds.y);
    let maxY = Math.max(sourceBounds.y + sourceBounds.height(), targetBounds.y + targetBounds.height());

    for (const { bounds } of blockingNodes) {
      minX = Math.min(minX, bounds.x);
      maxX = Math.max(maxX, bounds.x + bounds.width());
      minY = Math.min(minY, bounds.y);
      maxY = Math.max(maxY, bounds.y + bounds.height());
    }

    const sCenterX = sourceBounds.x + sourceBounds.width() / 2;
    const sCenterY = sourceBounds.y + sourceBounds.height() / 2;
    const tCenterX = targetBounds.x + targetBounds.width() / 2;
    const tCenterY = targetBounds.y + targetBounds.height() / 2;

    // Determine if nodes are primarily stacked vertically or horizontally
    const dx = Math.abs(tCenterX - sCenterX);
    const dy = Math.abs(tCenterY - sCenterY);

    if (dy > dx) {
      // Primarily vertical arrangement - route to the left or right
      const goLeft = sCenterX <= tCenterX;

      if (goLeft) {
        const routeX = minX - ROUTE_OFFSET;
        return {
          sourcePoint: { x: sourceBounds.x, y: sCenterY },
          targetPoint: { x: targetBounds.x, y: tCenterY },
          middlePoints: [
            { x: routeX, y: sCenterY },
            { x: routeX, y: tCenterY }
          ]
        };
      } else {
        const routeX = maxX + ROUTE_OFFSET;
        return {
          sourcePoint: { x: sourceBounds.x + sourceBounds.width(), y: sCenterY },
          targetPoint: { x: targetBounds.x + targetBounds.width(), y: tCenterY },
          middlePoints: [
            { x: routeX, y: sCenterY },
            { x: routeX, y: tCenterY }
          ]
        };
      }
    } else {
      // Primarily horizontal arrangement - route above or below
      const goTop = sCenterY <= tCenterY;

      if (goTop) {
        const routeY = minY - ROUTE_OFFSET;
        return {
          sourcePoint: { x: sCenterX, y: sourceBounds.y },
          targetPoint: { x: tCenterX, y: targetBounds.y },
          middlePoints: [
            { x: sCenterX, y: routeY },
            { x: tCenterX, y: routeY }
          ]
        };
      } else {
        const routeY = maxY + ROUTE_OFFSET;
        return {
          sourcePoint: { x: sCenterX, y: sourceBounds.y + sourceBounds.height() },
          targetPoint: { x: tCenterX, y: targetBounds.y + targetBounds.height() },
          middlePoints: [
            { x: sCenterX, y: routeY },
            { x: tCenterX, y: routeY }
          ]
        };
      }
    }
  }

  /**
   * Checks if two nodes are near-touching (directly or transitively via intermediate nodes)
   * and returns a perpendicular route if so.
   * Used by both default and grid routing modes to ensure edges are visible
   * when nodes are close together or touching, or when intermediate nodes would block the edge.
   * 
   * @param edgeData - The edge data with source and target nodes
   * @returns Array of route points if rerouting needed, null otherwise
   */
  private getNearTouchPerpendicularRoute(edgeData: any): Array<{ x: number; y: number }> | null {
    if (!edgeData.source || !edgeData.target) {
      return null;
    }

    // Skip self-loops - they're handled separately
    if (edgeData.source.id === edgeData.target.id) {
      return null;
    }

    const source = edgeData.source;
    const target = edgeData.target;

    const normSourceBounds = this.normalizeNodeBounds(source);
    const normTargetBounds = this.normalizeNodeBounds(target);

    const NEAR_TOUCH_THRESHOLD = 5;
    const touchDirection = this.getTouchDirection(normSourceBounds, normTargetBounds, NEAR_TOUCH_THRESHOLD);

    // Case 1: Direct near-touching - use existing logic
    if (touchDirection !== 'none') {
      const { sourcePoint, targetPoint, middlePoints } = this.computePerpendicularRoute(
        normSourceBounds, normTargetBounds, touchDirection
      );
      return [sourcePoint, ...middlePoints, targetPoint];
    }

    // Case 2: Check for intermediate blocking nodes (transitive touching)
    const blockingNodes = this.findBlockingNodes(source, target, source.id, target.id);

    if (blockingNodes.length > 0) {
      // Route around the blocking nodes
      const { sourcePoint, targetPoint, middlePoints } = this.computeRouteAroundBlockingNodes(
        normSourceBounds, normTargetBounds, blockingNodes
      );
      return [sourcePoint, ...middlePoints, targetPoint];
    }

    return null;
  }

  private gridRouteToPoints(route: any[]) {
    const points: Array<{ x: number; y: number }> = [];
    route.forEach((segment: any, index: number) => {
      if (index === 0) {
        points.push({ x: segment[0].x, y: segment[0].y });
      }
      points.push({ x: segment[1].x, y: segment[1].y });
    });
    return points;
  }

  private pointsToGridRoute(points: Array<{ x: number; y: number }>) {
    const segments: Array<Array<{ x: number; y: number }>> = [];
    for (let i = 0; i < points.length - 1; i += 1) {
      segments.push([points[i], points[i + 1]]);
    }
    return segments;
  }

  /**
   * Removes redundant bends from an orthogonal grid route (Tier 1.4).
   *
   * Two passes:
   *   1. Collinear merge — drop interior points that are collinear with their
   *      immediate neighbors. Always safe (no geometry change), no collision
   *      check needed.
   *   2. Z-shape flattening — for each 4-point Z (A→B→C→D where AB and CD are
   *      parallel and offset), try replacing B and C with a single corner M at
   *      either (A.x, D.y) or (D.x, A.y). Accept whichever produces an
   *      orthogonal L that doesn't pass through any non-endpoint node bounds.
   *      Each successful replacement removes one bend.
   *
   * Performance guardrails:
   *   - Caller already gates on edge count ≤ CROSSING_OPTIMIZATION_EDGE_THRESHOLD.
   *   - Z-shape pass is capped at MAX_FLATTEN_ATTEMPTS successful flattens per
   *     edge — diminishing returns past 2.
   *   - Returns the original route unchanged on any error (defensive).
   *
   * @param route - Cola GridRouter output (array of [start, end] segments)
   * @param nodes - All current layout nodes (for collision checks)
   * @param edge - The edge being flattened (its endpoints are excluded from collision checks)
   */
  private flattenGridRouteBends(route: any[], nodes: any[], edge: any): any[] {
    try {
      if (!route || route.length < 2) return route;

      const original = this.gridRouteToPoints(route);
      if (original.length < 3) return route;

      // Iterate (collinear-merge → Z-flatten → U-flatten) until no pass
      // changes the route. Each flatten can expose new collinear points or
      // new flatten targets, so a single pass leaves easy wins on the table.
      // Bounded by a hard MAX_ITERATIONS so a pathological route can't loop.
      const MAX_ITERATIONS = 6;
      let points = original;
      for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
        const before = points.length;
        points = this.dropCollinearGridPoints(points);
        points = this.flattenGridRouteZShapes(points, nodes, edge);
        points = this.flattenGridRouteUBumps(points, nodes, edge);
        if (points.length === before) break; // converged
      }

      // No changes? Return original route to avoid pointless allocation churn.
      if (points.length === original.length) return route;

      return this.pointsToGridRoute(points);
    } catch (e) {
      console.warn('[flattenGridRouteBends] Failed; returning original route:', e);
      return route;
    }
  }

  /**
   * Drops interior points that are collinear with their immediate neighbors.
   * For an orthogonal route, "collinear" means three consecutive points share
   * the same X (vertical line) or the same Y (horizontal line). Always safe.
   */
  private dropCollinearGridPoints(
    points: Array<{ x: number; y: number }>
  ): Array<{ x: number; y: number }> {
    if (points.length < 3) return points;
    const result: Array<{ x: number; y: number }> = [points[0]];
    for (let i = 1; i < points.length - 1; i++) {
      const prev = result[result.length - 1];
      const cur = points[i];
      const next = points[i + 1];
      const collinearX = prev.x === cur.x && cur.x === next.x;
      const collinearY = prev.y === cur.y && cur.y === next.y;
      if (collinearX || collinearY) continue; // cur is redundant
      result.push(cur);
    }
    result.push(points[points.length - 1]);
    return result;
  }

  /**
   * Replaces 4-point Z-shapes with 2-point L-shapes when the alternate corner
   * is collision-free. See flattenGridRouteBends for the surrounding context.
   * Cap is per-call; the caller iterates this with the collinear pass until
   * stable, so the per-call cap mainly bounds worst-case work on a single
   * pathological route.
   */
  private flattenGridRouteZShapes(
    points: Array<{ x: number; y: number }>,
    nodes: any[],
    edge: any
  ): Array<{ x: number; y: number }> {
    const MAX_FLATTEN_ATTEMPTS = 16;
    const NODE_COLLISION_MARGIN = 2; // px

    // Endpoints belong to the edge's source/target; excluded from collision checks.
    const excludeIds = new Set<string>();
    if (edge?.source?.id) excludeIds.add(edge.source.id);
    if (edge?.target?.id) excludeIds.add(edge.target.id);

    const result = points.slice();
    let flattens = 0;
    let i = 0;
    while (i + 3 < result.length && flattens < MAX_FLATTEN_ATTEMPTS) {
      const a = result[i];
      const b = result[i + 1];
      const c = result[i + 2];
      const d = result[i + 3];

      // Z-shape requires AB and CD to be parallel-and-offset (segments alternate
      // orthogonal axes). After collinear merge, axes alternate by construction,
      // so the structural test is "AB parallel to CD" → A.y === B.y matches C.y === D.y.
      const abHorizontal = a.y === b.y;
      const cdHorizontal = c.y === d.y;
      const isZ = abHorizontal === cdHorizontal && (
        abHorizontal ? a.y !== d.y : a.x !== d.x
      );
      if (!isZ) {
        i++;
        continue;
      }

      // Try both alternate corners for the replacement L.
      const candidates: Array<{ x: number; y: number }> = [
        { x: a.x, y: d.y },
        { x: d.x, y: a.y },
      ];
      let replaced = false;
      for (const m of candidates) {
        if (
          this.isOrthogonalSegmentClearOfNodes(a, m, nodes, excludeIds, NODE_COLLISION_MARGIN) &&
          this.isOrthogonalSegmentClearOfNodes(m, d, nodes, excludeIds, NODE_COLLISION_MARGIN)
        ) {
          result.splice(i + 1, 2, m); // Replace b, c with m → removes one bend.
          flattens++;
          replaced = true;
          break;
        }
      }
      if (!replaced) i++;
      // If replaced, don't advance i — re-check from the same position in case
      // the new L can be flattened further with the next neighbor.
    }
    return result;
  }

  /**
   * Flattens 5-point "U-bump" patterns A→B→C→D→E where A and E are collinear
   * (same X or same Y, i.e. the bump is a detour off a straight line) and the
   * direct A→E segment is clear of non-endpoint nodes. Replaces B,C,D with
   * nothing — removes two bends in one shot.
   *
   * Companion to flattenGridRouteZShapes: Z-flatten removes one bend per
   * 4-point window; U-flatten removes two when the bump was just an avoidable
   * detour. Common after Z-flatten exposes a collinear endpoint pair.
   */
  private flattenGridRouteUBumps(
    points: Array<{ x: number; y: number }>,
    nodes: any[],
    edge: any
  ): Array<{ x: number; y: number }> {
    if (points.length < 5) return points;
    const MAX_FLATTEN_ATTEMPTS = 16;
    const NODE_COLLISION_MARGIN = 2;

    const excludeIds = new Set<string>();
    if (edge?.source?.id) excludeIds.add(edge.source.id);
    if (edge?.target?.id) excludeIds.add(edge.target.id);

    const result = points.slice();
    let flattens = 0;
    let i = 0;
    while (i + 4 < result.length && flattens < MAX_FLATTEN_ATTEMPTS) {
      const a = result[i];
      const e = result[i + 4];

      // U-bump: A and E share an axis (so A→E is itself orthogonal).
      const sameX = Math.abs(a.x - e.x) < 0.001;
      const sameY = Math.abs(a.y - e.y) < 0.001;
      if (!(sameX || sameY)) {
        i++;
        continue;
      }
      // Reject degenerate case: A == E.
      if (sameX && sameY) {
        i++;
        continue;
      }

      if (this.isOrthogonalSegmentClearOfNodes(a, e, nodes, excludeIds, NODE_COLLISION_MARGIN)) {
        result.splice(i + 1, 3); // drop B, C, D
        flattens++;
        // Don't advance — the new A→E neighborhood may itself participate in
        // another U-bump or Z with the next point.
      } else {
        i++;
      }
    }
    return result;
  }

  /**
   * Returns true if an axis-aligned segment from `a` to `b` doesn't pass through
   * any node's bounds (with a small margin), excluding nodes whose ids are in
   * `excludeIds` (typically the segment's edge endpoints).
   *
   * Returns false on non-axis-aligned input — flattenGridRouteZShapes only ever
   * produces axis-aligned segments, so this is a defensive guard.
   */
  private isOrthogonalSegmentClearOfNodes(
    a: { x: number; y: number },
    b: { x: number; y: number },
    nodes: any[],
    excludeIds: Set<string>,
    margin: number
  ): boolean {
    const isHorizontal = a.y === b.y;
    const isVertical = a.x === b.x;
    if (!isHorizontal && !isVertical) return false;

    for (const node of nodes) {
      if (excludeIds.has(node.id)) continue;
      const bounds = node.bounds || node.innerBounds;
      if (!bounds) continue;

      const widthFn = typeof bounds.width === 'function' ? bounds.width() : 0;
      const heightFn = typeof bounds.height === 'function' ? bounds.height() : 0;
      const nx = (typeof bounds.x === 'number' ? bounds.x : 0) - margin;
      const nX = (bounds.X !== undefined ? bounds.X : (bounds.x || 0) + widthFn) + margin;
      const ny = (typeof bounds.y === 'number' ? bounds.y : 0) - margin;
      const nY = (bounds.Y !== undefined ? bounds.Y : (bounds.y || 0) + heightFn) + margin;

      if (isHorizontal) {
        if (a.y < ny || a.y > nY) continue; // Outside node's y-range
        const xMin = Math.min(a.x, b.x);
        const xMax = Math.max(a.x, b.x);
        if (xMax < nx || xMin > nX) continue; // No x-overlap
        return false; // Segment passes through this node
      } else {
        if (a.x < nx || a.x > nX) continue;
        const yMin = Math.min(a.y, b.y);
        const yMax = Math.max(a.y, b.y);
        if (yMax < ny || yMin > nY) continue;
        return false;
      }
    }
    return true;
  }

  private createFallbackBounds(node: any) {
    if (!cola?.Rectangle) {
      return null;
    }
    const halfWidth = ((node.visualWidth ?? node.width) || 50) / 2;
    const halfHeight = ((node.visualHeight ?? node.height) || 30) / 2;
    const x = (node.x || 0) - halfWidth;
    const X = (node.x || 0) + halfWidth;
    const y = (node.y || 0) - halfHeight;
    const Y = (node.y || 0) + halfHeight;
    return new cola.Rectangle(x, X, y, Y);
  }

  /**
   * Calculate the intersection point between a line and a rectangle.
   * Used for positioning arrow heads at node boundaries in grid mode.
   * 
   * @param x1 - Start x coordinate (usually center of node)
   * @param y1 - Start y coordinate (usually center of node)
   * @param x2 - End x coordinate (next point in path)
   * @param y2 - End y coordinate (next point in path)
   * @param bounds - Rectangle bounds with x, y, width(), height()
   * @returns Intersection point or null if no intersection
   */
  private getRectangleIntersection(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    bounds: any
  ): { x: number; y: number } | null {
    // Get rectangle boundaries
    const rectLeft = bounds.x;
    const rectRight = bounds.x + bounds.width();
    const rectTop = bounds.y;
    const rectBottom = bounds.y + bounds.height();

    // Calculate line direction
    const dx = x2 - x1;
    const dy = y2 - y1;

    // If line has no direction, return center point
    if (dx === 0 && dy === 0) {
      return { x: x1, y: y1 };
    }

    // Find intersection with each rectangle edge
    let tMin = 0;
    let tMax = 1;

    // Check intersection with vertical edges (left and right)
    if (dx !== 0) {
      const t1 = (rectLeft - x1) / dx;
      const t2 = (rectRight - x1) / dx;
      tMin = Math.max(tMin, Math.min(t1, t2));
      tMax = Math.min(tMax, Math.max(t1, t2));
    }

    // Check intersection with horizontal edges (top and bottom)
    if (dy !== 0) {
      const t1 = (rectTop - y1) / dy;
      const t2 = (rectBottom - y1) / dy;
      tMin = Math.max(tMin, Math.min(t1, t2));
      tMax = Math.min(tMax, Math.max(t1, t2));
    }

    // If tMin > tMax, there's no intersection
    if (tMin > tMax) {
      return null;
    }

    // Return the intersection point at the rectangle boundary
    // Use tMin if starting inside, tMax if starting outside
    const t = tMin > 0 ? tMin : tMax;
    return {
      x: x1 + t * dx,
      y: y1 + t * dy
    };
  }

  /**
   * Stage 1: Compute route arrays for all edges and store in computedRoutes map.
   * This enables post-routing optimization before SVG rendering.
   */
  private computeAllRoutes(): void {
    this.computedRoutes.clear();

    this.container.selectAll('.link-group path').each((d: any) => {
      try {
        const route = this.computeSingleRoute(d);
        if (route) {
          this.computedRoutes.set(d.id, route);
        }
      } catch (error) {
        console.error(`Error routing edge ${d.id} from ${d.source.id} to ${d.target.id}:`, error);
        this.showRuntimeAlert(d.source.id, d.target.id);
        // Fallback to simple line
        this.computedRoutes.set(d.id, [
          { x: d.source.x || 0, y: d.source.y || 0 },
          { x: d.target.x || 0, y: d.target.y || 0 }
        ]);
      }
    });
  }

  /**
   * Stage 2: Apply computed routes to SVG path elements.
   */
  private applyRoutesToSVG(): void {
    this.container.selectAll('.link-group path')
      .attr('d', (d: any) => {
        const route = this.computedRoutes.get(d.id);
        if (!route) return null;
        return this.lineFunction(this.addTangentGuides(route));
      });
  }

  /**
   * Inserts "tangent guide" points near each endpoint of a multi-point route
   * to force the d3.curveBasis B-spline tangent to align with the actual
   * final/first segment direction. Without these, interior control points
   * can pull the endpoint tangent off-axis, causing arrowhead markers
   * (orient="auto") to render at odd angles.
   *
   * Strategy: insert TWO co-linear guides (far + near) at each endpoint so
   * the spline's last 2–3 control points all lie on the desired tangent
   * line. The single-guide approach left enough slack for interior bends to
   * still skew the endpoint tangent; two guides clamp it much harder.
   *
   * For 2-point routes (straight lines), the tangent is already correct
   * so no guides are added.
   */
  private addTangentGuides(
    route: Array<{ x: number; y: number }>
  ): Array<{ x: number; y: number }> {
    if (route.length <= 2) return route;

    // Far guide steers the cubic block's far control; near guide locks the
    // immediate tangent. Both must be collinear with the final segment.
    const FAR_MAX = 8;   // px
    const NEAR_MAX = 2;  // px
    const result = [...route];

    // ── End: build guides along the final segment direction ──
    const last = result.length - 1;
    const dxE = result[last].x - result[last - 1].x;
    const dyE = result[last].y - result[last - 1].y;
    const lenE = Math.sqrt(dxE * dxE + dyE * dyE);
    if (lenE > 0.5) {
      const ux = dxE / lenE;
      const uy = dyE / lenE;
      // Cap each guide at a fraction of the segment so very short final
      // segments still get guides (the legacy 6px cutoff dropped them and
      // those are exactly the cases where the arrow looked worst).
      const farDist = Math.min(FAR_MAX, lenE * 0.5);
      const nearDist = Math.min(NEAR_MAX, lenE * 0.2);
      if (farDist > nearDist + 0.1) {
        // Order: ..., farGuide, nearGuide, last
        result.splice(last, 0,
          { x: result[last].x - ux * farDist, y: result[last].y - uy * farDist },
          { x: result[last].x - ux * nearDist, y: result[last].y - uy * nearDist }
        );
      } else {
        // Segment too short for two distinct guides — emit just the near one.
        result.splice(last, 0,
          { x: result[last].x - ux * nearDist, y: result[last].y - uy * nearDist }
        );
      }
    }

    // ── Start: build guides along the first segment direction ──
    const dx0 = result[1].x - result[0].x;
    const dy0 = result[1].y - result[0].y;
    const len0 = Math.sqrt(dx0 * dx0 + dy0 * dy0);
    if (len0 > 0.5) {
      const ux = dx0 / len0;
      const uy = dy0 / len0;
      const farDist = Math.min(FAR_MAX, len0 * 0.5);
      const nearDist = Math.min(NEAR_MAX, len0 * 0.2);
      if (farDist > nearDist + 0.1) {
        // Order: 0, nearGuide, farGuide, ...
        result.splice(1, 0,
          { x: result[0].x + ux * nearDist, y: result[0].y + uy * nearDist },
          { x: result[0].x + ux * farDist, y: result[0].y + uy * farDist }
        );
      } else {
        result.splice(1, 0,
          { x: result[0].x + ux * nearDist, y: result[0].y + uy * nearDist }
        );
      }
    }

    return result;
  }

  /**
   * Direct-line fast path. Returns a 2-point perimeter-to-perimeter route iff:
   *   (a) the edge has no parallel siblings between the same pair (multi-edge
   *       logic owns those), and
   *   (b) source and target are not near-touching (perpendicular-route logic
   *       owns those), and
   *   (c) no *other* node's visible rectangle intersects the source-center to
   *       target-center line.
   *
   * Otherwise returns null and the caller falls back to WebCola's router.
   * Group containers are not treated as obstacles here — edges from one group
   * to another necessarily cross group boundaries, and the visible rectangle
   * is the only obstacle the eye actually registers.
   *
   * Why this exists: empirically (Purchase 1997/2002; Huang/Hong/Eades 2008)
   * the dominant readability cost on a clear path is *exit angle*, not bend
   * count — an arrow that leaves a node pointing 90° away from its target
   * reads as wrong even if the curve eventually arrives. WebCola's
   * visibility-graph router snaps endpoints to N/S/E/W rectangle midpoints
   * regardless of target direction, so on layouts where the inflated
   * collision bounds form a phantom corridor, it produces routes that exit
   * anti-parallel to the target. The fast path short-circuits those cases.
   */
  private tryDirectLineRoute(edgeData: any): Array<{ x: number; y: number }> | null {
    if (!edgeData?.source || !edgeData?.target) return null;
    if (edgeData.source.id === edgeData.target.id) return null;

    // (a) Skip if there are parallel siblings — handleMultipleEdgeRouting +
    // applyPortBasedEndpoints want to see WebCola's route to apply curvature
    // and port distribution.
    const siblings = this.getAllEdgesBetweenNodes(edgeData.source.id, edgeData.target.id);
    if (siblings.length > 1) return null;

    // Use the *visible* rectangle (visualWidth/visualHeight for nodes, inset
    // bounds for groups) so the arrowhead lands on the rendered border. The
    // alternative — normalizeNodeBounds — returns WebCola's inflated collision
    // bounds, which are ~3 px outside the rendered rectangle; clipping to
    // those puts the arrow tip in the padding region and lets the 12 px
    // marker body extend back into the node fill (which then covers it since
    // renderNodes appends after renderLinks).
    const sourceBounds = this.getRenderedBounds(edgeData.source);
    const targetBounds = this.getRenderedBounds(edgeData.target);

    // (b) Skip if near-touching — getNearTouchPerpendicularRoute handles those
    // with a U-bend that the direct line would skip entirely.
    const NEAR_TOUCH_THRESHOLD = 5;
    if (this.getTouchDirection(sourceBounds, targetBounds, NEAR_TOUCH_THRESHOLD) !== 'none') {
      return null;
    }

    const sourceCenter = {
      x: sourceBounds.x + sourceBounds.width() / 2,
      y: sourceBounds.y + sourceBounds.height() / 2
    };
    const targetCenter = {
      x: targetBounds.x + targetBounds.width() / 2,
      y: targetBounds.y + targetBounds.height() / 2
    };

    // (c) Bail if any other node's visible rectangle intersects the line.
    if (this.currentLayout?.nodes) {
      for (const node of this.currentLayout.nodes) {
        if (node.id === edgeData.source.id || node.id === edgeData.target.id) continue;
        const nb = this.normalizeNodeBounds(node);
        if (this.lineIntersectsRect(sourceCenter, targetCenter, nb)) return null;
      }
    }

    // Clip endpoints to perimeters so arrowheads land on the rendered boundary
    // rather than at the node centers (which would put them behind the fill).
    const sourcePoint = this.clipLineToRectExit(sourceCenter, targetCenter, sourceBounds);
    const targetPoint = this.clipLineToRectExit(targetCenter, sourceCenter, targetBounds);

    return [sourcePoint, targetPoint];
  }

  /**
   * Run port distribution on a direct-line fast-path route while preserving
   * the invariant that endpoints sit on their nodes' visible perimeters.
   *
   * applyPortBasedEndpoints distributes endpoints along the dominant-direction
   * side (height for horizontal-axis edges, width for vertical-axis edges).
   * For purely cardinal edges (target directly N/S/E/W of source) my fast-path
   * clip already exits on that side, so distribution slides cleanly along it.
   * For diagonal edges in the 31–45° band (or whatever the rect's
   * aspect-ratio-corner band is), the natural line-intersection clip can hit
   * the bottom/top while dominant direction expects left/right — distribution
   * would then shift the endpoint *inside* the rectangle.
   *
   * Strategy: try distribution; if either endpoint leaves the perimeter, fall
   * back to the un-distributed clip. The un-distributed clip already provides
   * meaningful endpoint separation via the line-geometry difference between
   * sibling edges (their target directions differ → their clip points differ),
   * so the worst case is "less spread" rather than "wrong attachment."
   */
  private applyPortBasedEndpointsToDirectRoute(
    edgeData: any,
    route: Array<{ x: number; y: number }>
  ): Array<{ x: number; y: number }> {
    const hasSourcePort = edgeData._sourcePortCount > 1;
    const hasTargetPort = edgeData._targetPortCount > 1;
    if (!hasSourcePort && !hasTargetPort) return route;

    const distributed = this.applyPortBasedEndpoints(
      edgeData,
      route.map(p => ({ ...p }))
    );

    // Validate against rendered bounds (matches what tryDirectLineRoute
    // clipped against — using inflated bounds here would let an endpoint that
    // shifted into the padding region pass the check).
    const sourceBounds = this.getRenderedBounds(edgeData.source);
    const targetBounds = this.getRenderedBounds(edgeData.target);
    if (
      this.isPointOnRectPerimeter(distributed[0], sourceBounds) &&
      this.isPointOnRectPerimeter(distributed[distributed.length - 1], targetBounds)
    ) {
      return distributed;
    }
    return route;
  }

  /**
   * Returns the *rendered* rectangle of a node — what the user actually sees,
   * not WebCola's inflated collision bounds. Prefers getVisibleBounds (which
   * applies visualWidth/visualHeight and the group inset) and falls back to
   * normalizeNodeBounds when getVisibleBounds can't derive a size.
   *
   * Edge endpoint placement should always use this; routing it against
   * inflated bounds puts the arrowhead in the padding region where the node's
   * fill covers the marker body.
   */
  private getRenderedBounds(node: any): { x: number; y: number; width: () => number; height: () => number } {
    const visible = this.getVisibleBounds(node);
    if (visible) {
      return {
        x: visible.x,
        y: visible.y,
        width: visible.width,
        height: visible.height,
      };
    }
    return this.normalizeNodeBounds(node);
  }

  /**
   * Returns true if `point` lies within `tolerance` pixels of any of the four
   * edges of `rect`. Used by the direct-line fast path to validate that port
   * distribution didn't shift an endpoint inside the rectangle.
   */
  private isPointOnRectPerimeter(
    point: { x: number; y: number },
    rect: { x: number; y: number; width: () => number; height: () => number },
    tolerance: number = 1
  ): boolean {
    const left = rect.x;
    const right = rect.x + rect.width();
    const top = rect.y;
    const bottom = rect.y + rect.height();
    const withinX = point.x >= left - tolerance && point.x <= right + tolerance;
    const withinY = point.y >= top - tolerance && point.y <= bottom + tolerance;
    if (!withinX || !withinY) return false;
    return (
      Math.abs(point.x - left) <= tolerance ||
      Math.abs(point.x - right) <= tolerance ||
      Math.abs(point.y - top) <= tolerance ||
      Math.abs(point.y - bottom) <= tolerance
    );
  }

  /**
   * Given a point `inside` a rectangle and another point `outside` (or at
   * least farther along the ray direction), returns the point where the line
   * from `inside` toward `outside` exits the rectangle.
   *
   * Used by tryDirectLineRoute to clip the source-center → target-center line
   * to the source and target rectangle perimeters so the arrowhead lands on
   * the rendered boundary.
   */
  private clipLineToRectExit(
    inside: { x: number; y: number },
    outside: { x: number; y: number },
    rect: { x: number; y: number; width: () => number; height: () => number }
  ): { x: number; y: number } {
    const dx = outside.x - inside.x;
    const dy = outside.y - inside.y;
    if (dx === 0 && dy === 0) return { x: inside.x, y: inside.y };

    const left = rect.x;
    const right = rect.x + rect.width();
    const top = rect.y;
    const bottom = rect.y + rect.height();

    const candidates: number[] = [];
    if (dx > 0) candidates.push((right - inside.x) / dx);
    else if (dx < 0) candidates.push((left - inside.x) / dx);
    if (dy > 0) candidates.push((bottom - inside.y) / dy);
    else if (dy < 0) candidates.push((top - inside.y) / dy);

    // Smallest positive t is the first boundary crossing from `inside`.
    let t = Infinity;
    for (const c of candidates) {
      if (c > 0 && c < t) t = c;
    }
    if (!Number.isFinite(t)) return { x: inside.x, y: inside.y };

    return { x: inside.x + t * dx, y: inside.y + t * dy };
  }

  /**
   * Computes route points for a single edge.
   * Returns an array of {x, y} points (not an SVG path string).
   *
   * @param edgeData - The edge data object
   * @returns Array of route points, or null if edge should not be rendered
   */
  private computeSingleRoute(edgeData: any): Array<{ x: number; y: number }> | null {
    // Early return for alignment edges - they don't need complex routing
    if (this.isAlignmentEdge(edgeData)) {
      return [
        { x: edgeData.source.x || 0, y: edgeData.source.y || 0 },
        { x: edgeData.target.x || 0, y: edgeData.target.y || 0 }
      ];
    }

    // Self-loops: build the petal route directly. WebCola's routeEdge can't
    // produce a usable polyline for self-loops (source == target collapses to
    // a single point, failing the length>=2 check), and the previous
    // implementation would silently fall back to a degenerate 2-point route
    // with both endpoints at the node center — rendering as an empty path.
    if (edgeData.source.id === edgeData.target.id) {
      return this.createSelfLoopRoute(edgeData);
    }

    // Direct-line fast path: when nothing visible sits between source and target
    // and there are no parallel siblings or near-touch conditions, WebCola's
    // visibility-graph router still occasionally produces snake-shaped routes
    // (its obstacle set includes the *inflated* collision bounds, not the
    // visible rectangles, so clear-looking corridors register as blocked).
    // Bypass it for these cases — the result is a straight diagonal that
    // exits the source pointing at the target, which is what the eye expects.
    //
    // Port distribution still applies to fast-path edges: edges that share a
    // node side with siblings (multiple incoming arrows on a BDD terminal,
    // multiple outgoing arrows from a hub) need their endpoints spread along
    // the side or they cluster at the natural line-intersection point.
    if (!edgeData.id?.startsWith('_g_')) {
      const direct = this.tryDirectLineRoute(edgeData);
      if (direct) {
        return this.applyPortBasedEndpointsToDirectRoute(edgeData, direct);
      }
    }

    const defaultRoute = [
      { x: edgeData.source.x || 0, y: edgeData.source.y || 0 },
      { x: edgeData.target.x || 0, y: edgeData.target.y || 0 }
    ];
    let route: Array<{ x: number; y: number }>;

    // Get initial route from WebCola
    if (typeof (this.colaLayout as any)?.routeEdge === 'function') {
      try {
        route = (this.colaLayout as any).routeEdge(edgeData);

        // Error check the route
        if (!route || !Array.isArray(route) || route.length < 2 ||
            !route[0] || !route[1] || route[0].x === undefined || route[0].y === undefined) {
                throw new Error(`WebCola failed to route edge ${edgeData.id} from ${edgeData.source.id} to ${edgeData.target.id}`);
        }
      } catch (e) {
        console.log("Error routing edge", edgeData.id, `from ${edgeData.source.id} to ${edgeData.target.id}`);
        console.error(e);
        return defaultRoute;
      }
    } else {
      // Fallback route
      route = defaultRoute;
    }

    // Handle group edges: snap the member end to the group boundary.
    // After this, skip getNearTouchPerpendicularRoute — it uses raw node positions
    // and would override the group boundary snap with a member-node-center route.
    if (edgeData.id?.startsWith('_g_')) {
      return this.routeGroupEdge(edgeData, route);
    }
    // Handle multiple edges between same nodes
    route = this.handleMultipleEdgeRouting(edgeData, route);

    // Apply port-based endpoint distribution for ALL edges (not just multi-edges
    // between the same pair). This spreads edges that share the same node side
    // across the side length to reduce crossings and overlapping anchors.
    route = this.applyPortBasedEndpoints(edgeData, route);

    // Check for near-touching nodes and reroute via perpendicular sides if needed
    const nearTouchRoute = this.getNearTouchPerpendicularRoute(edgeData);
    if (nearTouchRoute) {
      return nearTouchRoute;
    }

    return route;
  }

  // ── Edge Crossing Optimization ──────────────────────────────────────

  /**
   * Orchestrates post-routing crossing detection and resolution.
   * Runs up to MAX_CROSSING_OPTIMIZATION_PASSES iterations, each pass
   * detecting crossings and attempting local fixes.
   *
   * Performance guardrails (Tier 1.5):
   *   - Skip entirely if edge count exceeds CROSSING_OPTIMIZATION_EDGE_THRESHOLD
   *     (50 — keeps the worst case bounded by graph size).
   *   - Wall-clock budget MAX_CROSSING_OPTIMIZATION_BUDGET_MS (30ms) — if
   *     detection+resolution overruns, abort and accept current routing. This
   *     covers pathological graphs that fall below the edge-count gate but
   *     have, e.g., very long polyline routes or many crossings.
   */
  private optimizeCrossings(): void {
    // Need at least 2 non-alignment edges to have a crossing
    if (this.computedRoutes.size < 2) return;

    // Skip O(E²) crossing optimization on dense graphs — the base routing
    // (port angle sorting + blocking-node avoidance) is good enough.
    let candidateEdges = 0;
    if (this.currentLayout?.links) {
      for (const edge of this.currentLayout.links as EdgeWithMetadata[]) {
        if (this.isAlignmentEdge(edge)) continue;
        if (edge.source.id === edge.target.id) continue;
        if (!this.computedRoutes.get(edge.id)) continue;
        candidateEdges++;
      }
    }
    if (candidateEdges > WebColaCnDGraph.CROSSING_OPTIMIZATION_EDGE_THRESHOLD) return;

    const budgetMs = WebColaCnDGraph.MAX_CROSSING_OPTIMIZATION_BUDGET_MS;
    const startedAt = (typeof performance !== 'undefined' && typeof performance.now === 'function')
      ? performance.now()
      : Date.now();
    const elapsed = (): number => (
      (typeof performance !== 'undefined' && typeof performance.now === 'function')
        ? performance.now()
        : Date.now()
    ) - startedAt;

    for (let pass = 0; pass < WebColaCnDGraph.MAX_CROSSING_OPTIMIZATION_PASSES; pass++) {
      if (elapsed() > budgetMs) {
        console.warn(`[optimizeCrossings] Budget (${budgetMs}ms) exceeded after pass ${pass}; accepting current routing.`);
        return;
      }
      const crossings = this.detectEdgeCrossings();
      if (crossings.length === 0) break;
      if (elapsed() > budgetMs) {
        console.warn(`[optimizeCrossings] Budget exceeded after detection on pass ${pass}; accepting current routing.`);
        return;
      }
      const improved = this.resolveEdgeCrossings(crossings);
      if (!improved) break;
    }
  }

  /**
   * Detects edge-edge crossings in the computed routes using segment-segment
   * intersection tests (cross-product method).
   *
   * Skips alignment edges and self-loops. For pairs that share an endpoint
   * node, drops the segments incident to the shared node before testing for
   * intersection — touching at a shared node is unavoidable, but a *real*
   * crossing past the shared node is fixable (typically by a port swap).
   *
   * @returns Array of [edgeId1, edgeId2] crossing pairs, sorted by severity
   *          (longer total edge length = more severe crossing)
   */
  private detectEdgeCrossings(): Array<[string, string]> {
    const edgeIds: string[] = [];
    const edgeRoutes: Array<Array<{ x: number; y: number }>> = [];
    const edgeEndpoints: Array<{ sourceId: string; targetId: string }> = [];

    // Collect non-excluded edges
    if (this.currentLayout?.links) {
      for (const edge of this.currentLayout.links as EdgeWithMetadata[]) {
        if (this.isAlignmentEdge(edge)) continue;
        if (edge.source.id === edge.target.id) continue; // self-loop
        const route = this.computedRoutes.get(edge.id);
        if (!route || route.length < 2) continue;
        edgeIds.push(edge.id);
        edgeRoutes.push(route);
        edgeEndpoints.push({ sourceId: edge.source.id, targetId: edge.target.id });
      }
    }

    const crossings: Array<[string, string, number]> = []; // [id1, id2, severity]

    for (let i = 0; i < edgeIds.length; i++) {
      for (let j = i + 1; j < edgeIds.length; j++) {
        const ei = edgeEndpoints[i];
        const ej = edgeEndpoints[j];
        const sharedAtSourceI = ei.sourceId === ej.sourceId || ei.sourceId === ej.targetId;
        const sharedAtTargetI = ei.targetId === ej.sourceId || ei.targetId === ej.targetId;
        // Parallel edges (both endpoints shared) only ever touch at endpoints —
        // skip them (port distribution handles their separation).
        if (sharedAtSourceI && sharedAtTargetI) continue;

        // For all other pairs (including those sharing one node) the strict
        // proper-intersection test in segmentsIntersect rules out touches at
        // the shared endpoint, so the full routes can be tested directly.
        if (this.routesCross(edgeRoutes[i], edgeRoutes[j])) {
          const severity = this.getRouteLength(edgeRoutes[i]) + this.getRouteLength(edgeRoutes[j]);
          crossings.push([edgeIds[i], edgeIds[j], severity]);
        }
      }
    }

    // Sort by severity descending (worst crossings first)
    crossings.sort((a, b) => b[2] - a[2]);
    return crossings.map(([id1, id2]) => [id1, id2]);
  }

  /**
   * Tests whether two polyline routes cross each other using
   * segment-segment intersection (cross-product method).
   */
  private routesCross(
    routeA: Array<{ x: number; y: number }>,
    routeB: Array<{ x: number; y: number }>
  ): boolean {
    for (let i = 0; i < routeA.length - 1; i++) {
      for (let j = 0; j < routeB.length - 1; j++) {
        if (this.segmentsIntersect(routeA[i], routeA[i + 1], routeB[j], routeB[j + 1])) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Tests whether two line segments (p1-p2) and (p3-p4) properly intersect
   * (cross each other, not just touch at endpoints).
   */
  private segmentsIntersect(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    p3: { x: number; y: number },
    p4: { x: number; y: number }
  ): boolean {
    const d1 = this.cross(p3, p4, p1);
    const d2 = this.cross(p3, p4, p2);
    const d3 = this.cross(p1, p2, p3);
    const d4 = this.cross(p1, p2, p4);

    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
        ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
      return true;
    }
    return false;
  }

  /** Cross product of vectors (b-a) and (c-a). */
  private cross(
    a: { x: number; y: number },
    b: { x: number; y: number },
    c: { x: number; y: number }
  ): number {
    return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  }

  /**
   * Attempts to resolve detected edge crossings using local strategies.
   *
   * Strategy 1: Control point nudge — add/adjust an intermediate waypoint
   * perpendicular to the crossing, pushing one edge away from the other.
   *
   * @returns true if any route was modified
   */
  private resolveEdgeCrossings(crossings: Array<[string, string]>): boolean {
    let modified = false;

    for (const [id1, id2] of crossings) {
      const route1 = this.computedRoutes.get(id1);
      const route2 = this.computedRoutes.get(id2);
      if (!route1 || !route2) continue;

      // Strategy 0: if the two crossing edges share an endpoint node, try
      // swapping their port indices at that node. This is the canonical fix
      // for "two edges left the same side in the wrong angular order" and is
      // cheaper than nudging or rerouting.
      const edge1 = this.findEdgeById(id1);
      const edge2 = this.findEdgeById(id2);
      const sharedNodeId = edge1 && edge2 ? this.findSharedNodeId(edge1, edge2) : null;
      if (edge1 && edge2 && sharedNodeId) {
        if (this.tryResolveByPortSwap(edge1, edge2, sharedNodeId)) {
          modified = true;
          continue;
        }
      }

      // Find the exact crossing point and which segments cross
      const crossingInfo = this.findCrossingSegments(route1, route2);
      if (!crossingInfo) continue;

      // Nudge the shorter route to avoid the crossing.
      const len1 = this.getRouteLength(route1);
      const len2 = this.getRouteLength(route2);
      const [targetRoute, targetId] = len1 <= len2 ? [route1, id1] : [route2, id2];
      const otherRoute = targetRoute === route1 ? route2 : route1;
      const segIdx = targetRoute === route1 ? crossingInfo.segIdxA : crossingInfo.segIdxB;

      // Strategy 1: Small perpendicular nudge (works for near-miss crossings)
      const seg = { dx: targetRoute[segIdx + 1].x - targetRoute[segIdx].x, dy: targetRoute[segIdx + 1].y - targetRoute[segIdx].y };
      const segLen = Math.sqrt(seg.dx * seg.dx + seg.dy * seg.dy);
      if (segLen < 1) continue;

      const perpX = -seg.dy / segLen;
      const perpY = seg.dx / segLen;

      let resolved = false;
      const nudgeDist = WebColaCnDGraph.CROSSING_NUDGE_DISTANCE;
      for (const sign of [1, -1]) {
        const midX = (targetRoute[segIdx].x + targetRoute[segIdx + 1].x) / 2 + perpX * nudgeDist * sign;
        const midY = (targetRoute[segIdx].y + targetRoute[segIdx + 1].y) / 2 + perpY * nudgeDist * sign;

        const candidate = [...targetRoute];
        candidate.splice(segIdx + 1, 0, { x: midX, y: midY });

        if (!this.routesCross(candidate, otherRoute)) {
          this.computedRoutes.set(targetId, candidate);
          modified = true;
          resolved = true;
          break;
        }
      }

      // Strategy 2: Route around the blocking edge by going to one side of its bounding box
      if (!resolved) {
        const rerouteResult = this.rerouteAroundEdge(targetRoute, otherRoute);
        if (rerouteResult && !this.routesCross(rerouteResult, otherRoute)) {
          this.computedRoutes.set(targetId, rerouteResult);
          modified = true;
        }
      }
    }

    return modified;
  }

  /** Look up an edge in the current layout by its id. */
  private findEdgeById(edgeId: string): EdgeWithMetadata | null {
    if (!this.currentLayout?.links) return null;
    for (const e of this.currentLayout.links as EdgeWithMetadata[]) {
      if (e.id === edgeId) return e;
    }
    return null;
  }

  /**
   * Returns the id of the node both edges have in common, or null if they
   * don't share an endpoint. Two parallel edges (sharing both endpoints) are
   * treated as no shared-node fixable case — port distribution handles those.
   */
  private findSharedNodeId(a: EdgeWithMetadata, b: EdgeWithMetadata): string | null {
    const aSrc = a.source.id;
    const aTgt = a.target.id;
    const bSrc = b.source.id;
    const bTgt = b.target.id;
    const sharedAtA1 = aSrc === bSrc || aSrc === bTgt;
    const sharedAtA2 = aTgt === bSrc || aTgt === bTgt;
    if (sharedAtA1 && sharedAtA2) return null; // parallel
    if (sharedAtA1) return aSrc;
    if (sharedAtA2) return aTgt;
    return null;
  }

  /**
   * Swap the port indices of two edges at their shared node, recompute the
   * relevant endpoints, and accept the swap only if it reduces or removes the
   * crossing between them. Reverts otherwise.
   */
  private tryResolveByPortSwap(
    edge1: EdgeWithMetadata,
    edge2: EdgeWithMetadata,
    sharedNodeId: string
  ): boolean {
    const e1: any = edge1;
    const e2: any = edge2;
    const route1Before = this.computedRoutes.get(edge1.id);
    const route2Before = this.computedRoutes.get(edge2.id);
    if (!route1Before || !route2Before) return false;

    const isSrc1 = edge1.source.id === sharedNodeId;
    const isSrc2 = edge2.source.id === sharedNodeId;
    const idxKey1 = isSrc1 ? '_sourcePortIndex' : '_targetPortIndex';
    const cntKey1 = isSrc1 ? '_sourcePortCount' : '_targetPortCount';
    const idxKey2 = isSrc2 ? '_sourcePortIndex' : '_targetPortIndex';
    const cntKey2 = isSrc2 ? '_sourcePortCount' : '_targetPortCount';

    const i1 = e1[idxKey1];
    const i2 = e2[idxKey2];
    const c1 = e1[cntKey1];
    const c2 = e2[cntKey2];

    // Need both edges to have port info on the same node and same port-count
    // bucket (same side of the shared node).
    if (i1 === undefined || i2 === undefined || c1 === undefined || c2 === undefined) return false;
    if (c1 !== c2) return false;
    if (i1 === i2) return false;

    // Swap and recompute routes via the same pipeline used at routing time.
    e1[idxKey1] = i2;
    e2[idxKey2] = i1;

    const route1After = this.applyPortBasedEndpoints(e1, route1Before.map(p => ({ ...p })));
    const route2After = this.applyPortBasedEndpoints(e2, route2Before.map(p => ({ ...p })));

    if (this.routesCross(route1After, route2After)) {
      // Revert
      e1[idxKey1] = i1;
      e2[idxKey2] = i2;
      return false;
    }

    this.computedRoutes.set(edge1.id, route1After);
    this.computedRoutes.set(edge2.id, route2After);
    return true;
  }

  /**
   * Reroutes an edge to go around a blocking edge's bounding box.
   *
   * Determines which side of the blocking route the start and end points are on,
   * and routes via waypoints that stay clear of the blocking path.
   */
  private rerouteAroundEdge(
    route: Array<{ x: number; y: number }>,
    blockingRoute: Array<{ x: number; y: number }>
  ): Array<{ x: number; y: number }> | null {
    // Compute bounding box of the blocking route
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of blockingRoute) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }

    const MARGIN = WebColaCnDGraph.CROSSING_NUDGE_DISTANCE;
    const start = route[0];
    const end = route[route.length - 1];
    const blockW = maxX - minX;
    const blockH = maxY - minY;

    const candidates: Array<Array<{ x: number; y: number }>> = [];

    if (blockH >= blockW) {
      // Blocking route is tall/vertical — route left or right of it
      const leftX = minX - MARGIN;
      const rightX = maxX + MARGIN;

      // For each side, build a path: start → waypoint1 → waypoint2 → end
      // that stays entirely on that side of the blocking route.
      for (const sideX of [leftX, rightX]) {
        const candidate = [
          start,
          { x: sideX, y: start.y },
          { x: sideX, y: end.y },
          end
        ];
        if (!this.routesCross(candidate, blockingRoute)) {
          candidates.push(candidate);
        }
      }

      // If simple L-routes don't work (start/end are on opposite sides),
      // try a Z-route that crosses the blocking route above or below its extent
      if (candidates.length === 0) {
        for (const crossY of [minY - MARGIN, maxY + MARGIN]) {
          const candidate = [
            start,
            { x: start.x, y: crossY },
            { x: end.x, y: crossY },
            end
          ];
          if (!this.routesCross(candidate, blockingRoute)) {
            candidates.push(candidate);
          }
        }
      }
    } else {
      // Blocking route is wide/horizontal — route above or below
      const topY = minY - MARGIN;
      const bottomY = maxY + MARGIN;

      for (const sideY of [topY, bottomY]) {
        const candidate = [
          start,
          { x: start.x, y: sideY },
          { x: end.x, y: sideY },
          end
        ];
        if (!this.routesCross(candidate, blockingRoute)) {
          candidates.push(candidate);
        }
      }

      // Z-route fallback: cross to the left or right of the blocking extent
      if (candidates.length === 0) {
        for (const crossX of [minX - MARGIN, maxX + MARGIN]) {
          const candidate = [
            start,
            { x: crossX, y: start.y },
            { x: crossX, y: end.y },
            end
          ];
          if (!this.routesCross(candidate, blockingRoute)) {
            candidates.push(candidate);
          }
        }
      }
    }

    if (candidates.length === 0) return null;

    // Pick the shortest candidate
    candidates.sort((a, b) => this.getRouteLength(a) - this.getRouteLength(b));
    return candidates[0];
  }

  /**
   * Finds the specific segment indices where two routes cross.
   * Returns the first crossing found, or null if none.
   */
  private findCrossingSegments(
    routeA: Array<{ x: number; y: number }>,
    routeB: Array<{ x: number; y: number }>
  ): { segIdxA: number; segIdxB: number } | null {
    for (let i = 0; i < routeA.length - 1; i++) {
      for (let j = 0; j < routeB.length - 1; j++) {
        if (this.segmentsIntersect(routeA[i], routeA[i + 1], routeB[j], routeB[j + 1])) {
          return { segIdxA: i, segIdxB: j };
        }
      }
    }
    return null;
  }

  /**
   * Creates a self-loop route for edges that connect a node to itself.
   *
   * Geometry: a *tulip petal* — start and end land on the same side of the
   * node, close together, and the curve bulges out perpendicular to that
   * side, forming a teardrop. Loops distribute around all four sides (in
   * the order right → bottom → left → top), so a node with two self-loops
   * gets one petal on the right and one on the bottom rather than two
   * stacked on the top. The 5th+ loop on a node goes into an outer ring
   * (further bulge).
   *
   * The 5-point polyline gives curveBasis enough control to produce both a
   * rounded apex and arrival tangents perpendicular to the side, so the
   * arrowhead points back into the node rather than skimming along the
   * perimeter.
   *
   * @param edgeData - The edge data object
   * @returns Array of route points for the self-loop
   */
  private createSelfLoopRoute(edgeData: any): Array<{ x: number; y: number }> {
    const source = edgeData.source;
    const bounds = source.bounds;

    if (!bounds) {
      // Fallback for missing bounds — minimal upward petal.
      return [
        { x: source.x - 8, y: source.y },
        { x: source.x - 8, y: source.y - 20 },
        { x: source.x,     y: source.y - 28 },
        { x: source.x + 8, y: source.y - 20 },
        { x: source.x + 8, y: source.y },
      ];
    }

    const width = bounds.X - bounds.x;
    const height = bounds.Y - bounds.y;
    const cx = bounds.x + width / 2;
    const cy = bounds.y + height / 2;

    const selfLoopIndex = this.getSelfLoopIndex(edgeData);
    const sideIdx = selfLoopIndex % 4;
    const ring = Math.floor(selfLoopIndex / 4);
    const ringScale = 1 + ring * WebColaCnDGraph.SELF_LOOP_CURVATURE_SCALE;

    // Side geometry: pick a "center" on the side, an outward unit normal,
    // and a unit tangent along the side. The petal opens outward; start and
    // end are placed symmetrically along the tangent.
    let sideCenter: { x: number; y: number };
    let outwardX = 0, outwardY = 0;
    let alongX = 0, alongY = 0;
    switch (sideIdx) {
      case 0: // right
        sideCenter = { x: bounds.X, y: cy };
        outwardX = 1;  alongX = 0;  outwardY = 0;  alongY = 1;
        break;
      case 1: // bottom
        sideCenter = { x: cx, y: bounds.Y };
        outwardX = 0;  alongX = -1; outwardY = 1;  alongY = 0;
        break;
      case 2: // left
        sideCenter = { x: bounds.x, y: cy };
        outwardX = -1; alongX = 0;  outwardY = 0;  alongY = -1;
        break;
      case 3: // top
      default:
        sideCenter = { x: cx, y: bounds.y };
        outwardX = 0;  alongX = 1;  outwardY = -1; alongY = 0;
        break;
    }

    // Spread = how far apart start and end sit along the side. Bulge = how
    // far the apex extends perpendicular to the side. Both scale with the
    // shorter node dimension so petals stay proportional on long-thin
    // rectangles, and grow with the ring index so outer-ring petals are
    // visibly distinct from inner ones.
    const minDim = Math.min(width, height);
    const sideLen = (sideIdx === 0 || sideIdx === 2) ? height : width;
    const spread = Math.min(sideLen * 0.45, minDim * 0.5);
    const bulge = minDim * 0.7 * ringScale;
    const tangentLen = spread * 0.55; // sets how sharply the petal "opens"

    const startPoint = {
      x: sideCenter.x - alongX * spread / 2,
      y: sideCenter.y - alongY * spread / 2,
    };
    const endPoint = {
      x: sideCenter.x + alongX * spread / 2,
      y: sideCenter.y + alongY * spread / 2,
    };
    // Tangent guides at start/end push the curve outward perpendicular to
    // the side; the apex closes the bulge at the petal's far point.
    const ctrlA = {
      x: startPoint.x + outwardX * tangentLen,
      y: startPoint.y + outwardY * tangentLen,
    };
    const ctrlB = {
      x: endPoint.x + outwardX * tangentLen,
      y: endPoint.y + outwardY * tangentLen,
    };
    const apex = {
      x: sideCenter.x + outwardX * bulge,
      y: sideCenter.y + outwardY * bulge,
    };

    return [startPoint, ctrlA, apex, ctrlB, endPoint];
  }

  /**
   * Grid-mode self-loop route: an orthogonal "petal" — a rectangular bump
   * perpendicular to one side of the node. Mirrors the side / ring
   * distribution from createSelfLoopRoute() (the curvy default-mode petal)
   * so the two routing modes agree on which side each self-loop lives on.
   *
   * Geometry (e.g. for top-side petal):
   *   p0 = (cx - spread/2, top)   ← start
   *   p1 = (cx - spread/2, top - bulge)
   *   p2 = (cx + spread/2, top - bulge)
   *   p3 = (cx + spread/2, top)   ← end
   * Drawn with the rectilinear gridLineFunction → three right-angle bends.
   */
  private createGridSelfLoopRoute(edgeData: any): Array<{ x: number; y: number }> {
    const source = edgeData.source;
    const bounds = source.bounds;

    if (!bounds) {
      return [
        { x: source.x - 10, y: source.y },
        { x: source.x - 10, y: source.y - 24 },
        { x: source.x + 10, y: source.y - 24 },
        { x: source.x + 10, y: source.y },
      ];
    }

    const width = bounds.X - bounds.x;
    const height = bounds.Y - bounds.y;
    const cx = bounds.x + width / 2;
    const cy = bounds.y + height / 2;

    const selfLoopIndex = this.getSelfLoopIndex(edgeData);
    const sideIdx = selfLoopIndex % 4;
    const ring = Math.floor(selfLoopIndex / 4);
    const ringScale = 1 + ring * WebColaCnDGraph.SELF_LOOP_CURVATURE_SCALE;

    // Spread and bulge scaled to the node so petals stay proportional.
    const minDim = Math.min(width, height);
    const sideLen = (sideIdx === 0 || sideIdx === 2) ? height : width;
    const spread = Math.min(sideLen * 0.45, minDim * 0.5);
    const bulge = minDim * 0.55 * ringScale;

    let p0: { x: number; y: number };
    let p1: { x: number; y: number };
    let p2: { x: number; y: number };
    let p3: { x: number; y: number };

    switch (sideIdx) {
      case 0: // right side: bump out to the right
        p0 = { x: bounds.X,           y: cy - spread / 2 };
        p1 = { x: bounds.X + bulge,   y: cy - spread / 2 };
        p2 = { x: bounds.X + bulge,   y: cy + spread / 2 };
        p3 = { x: bounds.X,           y: cy + spread / 2 };
        break;
      case 1: // bottom side: bump down
        p0 = { x: cx + spread / 2,    y: bounds.Y };
        p1 = { x: cx + spread / 2,    y: bounds.Y + bulge };
        p2 = { x: cx - spread / 2,    y: bounds.Y + bulge };
        p3 = { x: cx - spread / 2,    y: bounds.Y };
        break;
      case 2: // left side: bump to the left
        p0 = { x: bounds.x,           y: cy + spread / 2 };
        p1 = { x: bounds.x - bulge,   y: cy + spread / 2 };
        p2 = { x: bounds.x - bulge,   y: cy - spread / 2 };
        p3 = { x: bounds.x,           y: cy - spread / 2 };
        break;
      case 3: // top side: bump up
      default:
        p0 = { x: cx - spread / 2,    y: bounds.y };
        p1 = { x: cx - spread / 2,    y: bounds.y - bulge };
        p2 = { x: cx + spread / 2,    y: bounds.y - bulge };
        p3 = { x: cx + spread / 2,    y: bounds.y };
        break;
    }

    return [p0, p1, p2, p3];
  }

  /**
   * Returns the 0-based index of this self-loop among all self-loops on its node.
   * Uses the edgesBetweenNodes cache when available.
   */
  private getSelfLoopIndex(edgeData: any): number {
    const nodeId = edgeData.source.id;
    const key = this.getNodePairKey(nodeId, nodeId);
    const selfLoops = this.edgeRoutingCache.edgesBetweenNodes.get(key);
    if (selfLoops) {
      const idx = selfLoops.findIndex((e: any) => e.id === edgeData.id);
      return idx >= 0 ? idx : 0;
    }
    // Fallback: scan all links
    if (this.currentLayout?.links) {
      let idx = 0;
      for (const edge of this.currentLayout.links as any[]) {
        if (edge.source.id === nodeId && edge.target.id === nodeId) {
          if (edge.id === edgeData.id) return idx;
          idx++;
        }
      }
    }
    return 0;
  }

  /**
   * Routes group edges with special handling for group boundaries.
   * 
   * @param edgeData - The edge data object
   * @param route - Initial route points
   * @returns Modified route points for group edge
   */
  private routeGroupEdge(edgeData: any, route: Array<{ x: number; y: number }>): Array<{ x: number; y: number }> {
    // Look up the group directly by the groupId stamped on the edge at creation time.
    const group = edgeData.groupId
      ? (this.currentLayout?.groups || []).find((g: any) => g.id === edgeData.groupId)
      : null;

    if (!group) {
      console.warn('[routeGroupEdge] Group not found for edge:', edgeData.id, '— groupId:', edgeData.groupId);
      // Leave route unmodified; edge will point at member node center.
    } else {
      // Synthesise bounds if WebCola hasn't computed them yet (stability / few-iteration layouts).
      // Use the MEMBER node's position (not the anchor) so the edge points in the right direction.
      // In routeGroupEdge: route[0] is near source, route[last] is near target.
      if (!group.bounds && cola?.Rectangle) {
        const memberPt = edgeData.source?.id === edgeData.keyNodeId
          ? route[route.length - 1]   // source is anchor → member end is last route point
          : route[0];                  // target is anchor → member end is first route point
        if (memberPt) {
          group.bounds = new cola.Rectangle(
            memberPt.x - 50, memberPt.x + 50,
            memberPt.y - 30, memberPt.y + 30
          );
        }
      }

      if (group.bounds) {
        // edgeData.keyNodeId is the external anchor stamped at construction time (= graphlib edge.v).
        // The other end is the group member — snap that route endpoint to the group boundary.
        if (edgeData.source?.id === edgeData.keyNodeId) {
          // source is anchor → target is member → snap last route point to group boundary
          route[route.length - 1] = this.closestPointOnRect(group.bounds, route[0]);
        } else if (edgeData.target?.id === edgeData.keyNodeId) {
          // target is anchor → source is member → snap first route point to group boundary
          const bounds = group.bounds.inflate?.(-1) ?? group.bounds;
          route[0] = this.closestPointOnRect(bounds, route[route.length - 1]);
        } else {
          console.warn('[routeGroupEdge] keyNodeId matched neither side', {
            keyNodeId: edgeData.keyNodeId, sourceId: edgeData.source?.id, targetId: edgeData.target?.id
          });
        }
      }
    }

    // Simplify route — remove intermediate waypoints for group edges.
    if (route.length > 2) {
      route.splice(1, route.length - 2);
    }

    return route;
  }

  /**
   * Handles routing for multiple edges between the same pair of nodes.
   * Applies curvature and offset to prevent edge overlap.
   * 
   * @param edgeData - The edge data object
   * @param route - Initial route points
   * @returns Modified route points with curvature and offset
   */
  private handleMultipleEdgeRouting(edgeData: any, route: Array<{ x: number; y: number }>): Array<{ x: number; y: number }> {
    const allEdgesBetweenNodes = this.getAllEdgesBetweenNodes(edgeData.source.id, edgeData.target.id);
    
    // Early return for single edge - no curvature needed
    if (allEdgesBetweenNodes.length <= 1) {
      return route;
    }

    // Add midpoint if route only has start and end
    if (route.length === 2) {
      const midpoint = {
        x: (route[0].x + route[1].x) / 2,
        y: (route[0].y + route[1].y) / 2
      };
      route.splice(1, 0, midpoint);
    }

    // Calculate direction and distance once
    const dx = route[1].x - route[0].x;
    const dy = route[1].y - route[0].y;
    const angle = Math.atan2(dy, dx);
    const distance = this.getRouteLength(route);

    // Find edge index once and reuse for both offset and curvature
    const edgeIndex = allEdgesBetweenNodes.findIndex(edge => edge.id === edgeData.id);
    
    // Apply offset and curvature only if we found the edge
    if (edgeIndex !== -1) {
      route = this.applyEdgeOffsetWithIndex(edgeData, route, allEdgesBetweenNodes, angle, edgeIndex, distance);
      const curvature = this.calculateCurvatureWithIndex(allEdgesBetweenNodes, edgeData.id, edgeIndex);
      const cappedCurvature = this.clampCurvature(curvature);
      route = this.applyCurvatureToRoute(route, cappedCurvature, angle, distance);
    }

    return route;
  }

  /**
   * Gets all non-alignment edges between two nodes (bidirectional).
   * Uses cached results for O(1) lookup performance during edge routing.
   * 
   * @param sourceId - Source node ID
   * @param targetId - Target node ID
   * @returns Array of edges between the nodes
   */
  private getAllEdgesBetweenNodes(sourceId: string, targetId: string): EdgeWithMetadata[] {
    if (!this.currentLayout?.links) return [];
    
    // Use cache if available (during routing phase)
    const key = this.getNodePairKey(sourceId, targetId);
    if (this.edgeRoutingCache.edgesBetweenNodes.has(key)) {
      return this.edgeRoutingCache.edgesBetweenNodes.get(key)!;
    }
    
    // Fallback to direct filtering if cache not built (shouldn't happen during routing)
    return this.currentLayout.links.filter((edge: EdgeWithMetadata) => {
      return !this.isAlignmentEdge(edge) && (
        (edge.source.id === sourceId && edge.target.id === targetId) ||
        (edge.source.id === targetId && edge.target.id === sourceId)
      );
    });
  }

  /**
   * Calculates curvature for an edge based on the number of edges between nodes.
   * 
   * @param allEdges - All edges between the nodes
   * @param sourceId - Source node ID
   * @param targetId - Target node ID
   * @param edgeId - Current edge ID
   * @returns Curvature value for the edge
   */
  private calculateCurvature(allEdges: any[], sourceId: string, targetId: string, edgeId: string): number {
    if (edgeId.startsWith('_alignment_')) {
      return 0;
    }

    const edgeCount = allEdges.length;
    const edgeIndex = allEdges.findIndex(edge => edge.id === edgeId);

    if (edgeCount <= 1) {
      return 0;
    }

    return (edgeIndex % 2 === 0 ? 1 : -1) * 
            (Math.floor(edgeIndex / 2) + 1) * 
            WebColaCnDGraph.CURVATURE_BASE_MULTIPLIER * 
            edgeCount;
  }

  /**
   * Calculates curvature using pre-computed edge index (optimized version).
   * Alignment edges are already filtered out during cache building.
   *
   * Curvature direction is aligned with the edge's port offset from the side
   * centerline: a port above center curves further up, a port below center
   * curves further down. Without this alignment, two parallel edges between
   * the same pair (assigned alternating ports by sortEdgePortsByAngle) get
   * curves that bulge *toward* each other across the midline, producing the
   * very crossings the port assignment was meant to prevent. The legacy
   * formula (alternating sign by edge index) is kept as a fallback when port
   * info hasn't been stamped on the edge.
   *
   * @param allEdges - All edges between the nodes
   * @param edgeId - Current edge ID (only used for legacy fallback)
   * @param edgeIndex - Pre-computed index of edge in allEdges array
   * @returns Curvature value for the edge
   */
  private calculateCurvatureWithIndex(allEdges: any[], edgeId: string, edgeIndex: number): number {
    const edgeCount = allEdges.length;
    if (edgeCount <= 1) {
      return 0;
    }

    // Prefer port-aligned curvature when available. Use the source-side port
    // index; for parallel edges (same source/target pair), the source and
    // target ports get the same sort order so this also aligns the target
    // end.
    const edge = allEdges[edgeIndex] as any;
    const portIndex = edge?._sourcePortIndex;
    const portCount = edge?._sourcePortCount;
    if (
      typeof portIndex === 'number' &&
      typeof portCount === 'number' &&
      portCount > 1
    ) {
      // Map port index to a signed offset in [-1, +1] relative to centerline.
      const centerOffset = (portIndex - (portCount - 1) / 2) / ((portCount - 1) / 2);
      // Scale matches the legacy peak magnitude: count × BASE.
      // For 2 ports, outer offset (±1) × 2 × 0.15 = ±0.3 (= legacy 2-edge value).
      // For 4 ports, outer ±1 × 4 × 0.15 = ±0.6 (= clamped legacy 4-edge value).
      return centerOffset * portCount * WebColaCnDGraph.CURVATURE_BASE_MULTIPLIER;
    }

    // Legacy fallback: alternating sign by edge index.
    return (edgeIndex % 2 === 0 ? 1 : -1) *
            (Math.floor(edgeIndex / 2) + 1) *
            WebColaCnDGraph.CURVATURE_BASE_MULTIPLIER *
            edgeCount;
  }

  /**
   * Applies offset to edge points to prevent overlap between multiple edges.
   * 
   * @param edgeData - The edge data object
   * @param route - Route points
   * @param allEdges - All edges between the nodes
   * @param angle - Edge angle
   * @returns Modified route with offset applied
   */
  private applyEdgeOffset(edgeData: any, route: Array<{ x: number; y: number }>, allEdges: any[], angle: number): Array<{ x: number; y: number }> {
    const edgeIndex = allEdges.findIndex(edge => edge.id === edgeData.id);
    const distance = this.getRouteLength(route);
    return this.applyEdgeOffsetWithIndex(edgeData, route, allEdges, angle, edgeIndex, distance);
  }

  /**
   * Applies offset using pre-computed edge index (optimized version).
   * Shared implementation for offset calculation to avoid code duplication.
   * 
   * @param edgeData - The edge data object
   * @param route - Route points
   * @param allEdges - All edges between the nodes
   * @param angle - Edge angle
   * @param edgeIndex - Pre-computed index of edge in allEdges array
   * @returns Modified route with offset applied
   */
  private applyEdgeOffsetWithIndex(edgeData: any, route: Array<{ x: number; y: number }>, allEdges: any[], angle: number, edgeIndex: number, distance: number): Array<{ x: number; y: number }> {
    const direction = this.getDominantDirection(angle);

    // Use port-based positioning when available (from sortEdgePortsByAngle)
    const sourcePortIndex = edgeData._sourcePortIndex;
    const sourcePortCount = edgeData._sourcePortCount;
    const targetPortIndex = edgeData._targetPortIndex;
    const targetPortCount = edgeData._targetPortCount;
    const hasPortInfo = sourcePortIndex !== undefined && sourcePortCount !== undefined && sourcePortCount > 1;
    const hasTargetPortInfo = targetPortIndex !== undefined && targetPortCount !== undefined && targetPortCount > 1;

    if (hasPortInfo || hasTargetPortInfo) {
      // Distribute edge endpoints evenly across the node side
      this.applyPortBasedOffset(route, edgeData, direction, hasPortInfo, hasTargetPortInfo);
    } else {
      // Fallback to legacy alternating offset for multi-edges between same pair
      const offset = (edgeIndex % 2 === 0 ? 1 : -1) *
                      (Math.floor(edgeIndex / 2) + 1) *
                      WebColaCnDGraph.MIN_EDGE_DISTANCE;
      const cappedOffset = this.clampOffset(offset, distance);

      if (direction === 'right' || direction === 'left') {
        route[0].y += cappedOffset;
        route[route.length - 1].y += cappedOffset;
      } else if (direction === 'up' || direction === 'down') {
        route[0].x += cappedOffset;
        route[route.length - 1].x += cappedOffset;
      }
    }

    // Ensure points stay on rectangle perimeter
    if (edgeData.source.innerBounds) {
      route[0] = this.adjustPointToRectanglePerimeter(route[0], edgeData.source.innerBounds);
    }
    if (edgeData.target.innerBounds) {
      route[route.length - 1] = this.adjustPointToRectanglePerimeter(route[route.length - 1], edgeData.target.innerBounds);
    }

    return route;
  }

  /**
   * Computes the per-side margin used when distributing ports along a node side.
   *
   * Default behavior (low port density) returns `sideLength * PORT_MARGIN_FRACTION`,
   * preserving the legacy look. When port density is high enough that adjacent ports
   * would land closer than MIN_PORT_PERIMETER_SPACING, the margin is shrunk so the
   * side is used more fully — improving angular separation between fanning edges at
   * high-degree nodes (Tier 1.3 of the visual-polish plan).
   *
   * Margin is never shrunk below MIN_ABSOLUTE_PORT_MARGIN_PX so ports don't sit on
   * the node corner. If the side is so small that even zero margin can't fit all
   * ports at minimum spacing, the absolute floor is used and ports are accepted at
   * tighter spacing — same as the legacy behavior, no worse.
   *
   * @param sideLength - Length of the node side (px)
   * @param portCount - Number of ports being distributed on this side
   * @returns Margin to use on each end of the side
   */
  private computePortMargin(sideLength: number, portCount: number): number {
    const baseMargin = sideLength * WebColaCnDGraph.PORT_MARGIN_FRACTION;
    if (portCount <= 1) return baseMargin;

    const baseSpacing = (sideLength - 2 * baseMargin) / portCount;
    if (baseSpacing >= WebColaCnDGraph.MIN_PORT_PERIMETER_SPACING) {
      return baseMargin; // Density is comfortable — keep current behavior.
    }

    // Density is tight: solve for margin that gives exactly MIN_PORT_PERIMETER_SPACING.
    // (sideLength - 2*margin) / portCount >= MIN  →  margin <= (sideLength - portCount*MIN) / 2
    const desiredMargin =
      (sideLength - portCount * WebColaCnDGraph.MIN_PORT_PERIMETER_SPACING) / 2;
    return Math.max(WebColaCnDGraph.MIN_ABSOLUTE_PORT_MARGIN_PX, desiredMargin);
  }

  /**
   * Adjusts route start/end points based on port assignments so that edges sharing
   * the same node side are spread across it rather than all exiting from the center.
   * This applies to ALL edges (not just multi-edges between the same pair), so that
   * e.g. d→b and a→b arriving at b's top side are spread apart.
   *
   * Only adjusts endpoints when port info has been stamped by sortEdgePortsByAngle().
   * Self-loops and group edges are excluded (handled by their own routing).
   */
  private applyPortBasedEndpoints(
    edgeData: any,
    route: Array<{ x: number; y: number }>
  ): Array<{ x: number; y: number }> {
    if (!route || route.length < 2) return route;
    // Skip self-loops (already handled)
    if (edgeData.source.id === edgeData.target.id) return route;

    const hasSourcePort = edgeData._sourcePortIndex !== undefined &&
                          edgeData._sourcePortCount !== undefined &&
                          edgeData._sourcePortCount > 1;
    const hasTargetPort = edgeData._targetPortIndex !== undefined &&
                          edgeData._targetPortCount !== undefined &&
                          edgeData._targetPortCount > 1;

    if (!hasSourcePort && !hasTargetPort) return route;

    // Determine edge direction for figuring out which axis to distribute along
    const dx = (edgeData.target.x || 0) - (edgeData.source.x || 0);
    const dy = (edgeData.target.y || 0) - (edgeData.source.y || 0);
    const angle = Math.atan2(dy, dx);
    const direction = this.getDominantDirection(angle);

    if (hasSourcePort) {
      const bounds = this.normalizeNodeBounds(edgeData.source);
      const portIndex = edgeData._sourcePortIndex;
      const portCount = edgeData._sourcePortCount;

      if (direction === 'right' || direction === 'left') {
        // Edge goes horizontal: distribute source Y along the exit side
        const sideLength = bounds.height();
        const margin = this.computePortMargin(sideLength, portCount);
        const usable = sideLength - 2 * margin;
        route[0] = { ...route[0], y: bounds.y + margin + (portIndex + 0.5) * usable / portCount };
      } else if (direction === 'up' || direction === 'down') {
        // Edge goes vertical: distribute source X along the exit side
        const sideLength = bounds.width();
        const margin = this.computePortMargin(sideLength, portCount);
        const usable = sideLength - 2 * margin;
        route[0] = { ...route[0], x: bounds.x + margin + (portIndex + 0.5) * usable / portCount };
      }
    }

    if (hasTargetPort) {
      const bounds = this.normalizeNodeBounds(edgeData.target);
      const portIndex = edgeData._targetPortIndex;
      const portCount = edgeData._targetPortCount;
      // Target side is opposite to edge direction
      const targetDir = direction === 'right' ? 'left' : direction === 'left' ? 'right' : direction === 'up' ? 'down' : 'up';

      if (targetDir === 'right' || targetDir === 'left') {
        const sideLength = bounds.height();
        const margin = this.computePortMargin(sideLength, portCount);
        const usable = sideLength - 2 * margin;
        const last = route.length - 1;
        route[last] = { ...route[last], y: bounds.y + margin + (portIndex + 0.5) * usable / portCount };
      } else if (targetDir === 'up' || targetDir === 'down') {
        const sideLength = bounds.width();
        const margin = this.computePortMargin(sideLength, portCount);
        const usable = sideLength - 2 * margin;
        const last = route.length - 1;
        route[last] = { ...route[last], x: bounds.x + margin + (portIndex + 0.5) * usable / portCount };
      }
    }

    return route;
  }

  /**
   * Grid-mode variant of applyPortBasedEndpoints: shifts the route's first and
   * last points to port-distributed positions on the *visible* node boundary,
   * inserting an L-bend waypoint when needed so the entry/exit segments stay
   * orthogonal. Without this, two parallel edges between the same node pair
   * would share endpoints and overlap.
   */
  private applyPortBasedEndpointsOrthogonal(
    edgeData: any,
    route: Array<{ x: number; y: number }>
  ): Array<{ x: number; y: number }> {
    if (!route || route.length < 2) return route;
    if (edgeData?.source?.id === edgeData?.target?.id) return route;

    const hasSourcePort = edgeData._sourcePortIndex !== undefined &&
                          edgeData._sourcePortCount !== undefined &&
                          edgeData._sourcePortCount > 1;
    const hasTargetPort = edgeData._targetPortIndex !== undefined &&
                          edgeData._targetPortCount !== undefined &&
                          edgeData._targetPortCount > 1;

    if (!hasSourcePort && !hasTargetPort) return route;

    let result = route.slice();

    if (hasSourcePort) {
      result = this.shiftRouteEndpointToPort(
        result, edgeData.source,
        edgeData._sourcePortIndex, edgeData._sourcePortCount,
        'start'
      );
    }
    if (hasTargetPort) {
      result = this.shiftRouteEndpointToPort(
        result, edgeData.target,
        edgeData._targetPortIndex, edgeData._targetPortCount,
        'end'
      );
    }

    return result;
  }

  /**
   * Shift the first or last route point to a port location on the visible node
   * boundary, preserving orthogonality of the entry/exit segment by inserting
   * an L-bend waypoint when necessary.
   */
  private shiftRouteEndpointToPort(
    route: Array<{ x: number; y: number }>,
    node: any,
    portIndex: number,
    portCount: number,
    which: 'start' | 'end'
  ): Array<{ x: number; y: number }> {
    if (route.length < 2) return route;
    const visible = this.getVisibleBounds(node);
    if (!visible) return route;

    const idx = which === 'start' ? 0 : route.length - 1;
    const neighborIdx = which === 'start' ? 1 : route.length - 2;
    const endpoint = route[idx];
    const neighbor = route[neighborIdx];

    const bx = visible.x;
    const bX = visible.X;
    const by = visible.y;
    const bY = visible.Y;
    const w = bX - bx;
    const h = bY - by;
    const eps = 1;

    let side: 'left' | 'right' | 'top' | 'bottom';
    const dLeft = Math.abs(endpoint.x - bx);
    const dRight = Math.abs(endpoint.x - bX);
    const dTop = Math.abs(endpoint.y - by);
    const dBottom = Math.abs(endpoint.y - bY);
    const minD = Math.min(dLeft, dRight, dTop, dBottom);
    if (minD === dLeft) side = 'left';
    else if (minD === dRight) side = 'right';
    else if (minD === dTop) side = 'top';
    else side = 'bottom';

    const sideLength = (side === 'left' || side === 'right') ? h : w;
    if (sideLength <= 0) return route;
    const margin = this.computePortMargin(sideLength, portCount);
    const usable = sideLength - 2 * margin;
    const portOffset = margin + (portIndex + 0.5) * usable / portCount;

    let newEndpoint: { x: number; y: number };
    switch (side) {
      case 'left':   newEndpoint = { x: bx, y: by + portOffset }; break;
      case 'right':  newEndpoint = { x: bX, y: by + portOffset }; break;
      case 'top':    newEndpoint = { x: bx + portOffset, y: by }; break;
      case 'bottom': newEndpoint = { x: bx + portOffset, y: bY }; break;
    }

    const result = route.slice();
    const isAxisAligned = Math.abs(newEndpoint.x - neighbor.x) < eps ||
                          Math.abs(newEndpoint.y - neighbor.y) < eps;

    if (isAxisAligned) {
      result[idx] = newEndpoint;
      return result;
    }

    // Need an L-bend to keep both new segments orthogonal.
    let bend: { x: number; y: number };
    if (side === 'left' || side === 'right') {
      // Egress along X; the side direction is Y. Bend at (neighbor.x, port.y).
      bend = { x: neighbor.x, y: newEndpoint.y };
    } else {
      // Egress along Y; the side direction is X. Bend at (port.x, neighbor.y).
      bend = { x: newEndpoint.x, y: neighbor.y };
    }

    if (which === 'start') {
      result[0] = newEndpoint;
      result.splice(1, 0, bend);
    } else {
      result[result.length - 1] = newEndpoint;
      result.splice(result.length - 1, 0, bend);
    }
    return result;
  }

  /**
   * Distributes edge endpoints evenly across a node side using port assignments.
   * For a side of length L with N ports, port i is placed at sideStart + margin + (i + 0.5) * usableLength / N.
   */
  private applyPortBasedOffset(
    route: Array<{ x: number; y: number }>,
    edgeData: any,
    direction: 'right' | 'up' | 'left' | 'down' | null,
    hasSourcePort: boolean,
    hasTargetPort: boolean
  ): void {
    if (hasSourcePort) {
      const bounds = this.normalizeNodeBounds(edgeData.source);
      const portIndex = edgeData._sourcePortIndex;
      const portCount = edgeData._sourcePortCount;

      if (direction === 'right' || direction === 'left') {
        // Edge goes horizontal: distribute along Y axis of the source side
        const sideLength = bounds.height();
        const margin = this.computePortMargin(sideLength, portCount);
        const usable = sideLength - 2 * margin;
        const portY = bounds.y + margin + (portIndex + 0.5) * usable / portCount;
        route[0].y = portY;
      } else if (direction === 'up' || direction === 'down') {
        // Edge goes vertical: distribute along X axis of the source side
        const sideLength = bounds.width();
        const margin = this.computePortMargin(sideLength, portCount);
        const usable = sideLength - 2 * margin;
        const portX = bounds.x + margin + (portIndex + 0.5) * usable / portCount;
        route[0].x = portX;
      }
    }

    if (hasTargetPort) {
      const bounds = this.normalizeNodeBounds(edgeData.target);
      const portIndex = edgeData._targetPortIndex;
      const portCount = edgeData._targetPortCount;
      // For target, direction is reversed from edge direction
      const targetDirection = direction === 'right' ? 'left' : direction === 'left' ? 'right' : direction === 'up' ? 'down' : 'up';

      if (targetDirection === 'right' || targetDirection === 'left') {
        const sideLength = bounds.height();
        const margin = this.computePortMargin(sideLength, portCount);
        const usable = sideLength - 2 * margin;
        const portY = bounds.y + margin + (portIndex + 0.5) * usable / portCount;
        route[route.length - 1].y = portY;
      } else if (targetDirection === 'up' || targetDirection === 'down') {
        const sideLength = bounds.width();
        const margin = this.computePortMargin(sideLength, portCount);
        const usable = sideLength - 2 * margin;
        const portX = bounds.x + margin + (portIndex + 0.5) * usable / portCount;
        route[route.length - 1].x = portX;
      }
    }
  }

  /**
   * Clamp edge offset so dense edge bundles don't explode outward.
   */
  private clampOffset(offset: number, distance: number): number {
    const maxOffset = Math.max(WebColaCnDGraph.MIN_EDGE_DISTANCE, distance * WebColaCnDGraph.MAX_EDGE_OFFSET_RATIO);
    return Math.max(-maxOffset, Math.min(maxOffset, offset));
  }

  /**
   * Calculates total route length to avoid clamping based on a short first segment.
   */
  private getRouteLength(route: Array<{ x: number; y: number }>): number {
    if (route.length < 2) {
      return 0;
    }

    return route.slice(1).reduce((total, point, index) => {
      const prev = route[index];
      const dx = point.x - prev.x;
      const dy = point.y - prev.y;
      return total + Math.sqrt(dx * dx + dy * dy);
    }, 0);
  }

  /**
   * Clamp curvature to avoid excessive bulging for many parallel edges.
   */
  private clampCurvature(curvature: number): number {
    return Math.max(-WebColaCnDGraph.MAX_EDGE_CURVATURE_RATIO, Math.min(WebColaCnDGraph.MAX_EDGE_CURVATURE_RATIO, curvature));
  }

  /**
   * Applies curvature to control points in the route.
   * 
   * @param route - Route points
   * @param curvature - Curvature value
   * @param angle - Edge angle
   * @param distance - Edge distance
   * @returns Route with curvature applied
   */
  private applyCurvatureToRoute(route: Array<{ x: number; y: number }>, curvature: number, angle: number, distance: number): Array<{ x: number; y: number }> {
    if (curvature === 0) return route;

    route.forEach((point, index) => {
      if (index > 0 && index < route.length - 1) {
        const offsetX = curvature * Math.abs(Math.sin(angle)) * distance;
        const offsetY = curvature * Math.abs(Math.cos(angle)) * distance;
        
        point.x += offsetX;
        point.y += offsetY;
      }
    });

    return route;
  }

  /**
   * Gets the dominant direction of an edge based on its angle.
   * 
   * @param angle - Edge angle in radians
   * @returns Dominant direction string
   */
  private getDominantDirection(angle: number): 'right' | 'up' | 'left' | 'down' | null {
    // Normalize angle between -π and π
    angle = ((angle + Math.PI) % (2 * Math.PI)) - Math.PI;

    if (angle >= -Math.PI / 4 && angle <= Math.PI / 4) {
      return 'right';
    } else if (angle > Math.PI / 4 && angle < 3 * Math.PI / 4) {
      return 'up';
    } else if (angle >= 3 * Math.PI / 4 || angle <= -3 * Math.PI / 4) {
      return 'left';
    } else if (angle > -3 * Math.PI / 4 && angle < -Math.PI / 4) {
      return 'down';
    }
    
    return null;
  }

  /**
   * Finds the closest point on a rectangle to a given point.
   * 
   * @param bounds - Rectangle bounds with x, y, X, Y properties
   * @param point - Point to find closest position for
   * @returns Closest point on rectangle perimeter
   */
  private closestPointOnRect(bounds: any, point: { x: number; y: number }): { x: number; y: number } {
    if (!bounds) return point;

    const { x, y, X, Y } = bounds;
    const closestX = Math.max(x, Math.min(point.x, X));
    const closestY = Math.max(y, Math.min(point.y, Y));

    return { x: closestX, y: closestY };
  }

  /**
   * Build a bounds-like object describing the *visible* rectangle of a node or
   * group. WebCola inflates `node.width/height` for collision avoidance, and
   * `group.bounds` covers the inflated container rectangle — both are larger
   * than what is actually rendered. Edges should clip to the rendered boundary
   * so arrowheads land exactly on it instead of behind a fill that draws over
   * them.
   *
   * Returns null if visible dimensions can't be derived.
   */
  private getVisibleBounds(node: any): {
    cx: () => number;
    cy: () => number;
    width: () => number;
    height: () => number;
    x: number;
    X: number;
    y: number;
    Y: number;
  } | null {
    if (!node) return null;
    const cx = node.x ?? (node.bounds && typeof node.bounds.cx === 'function' ? node.bounds.cx() : undefined);
    const cy = node.y ?? (node.bounds && typeof node.bounds.cy === 'function' ? node.bounds.cy() : undefined);
    if (cx === undefined || cy === undefined) return null;

    let hw: number | undefined;
    let hh: number | undefined;

    if (node.visualWidth !== undefined || node.visualHeight !== undefined) {
      hw = (node.visualWidth ?? node.width ?? 0) / 2;
      hh = (node.visualHeight ?? node.height ?? 0) / 2;
    } else if (Array.isArray(node.leaves) || Array.isArray(node.groups)) {
      // Group container — visible rectangle is bounds inset by GROUP_VISUAL_MARGIN_PX
      // (matches the bounds.inflate(-groupMargin) used at routing time).
      if (node.bounds && typeof node.bounds.width === 'function') {
        hw = node.bounds.width() / 2 - WebColaCnDGraph.GROUP_VISUAL_MARGIN_PX;
        hh = node.bounds.height() / 2 - WebColaCnDGraph.GROUP_VISUAL_MARGIN_PX;
      } else if (node.bounds && node.bounds.X !== undefined) {
        hw = (node.bounds.X - node.bounds.x) / 2 - WebColaCnDGraph.GROUP_VISUAL_MARGIN_PX;
        hh = (node.bounds.Y - node.bounds.y) / 2 - WebColaCnDGraph.GROUP_VISUAL_MARGIN_PX;
      }
    } else if (node.width !== undefined || node.height !== undefined) {
      hw = (node.width ?? 0) / 2;
      hh = (node.height ?? 0) / 2;
    }

    if (hw === undefined || hh === undefined || (hw <= 0 && hh <= 0)) return null;
    hw = Math.max(0, hw);
    hh = Math.max(0, hh);

    return {
      cx: () => cx,
      cy: () => cy,
      width: () => hw! * 2,
      height: () => hh! * 2,
      x: cx - hw,
      X: cx + hw,
      y: cy - hh,
      Y: cy + hh,
    };
  }

  /**
   * Calculates a stable anchor point on a rectangle's perimeter for edge drawing.
   * This method produces consistent, jitter-free anchor points by using the
   * center of the rectangle edge that faces the target point.
   * 
   * Unlike intersection-based approaches that can jump erratically as rectangles
   * move, this method selects one of four edge centers (top, bottom, left, right)
   * based on the dominant direction to the target, producing smooth transitions.
   * 
   * @param bounds - Rectangle bounds with x, y, X, Y properties (or cx(), cy(), width(), height() methods)
   * @param targetPoint - The point the edge is connecting to
   * @returns Stable anchor point on the rectangle's perimeter
   */
  private getStableEdgeAnchor(bounds: any, targetPoint: { x: number; y: number }): { x: number; y: number } {
    if (!bounds) return targetPoint;

    // Get rectangle center and dimensions
    let cx: number, cy: number, halfWidth: number, halfHeight: number;
    
    if (typeof bounds.cx === 'function') {
      // WebCola Rectangle with methods
      cx = bounds.cx();
      cy = bounds.cy();
      halfWidth = bounds.width() / 2;
      halfHeight = bounds.height() / 2;
    } else if (bounds.x !== undefined && bounds.X !== undefined) {
      // Rectangle with x, y, X, Y properties
      cx = (bounds.x + bounds.X) / 2;
      cy = (bounds.y + bounds.Y) / 2;
      halfWidth = (bounds.X - bounds.x) / 2;
      halfHeight = (bounds.Y - bounds.y) / 2;
    } else {
      return targetPoint;
    }

    // Calculate direction from rectangle center to target
    const dx = targetPoint.x - cx;
    const dy = targetPoint.y - cy;

    // Determine which edge to anchor to based on the dominant direction
    // Use aspect-ratio-normalized comparison for accurate edge selection
    const normalizedDx = Math.abs(dx) / halfWidth;
    const normalizedDy = Math.abs(dy) / halfHeight;

    if (normalizedDx > normalizedDy) {
      // Horizontal edge - left or right
      if (dx > 0) {
        // Right edge
        return { x: cx + halfWidth, y: cy };
      } else {
        // Left edge
        return { x: cx - halfWidth, y: cy };
      }
    } else {
      // Vertical edge - top or bottom
      if (dy > 0) {
        // Bottom edge
        return { x: cx, y: cy + halfHeight };
      } else {
        // Top edge
        return { x: cx, y: cy - halfHeight };
      }
    }
  }

  /**
   * Calculates stable edge path points for drawing during tick/drag operations.
   * This method avoids jitter by using stable anchor points instead of
   * dynamic intersection calculations.
   * 
   * @param source - Source node or group with bounds
   * @param target - Target node or group with bounds
   * @returns Array of two points for a simple line path
   */
  private getStableEdgePath(
    source: any,
    target: any,
    edgeData?: any
  ): Array<{ x: number; y: number }> {
    // Always use node.x/y as the center — these are the authoritative positions
    // from the WebCola solver and are always in sync, whereas bounds.cx()/cy()
    // lag slightly and can reflect the inflated collision rectangle center.
    const sourceCenter: { x: number; y: number } = { x: source.x || 0, y: source.y || 0 };
    const targetCenter: { x: number; y: number } = { x: target.x || 0, y: target.y || 0 };

    // Prefer visible bounds (visualWidth/visualHeight for plain nodes, bounds-inset
    // for groups). Falling back to WebCola bounds would land arrowheads on the
    // INFLATED rectangle, behind the rendered fill.
    const sourceBounds = this.getVisibleBounds(source) ?? source.bounds ?? source.innerBounds;
    const targetBounds = this.getVisibleBounds(target) ?? target.bounds ?? target.innerBounds;

    // Calculate stable anchor points on the perimeter
    let sourceAnchor = sourceBounds
      ? this.getStableEdgeAnchor(sourceBounds, targetCenter)
      : sourceCenter;

    let targetAnchor = targetBounds
      ? this.getStableEdgeAnchor(targetBounds, sourceCenter)
      : targetCenter;

    // Apply port-based distribution if port info is available on the edge.
    // This spreads multiple edges sharing the same node side during drag/tick,
    // preventing them from overlapping at the side center.
    if (edgeData && sourceBounds) {
      sourceAnchor = this.applyPortOffsetToAnchor(
        sourceAnchor, sourceBounds, edgeData._sourcePortIndex, edgeData._sourcePortCount,
        sourceCenter, targetCenter
      );
    }
    if (edgeData && targetBounds) {
      targetAnchor = this.applyPortOffsetToAnchor(
        targetAnchor, targetBounds, edgeData._targetPortIndex, edgeData._targetPortCount,
        targetCenter, sourceCenter
      );
    }

    return [sourceAnchor, targetAnchor];
  }

  /**
   * Adjusts an anchor point along a node side to reflect port assignment.
   * If no port info is available or there's only one port, returns the original anchor.
   */
  private applyPortOffsetToAnchor(
    anchor: { x: number; y: number },
    bounds: any,
    portIndex: number | undefined,
    portCount: number | undefined,
    nodeCenter: { x: number; y: number },
    remoteCenter: { x: number; y: number }
  ): { x: number; y: number } {
    if (portIndex === undefined || portCount === undefined || portCount <= 1) {
      return anchor;
    }

    // Determine which side the anchor is on by comparing to bounds
    const bx = typeof bounds.x === 'number' ? bounds.x : 0;
    const bX = bounds.X !== undefined ? bounds.X : bx + (typeof bounds.width === 'function' ? bounds.width() : 0);
    const by = typeof bounds.y === 'number' ? bounds.y : 0;
    const bY = bounds.Y !== undefined ? bounds.Y : by + (typeof bounds.height === 'function' ? bounds.height() : 0);
    const w = bX - bx;
    const h = bY - by;

    const eps = 1;

    if (Math.abs(anchor.x - bx) < eps || Math.abs(anchor.x - bX) < eps) {
      // Anchor is on left or right side → distribute along Y
      const margin = this.computePortMargin(h, portCount);
      const usable = h - 2 * margin;
      const portY = by + margin + (portIndex + 0.5) * usable / portCount;
      return { x: anchor.x, y: portY };
    } else if (Math.abs(anchor.y - by) < eps || Math.abs(anchor.y - bY) < eps) {
      // Anchor is on top or bottom side → distribute along X
      const margin = this.computePortMargin(w, portCount);
      const usable = w - 2 * margin;
      const portX = bx + margin + (portIndex + 0.5) * usable / portCount;
      return { x: portX, y: anchor.y };
    }

    return anchor;
  }

  /**
   * Adjusts a point to lie on the perimeter of a rectangle.
   * 
   * @param point - Point to adjust
   * @param bounds - Rectangle bounds
   * @returns Point on rectangle perimeter
   */
  private adjustPointToRectanglePerimeter(point: { x: number; y: number }, bounds: any): { x: number; y: number } {
    if (!bounds) return point;

    // Implementation would adjust point to rectangle edge
    // This is a simplified version - full implementation would calculate
    // the exact perimeter intersection
    return this.closestPointOnRect(bounds, point);
  }

  /**
   * Updates link label positions after edge routing is complete.
   *
   * Pipeline:
   *   1. Position each label at the midpoint of its routed path.
   *   2. Run a batch overlap-resolution pass that pushes overlapping labels
   *      apart along the smaller-overlap axis (Tier 1.1 of the visual-polish
   *      plan — see resolveLinkLabelOverlaps).
   *   3. Raise labels above edges so the white halo from `paint-order: stroke
   *      fill` reads correctly.
   */
  private updateLinkLabelsAfterRouting(): void {
    this.container.selectAll('.link-group .linklabel')
      .attr('x', (d: any) => {
        const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${d.id}"]`) as SVGPathElement;
        if (!pathElement) return 0;

        const pathLength = pathElement.getTotalLength();
        const midpoint = pathElement.getPointAtLength(pathLength / 2);
        return midpoint.x;
      })
      .attr('y', (d: any) => {
        const pathElement = this.shadowRoot?.querySelector(`path[data-link-id="${d.id}"]`) as SVGPathElement;
        if (!pathElement) return 0;

        const pathLength = pathElement.getTotalLength();
        const midpoint = pathElement.getPointAtLength(pathLength / 2);
        return midpoint.y;
      })
      .attr('text-anchor', 'middle');

    this.resolveLinkLabelOverlaps();

    this.container.selectAll('.link-group .linklabel').raise();
  }

  /**
   * Pushes overlapping link labels apart so they don't stack on top of each
   * other when multiple parallel edges or dense regions place several labels
   * at the same midpoint (Tier 1.1 of the visual-polish plan).
   *
   * Algorithm: minimum-translation-vector (MTV) pairwise pushaway, repeated
   * for up to MAX_LABEL_OVERLAP_PASSES passes. For each overlapping pair, both
   * labels move half the overlap (plus a small margin) along the *smaller*
   * overlap axis — the axis where moving disrupts position the least.
   *
   * Per-label cumulative displacement is capped at MAX_LABEL_DISPLACEMENT_PX
   * so labels never drift far from their edge midpoints. If an overlap can't
   * be resolved within the cap, we accept the residual rather than fly the
   * label off the canvas.
   *
   * Cost: O(N² × passes) where N = number of labels. Called once per layout
   * after routing, not per-tick — fine for typical graphs (≤ a few hundred
   * labels). For sparse graphs the inner loop short-circuits on `isOverlapping`.
   */
  private resolveLinkLabelOverlaps(): void {
    const labels = Array.from(
      this.container.selectAll<SVGTextElement, any>('.link-group .linklabel').nodes()
    );
    if (labels.length < 2) return;

    const MAX_PASSES = 4;
    const MAX_DISPLACEMENT_PX = 28;
    const MARGIN_PX = 2;

    // Track cumulative displacement per label so we can clamp to MAX_DISPLACEMENT_PX.
    const dxMap = new Map<SVGTextElement, number>();
    const dyMap = new Map<SVGTextElement, number>();

    for (let pass = 0; pass < MAX_PASSES; pass++) {
      let resolvedAny = false;

      for (let i = 0; i < labels.length; i++) {
        const a = labels[i];
        for (let j = i + 1; j < labels.length; j++) {
          const b = labels[j];
          if (!isOverlapping(a, b)) continue;

          const ba = a.getBBox();
          const bb = b.getBBox();

          const cax = ba.x + ba.width / 2;
          const cay = ba.y + ba.height / 2;
          const cbx = bb.x + bb.width / 2;
          const cby = bb.y + bb.height / 2;

          const overlapX = (ba.width + bb.width) / 2 - Math.abs(cax - cbx);
          const overlapY = (ba.height + bb.height) / 2 - Math.abs(cay - cby);
          if (overlapX <= 0 || overlapY <= 0) continue;

          // Push along the axis with the smaller overlap (less movement).
          // For identical-position labels the sign falls out of the >= compare,
          // giving a deterministic split.
          if (overlapY <= overlapX) {
            const push = (overlapY + MARGIN_PX) / 2;
            const sign = cay >= cby ? 1 : -1;
            this.applyLabelDisplacement(a, 0, sign * push, dxMap, dyMap, MAX_DISPLACEMENT_PX);
            this.applyLabelDisplacement(b, 0, -sign * push, dxMap, dyMap, MAX_DISPLACEMENT_PX);
          } else {
            const push = (overlapX + MARGIN_PX) / 2;
            const sign = cax >= cbx ? 1 : -1;
            this.applyLabelDisplacement(a, sign * push, 0, dxMap, dyMap, MAX_DISPLACEMENT_PX);
            this.applyLabelDisplacement(b, -sign * push, 0, dxMap, dyMap, MAX_DISPLACEMENT_PX);
          }
          resolvedAny = true;
        }
      }

      if (!resolvedAny) break;
    }
  }

  /**
   * Applies a displacement delta to a label's x/y attributes, clamping the
   * cumulative displacement magnitude to `cap`. If the new cumulative would
   * exceed the cap, the displacement is scaled down so the magnitude lands
   * exactly on the cap boundary.
   */
  private applyLabelDisplacement(
    label: SVGTextElement,
    deltaX: number,
    deltaY: number,
    dxMap: Map<SVGTextElement, number>,
    dyMap: Map<SVGTextElement, number>,
    cap: number
  ): void {
    const curDx = dxMap.get(label) || 0;
    const curDy = dyMap.get(label) || 0;
    let newDx = curDx + deltaX;
    let newDy = curDy + deltaY;

    const mag = Math.hypot(newDx, newDy);
    if (mag > cap) {
      const scale = cap / mag;
      newDx *= scale;
      newDy *= scale;
    }

    const sel = d3.select(label);
    const x = parseFloat(sel.attr('x') || '0') + (newDx - curDx);
    const y = parseFloat(sel.attr('y') || '0') + (newDy - curDy);
    sel.attr('x', x).attr('y', y);

    dxMap.set(label, newDx);
    dyMap.set(label, newDy);
  }

  /**
   * Fits the viewport to show all content with appropriate zoom and pan.
   * Uses D3 zoom transform for smooth, consistent behavior.
   * Only performs fit if:
   * - This is the initial render, OR
   * - User has not manually zoomed/panned, OR
   * - Force parameter is true (e.g., from reset button)
   * 
   * @param force - If true, fit regardless of user interaction state
   */
  private fitViewportToContent(force: boolean = false): void {
    const svgElement = this.svg?.node();
    if (!svgElement || !this.zoomBehavior) return;
    
    // Skip if user has manually zoomed and this isn't the initial render or forced
    if (this.userHasManuallyZoomed && !this.isInitialRender && !force) {
      return;
    }

    // Calculate content bounds
    const bounds = this.calculateContentBounds();
    if (!bounds) return;
    
    // Get container dimensions
    const containerWidth = svgElement.clientWidth || svgElement.parentElement?.clientWidth || 800;
    const containerHeight = svgElement.clientHeight || svgElement.parentElement?.clientHeight || 600;
    
    // Calculate padding
    const padding = WebColaCnDGraph.VIEWBOX_PADDING * 4; // Increase padding for comfortable view
    
    // Calculate scale to fit content
    const scaleX = (containerWidth - padding * 2) / bounds.width;
    const scaleY = (containerHeight - padding * 2) / bounds.height;
    const scale = Math.min(scaleX, scaleY, 1); // Don't zoom in beyond 1:1
    
    // Clamp scale to zoom extent
    const [minScale, maxScale] = this.zoomBehavior.scaleExtent();
    const clampedScale = Math.max(minScale, Math.min(maxScale, scale));
    
    // Calculate center of content
    const contentCenterX = bounds.x + bounds.width / 2;
    const contentCenterY = bounds.y + bounds.height / 2;
    
    // Calculate translation to center content
    const translateX = containerWidth / 2 - contentCenterX * clampedScale;
    const translateY = containerHeight / 2 - contentCenterY * clampedScale;
    
    // Create the transform
    const transform = d3.zoomIdentity
      .translate(translateX, translateY)
      .scale(clampedScale);
    
    // Apply with smooth transition (or instant on initial render)
    if (this.isInitialRender) {
      // Instant on first render
      this.svg.call(this.zoomBehavior.transform, transform);
      this.isInitialRender = false;
    } else {
      // Smooth transition for subsequent fits
      this.svg.transition()
        .duration(300)
        .ease(d3.easeCubicOut)
        .call(this.zoomBehavior.transform, transform);
    }
    
    // Update control states after transform
    this.updateZoomControlStates();
  }
  
  /**
   * Resets the view to fit all content, clearing user zoom state.
   * Called when user clicks the reset/fit button.
   */
  public resetViewToFitContent(): void {
    this.userHasManuallyZoomed = false;
    this.fitViewportToContent(true);
  }

  /**
   * Manually calculates the bounding box of all content to ensure accurate viewport fitting.
   * This method examines all nodes, edges, and groups to determine the true content bounds.
   * @returns Bounding box with x, y, width, height properties or null if calculation fails
   */
  private calculateContentBounds(): { x: number; y: number; width: number; height: number } | null {
    try {
      if (!this.currentLayout || !this.container) return null;

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      const updateBounds = (x0: number, x1: number, y0: number, y1: number): void => {
        if (![x0, x1, y0, y1].every(Number.isFinite)) return;
        minX = Math.min(minX, x0);
        maxX = Math.max(maxX, x1);
        minY = Math.min(minY, y0);
        maxY = Math.max(maxY, y1);
      };

      // Check all nodes
      const nodes = this.currentLayout.nodes;
      if (nodes && nodes.length > 0) {
        nodes.forEach((node: NodeWithMetadata) => {
          if (this.isHiddenNode(node)) return;
          if (typeof node.x !== 'number' || typeof node.y !== 'number') return;

          // WebCola node positions are centered at (x, y).
          const nodeWidth = (node as any).visualWidth ?? node.width ?? 0;
          const nodeHeight = (node as any).visualHeight ?? node.height ?? 0;
          const halfWidth = nodeWidth / 2;
          const halfHeight = nodeHeight / 2;
          updateBounds(
            node.x - halfWidth,
            node.x + halfWidth,
            node.y - halfHeight,
            node.y + halfHeight
          );
        });
      }

      const self = this;

      // Check rendered (non-alignment) edge paths.
      const edgePaths = this.container.selectAll('.link-group path');
      if (!edgePaths.empty()) {
        edgePaths.each(function(this: SVGPathElement, edgeData: any) {
          if (edgeData?.id && self.isAlignmentEdge(edgeData)) return;
          try {
            const bbox = this.getBBox();
            if (bbox.width > 0 || bbox.height > 0) {
              updateBounds(
                bbox.x,
                bbox.x + bbox.width,
                bbox.y,
                bbox.y + bbox.height
              );
            }
          } catch (e) {
            // Skip elements that can't provide bbox
          }
        });
      }

      // Check all node groups by examining actual DOM elements
      const nodeGroups = this.container.selectAll('.node, .error-node');
      if (!nodeGroups.empty()) {
        nodeGroups.each(function(this: SVGGElement, nodeData: NodeWithMetadata) {
          if (self.isHiddenNode(nodeData)) return;
          try {
            const bbox = this.getBBox();
            if (bbox.width > 0 || bbox.height > 0) {
              updateBounds(
                bbox.x,
                bbox.x + bbox.width,
                bbox.y,
                bbox.y + bbox.height
              );
            }
          } catch (e) {
            // Skip elements that can't provide bbox
          }
        });
      }

      // Check all text elements separately for better text bounds calculation
      const textElements = this.container.selectAll('text');
      if (!textElements.empty()) {
        textElements.each(function(this: SVGTextElement, boundData: any) {
          if (
            boundData &&
            (typeof boundData.name === 'string' || typeof boundData.id === 'string') &&
            self.isHiddenNode(boundData)
          ) {
            return;
          }

          try {
            const bbox = this.getBBox();
            if (bbox.width > 0 || bbox.height > 0) {
              // Add extra padding for text elements due to font metrics
              const textPadding = 5;
              updateBounds(
                bbox.x - textPadding,
                bbox.x + bbox.width + textPadding,
                bbox.y - textPadding,
                bbox.y + bbox.height + textPadding
              );
            }
          } catch (e) {
            // Skip elements that can't provide bbox
          }
        });
      }

      // Check for any group elements
      const groupElements = this.container.selectAll('.group');
      if (!groupElements.empty()) {
        groupElements.each(function(this: SVGGElement) {
          try {
            const bbox = this.getBBox();
            if (bbox.width > 0 || bbox.height > 0) {
              updateBounds(
                bbox.x,
                bbox.x + bbox.width,
                bbox.y,
                bbox.y + bbox.height
              );
            }
          } catch (e) {
            // Skip elements that can't provide bbox
          }
        });
      }

      // Return null if no valid bounds were found
      if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
        console.warn('Could not calculate content bounds - no valid elements found');
        return null;
      }

      const bounds = {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY
      };
      
      return bounds;

    } catch (error) {
      console.error('Error calculating content bounds:', error);
      return null;
    }
  }

  /**
   * Dispatches a custom event when relations become available after layout rendering.
   * Includes all available relations in the event detail for external listeners.
   * 
   * @private
   */
  private dispatchRelationsAvailableEvent(): void {
    // Get all available relations
    const relations = this.getAllRelations();
    
    // Create custom event with comprehensive details
    const event = new CustomEvent('relations-available', {
      detail: {
        relations: relations,           // Yes, include all relations
        count: relations.length,        // Convenient count property
        timestamp: Date.now(),          // When the event was created
        graphId: this.id || 'unknown'   // Which graph instance
      },
      bubbles: true,    // Allow event to bubble up the DOM tree
      cancelable: true  // Allow event to be cancelled by listeners
    });

    // Dispatch the event from this element
    this.dispatchEvent(event);
    
    // console.log('🎯 Dispatched relations-available event:', {
    //   relations,
    //   count: relations.length
    // });
  }

  /** Public API for relation highlighting */

  /**
   * Gets all unique relation names from the current layout.
   * @returns An array of the set of relation names in the current layout.
   */
  public getAllRelations(): string[] {
    if (!this.currentLayout?.links) return [];

    const relNames = new Set(
      this.currentLayout.links
        .filter((edge: any) => !this.isAlignmentEdge(edge))
        .map((edge: any) => edge.relName)
        .filter(Boolean)
    );

    return Array.from(relNames);
  }

  /**
   * Highlights all the links or inferred links by its relation name.
   * @param relName - The name of the relation to highlight
   * @returns True if the relation was successfully highlighted, false otherwise
   */
  public highlightRelation(relName: string): boolean {
    if (!this.currentLayout?.links) return false;

    (this.svgLinkGroups as d3.Selection<SVGGElement, any, any, unknown>)
      .filter((d) => d.relName === relName && !this.isAlignmentEdge(d))
      .selectAll('path')
      .classed('highlighted', true);
    
    return true;
  }

  /**
   * Clears highlighting of the given relation name.
   * @param relName - The name of the relation to clear highlighting for
   * @returns True if the relation highlighting was successfully cleared, false otherwise
   */
  public clearHighlightRelation(relName: string): boolean {
    if (!this.currentLayout?.links) return false;

    (this.svgLinkGroups as d3.Selection<SVGGElement, any, any, unknown>)
      .filter((d) => d.relName === relName && !this.isAlignmentEdge(d))
      .selectAll('path')
      .classed('highlighted', false);
    
    return true;
  }

  /** Public API for node highlighting */

  /**
   * Highlights nodes based on their IDs (for unary selector results).
   * This is useful for visualizing the results of a selector expression.
   * 
   * @param nodeIds - Array of node IDs to highlight (e.g., from evaluator.selectedAtoms())
   * @returns True if any nodes were highlighted, false otherwise
   * 
   * @example
   * ```typescript
   * const result = evaluator.evaluate('Student');
   * const nodeIds = result.selectedAtoms();
   * graph.highlightNodes(nodeIds);
   * ```
   */
  public highlightNodes(nodeIds: string[]): boolean {
    if (!this.currentLayout?.nodes || !this.svgNodes) return false;
    if (!nodeIds || nodeIds.length === 0) return false;

    const nodeIdSet = new Set(nodeIds);
    let highlighted = false;

    (this.svgNodes as d3.Selection<SVGGElement, any, any, unknown>)
      .each((d: any, i: number, nodes: any[]) => {
        if (nodeIdSet.has(d.id)) {
          d3.select(nodes[i]).classed('highlighted', true);
          highlighted = true;
        }
      });
    
    return highlighted;
  }

  /**
   * Highlights node pairs based on binary selector results.
   * Shows visual correspondence between first and second elements using different colors.
   * 
   * Note: If a node appears in multiple pairs with different roles (both first and second),
   * it will receive both 'highlighted-first' and 'highlighted-second' classes, and if badges
   * are enabled, only the last badge will be visible (this is intentional to avoid cluttering).
   * 
   * @param nodePairs - Array of [first, second] node ID pairs (e.g., from evaluator.selectedTwoples())
   * @param options - Optional configuration for highlighting
   * @param options.showBadges - If true, shows "1" and "2" badges on nodes (default: false)
   * @returns True if any nodes were highlighted, false otherwise
   * 
   * @example
   * ```typescript
   * const result = evaluator.evaluate('friend');
   * const pairs = result.selectedTwoples(); // [["Alice", "Bob"], ["Charlie", "Diana"]]
   * graph.highlightNodePairs(pairs, { showBadges: true });
   * ```
   */
  public highlightNodePairs(
    nodePairs: string[][], 
    options: { showBadges?: boolean } = {}
  ): boolean {
    if (!this.currentLayout?.nodes || !this.svgNodes) return false;
    if (!nodePairs || nodePairs.length === 0) return false;

    const { showBadges = false } = options;
    
    // Build sets of first and second node IDs
    const firstNodeIds = new Set<string>();
    const secondNodeIds = new Set<string>();
    
    // Validate and process pairs
    nodePairs.forEach((pair, index) => {
      if (!Array.isArray(pair)) {
        console.warn(`highlightNodePairs: Pair at index ${index} is not an array, skipping`);
        return;
      }
      if (pair.length !== 2) {
        console.warn(`highlightNodePairs: Pair at index ${index} has ${pair.length} elements (expected 2), skipping`);
        return;
      }
      
      const [first, second] = pair;
      if (first) firstNodeIds.add(first);
      if (second) secondNodeIds.add(second);
    });

    let highlighted = false;

    (this.svgNodes as d3.Selection<SVGGElement, any, any, unknown>)
      .each((d: any, i: number, nodes: any[]) => {
        const nodeGroup = d3.select(nodes[i]);
        
        // Check if this node is a first element
        if (firstNodeIds.has(d.id)) {
          nodeGroup.classed('highlighted-first', true);
          highlighted = true;
          
          if (showBadges) {
            this.addHighlightBadge(nodeGroup, d, '1', '#007aff');
          }
        }
        
        // Check if this node is a second element
        if (secondNodeIds.has(d.id)) {
          nodeGroup.classed('highlighted-second', true);
          highlighted = true;
          
          if (showBadges) {
            // For nodes that are both first and second, show a combined badge
            if (firstNodeIds.has(d.id)) {
              this.addHighlightBadge(nodeGroup, d, '1,2', '#9B59B6'); // Purple for dual role
            } else {
              this.addHighlightBadge(nodeGroup, d, '2', '#ff3b30');
            }
          }
        }
      });
    
    return highlighted;
  }

  /**
   * Clears all node highlights (both unary and binary).
   * 
   * @returns True if the operation completed successfully
   */
  public clearNodeHighlights(): boolean {
    if (!this.svgNodes) return false;

    (this.svgNodes as d3.Selection<SVGGElement, any, any, unknown>)
      .classed('highlighted', false)
      .classed('highlighted-first', false)
      .classed('highlighted-second', false)
      .selectAll('.highlight-badge, .highlight-badge-bg')
      .remove();
    
    return true;
  }

  /**
   * Adds a visual badge to a highlighted node to show first/second correspondence.
   * 
   * @param nodeGroup - D3 selection of the node group
   * @param nodeData - Node data containing position and dimensions
   * @param label - Badge label text ('1' or '2')
   * @param color - Badge background color
   */
  private addHighlightBadge(
    nodeGroup: any, 
    nodeData: any, 
    label: string, 
    color: string
  ): void {
    // Remove any existing badges first
    nodeGroup.selectAll('.highlight-badge, .highlight-badge-bg').remove();

    const badgeSize = 16;
    const padding = 4;
    
    // Position badge at top-right corner of the node
    const badgeX = (nodeData.width || 0) / 2 - badgeSize / 2 - padding;
    const badgeY = -(nodeData.height || 0) / 2 + badgeSize / 2 + padding;

    // Add circle background for badge
    nodeGroup.append('circle')
      .attr('class', 'highlight-badge-bg')
      .attr('cx', badgeX)
      .attr('cy', badgeY)
      .attr('r', badgeSize / 2)
      .attr('fill', color);

    // Add text label
    nodeGroup.append('text')
      .attr('class', 'highlight-badge')
      .attr('x', badgeX)
      .attr('y', badgeY)
      .attr('dy', '0.35em')
      .text(label);
  }

  /**
   * Shows a runtime alert for edge routing errors.
   * 
   * @param sourceId - Source node ID
   * @param targetId - Target node ID
   */
  private showRuntimeAlert(sourceId: string, targetId: string): void {
    console.warn(`Runtime (WebCola) error when laying out an edge from ${sourceId} to ${targetId}. You may have to click and drag these nodes slightly to un-stick layout.`);
  }

  /**
   * Get CSS styles for the component
   */
  private getCSS(): string {
    return `
      ${this.getFontImports()}
      :host {
        display: block;
        width: 100%;
        height: 100%;
        font-family: ${this.getFontFamily()};
      }
      
      #svg-container {
        position: relative; /* Make this the positioning context for zoom controls */
        width: 100%;
        height: 100%;
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-radius: 8px;
        background-color: ${this.getCanvasBackground()}; /* warm-white default; override via the background attribute */
        overflow: hidden;
      }

      #loading {
        position: absolute;
        top: 10px;
        left: 10px;
        z-index: 1000;
        display: flex;
        align-items: center;
        gap: 8px;
        max-width: min(65%, 420px);
        padding: 6px 10px;
        background: rgba(255, 255, 255, 0.93);
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 999px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
        color: #374151;
        font-size: 12px;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-4px);
        transition: opacity 120ms ease, transform 120ms ease, visibility 0s linear 120ms;
      }

      #loading.visible {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
        transition: opacity 120ms ease, transform 120ms ease;
      }

      .loading-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #2563eb;
        flex-shrink: 0;
        animation: loading-pulse 1s ease-in-out infinite;
      }

      #loading-progress {
        overflow: hidden;
        text-overflow: ellipsis;
      }

      @keyframes loading-pulse {
        0%, 100% { opacity: 0.4; transform: scale(0.85); }
        50% { opacity: 1; transform: scale(1); }
      }
      
      /* Make SVG fill the container completely */
      svg {
        width: 100%;          /* Fill container width */
        height: 100%;         /* Fill container height */
        display: block;       /* Remove inline spacing */
        cursor: grab;
      }
      
      svg:active {
        cursor: grabbing;
      }
      
      .node rect {
        cursor: move;
      }

      .error-node rect, .error-group {
        stroke-width: 2px;
        stroke-dasharray: 5 5;
        animation: dash 1s linear infinite;
      }

      /* Enhanced visibility for small error nodes */
      .small-error-node rect {
        stroke-width: 4px !important; /* Thicker stroke for visibility */
        stroke-dasharray: 8 4 !important; /* Larger dash pattern */
        animation: dash 1s linear infinite, pulse-bg 2s ease-in-out infinite !important;
        fill: rgba(225, 112, 46, 0.46) !important; /* Light reddish background */
      }

      @keyframes dash {
        to {
          stroke-dashoffset: -10;
        }
      }

      /* Pulsing background animation for small error nodes */
      @keyframes pulse-bg {
        0%, 100% { 
          fill-opacity: 0.15; 
        }
        50% { 
          fill-opacity: 0.55; 
        }
      }
      
      .link {
        stroke-width: 1.25px;
        fill: none;
        marker-end: url(#end-arrow);
      }

      .inferredLink {
        stroke-width: 1.5px;
        fill: none;
        marker-end: url(#end-arrow);
      }


    .alignmentLink {
            stroke: transparent !important;    /* make the stroke invisible */
            stroke-width: 0 !important;        /* ensure no visible thickness */
            stroke-opacity: 0 !important;      /* defensive */
            fill: none !important;
            pointer-events: none !important;   /* don't block mouse events */
          }


      .link.highlighted {
        stroke: black; /* Change this to your desired highlight color */
        stroke-width: 3px; /* Change this to your desired highlight width */
      }

      .inferredLink.highlighted {
        stroke:#666666; /* Change this to your desired highlight color */
        stroke-width: 3px; /* Change this to your desired highlight width */
      }

      /* Node highlighting styles */
      .node.highlighted rect {
        stroke: #ff9500;
        stroke-width: 3px;
        filter: drop-shadow(0 0 6px rgba(255, 149, 0, 0.6));
      }

      .node.highlighted-first rect {
        stroke: #007aff;
        stroke-width: 3px;
        filter: drop-shadow(0 0 6px rgba(0, 122, 255, 0.6));
      }

      .node.highlighted-second rect {
        stroke: #ff3b30;
        stroke-width: 3px;
        filter: drop-shadow(0 0 6px rgba(255, 59, 48, 0.6));
      }

      /* Add a badge indicator for first/second in binary selectors */
      .highlight-badge {
        font-size: 10px;
        font-weight: bold;
        fill: white;
        text-anchor: middle;
        pointer-events: none;
      }

      .highlight-badge-bg {
        pointer-events: none;
      }
      
      .group {
        fill: rgba(200, 200, 200, 0.10);
        stroke: #666;
        stroke-width: 1.5px;
        stroke-opacity: 0.4;
      }

      .groupLabelBg {
        pointer-events: none;
      }
      
      .label {
        text-anchor: middle;
        dominant-baseline: middle;
        font-size: 10px;
        pointer-events: none;
      }

      .linklabel {
        text-anchor: middle;
        dominant-baseline: middle;
        font-size: 12px;
        font-weight: 500;
        fill: #1a1a1a;
        pointer-events: none;
        font-family: ${this.getFontFamily()};
        stroke: ${this.getCanvasBackground()};
        stroke-width: 3px;
        stroke-linejoin: round;
        paint-order: stroke fill;
      }
      
      .mostSpecificTypeLabel {
        font-size: 9px;
        font-weight: 600;
        pointer-events: none;
      }
      
      #error {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        padding: 20px;
        background: white;
        border: 1px solid #ccc;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }

      /* Input mode styles */
      svg.input-mode {
        cursor: crosshair !important;
      }

      svg.input-mode .node rect {
        cursor: crosshair !important;
      }

      svg.input-mode:active {
        cursor: crosshair !important;
      }

      .temporary-edge {
        pointer-events: none;
        z-index: 1000;
      }

      svg.input-mode .link {
        cursor: pointer;
      }

      svg.input-mode .link:hover {
        opacity: 0.8;
      }

      /* Error icon positioning - bottom area to avoid header overlap */
      #error-icon {
        margin: 5px;
        padding: 8px 12px;
        font-size: 16px;
        position: absolute;
        bottom: 10px; /* Position at bottom instead of top */
        left: 10px;
        z-index: 1000;
        cursor: help;
        background-color: rgba(220, 53, 69, 0.95);
        color: white;
        border-radius: 6px;
        border: none;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 6px;
        visibility: hidden; /* Use visibility instead of display */
      }
      
      #error-icon.visible {
        visibility: visible;
      }

      #error-icon::before {
        content: "⚠️";
        font-size: 18px;
      }

      /* Graph toolbar styling */
      #graph-toolbar {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding: 8px 12px;
        background: rgba(255, 255, 255, 0.95);
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 6px;
        margin-bottom: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        backdrop-filter: blur(4px);
      }

      /* Zoom controls styling */
      #zoom-controls {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
      }

      #zoom-controls button {
        width: 24px;
        height: 24px;
        border: 1px solid #d1d5db;
        background: #f9fafb;
        color: #374151;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
        user-select: none;
        line-height: 1;
      }

      #zoom-controls button:hover {
        background: #f3f4f6;
        border-color: #9ca3af;
        color: #111827;
      }

      #zoom-controls button:active {
        background: #e5e7eb;
        border-color: #6b7280;
        transform: translateY(0.5px);
      }

      #zoom-controls button:disabled {
        background: #f9fafb;
        border-color: #e5e7eb;
        color: #9ca3af;
        cursor: not-allowed;
      }

      #zoom-controls button:disabled:hover {
        background: #f9fafb;
        border-color: #e5e7eb;
        color: #9ca3af;
        transform: none;
      }

      /* Routing control styling */
      #routing-control {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-left: 16px;
        padding-left: 16px;
        border-left: 1px solid #e5e7eb;
      }

      #routing-control label {
        font-size: 12px;
        font-weight: 500;
        color: #6b7280;
        user-select: none;
      }

      #routing-mode {
        padding: 4px 8px;
        border: 1px solid #d1d5db;
        background: #f9fafb;
        color: #374151;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.15s ease;
        outline: none;
      }

      #routing-mode:hover {
        background: #f3f4f6;
        border-color: #9ca3af;
      }

      #routing-mode:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
      }

      /* Screenshot control styling */
      #screenshot-control {
        display: flex;
        align-items: center;
        margin-left: 16px;
        padding-left: 16px;
        border-left: 1px solid #e5e7eb;
      }

      #screenshot-btn {
        width: 24px;
        height: 24px;
        border: 1px solid #d1d5db;
        background: #f9fafb;
        color: #374151;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
        user-select: none;
        line-height: 1;
      }

      #screenshot-btn:hover {
        background: #f3f4f6;
        border-color: #9ca3af;
        color: #111827;
      }

      #screenshot-btn:active {
        background: #e5e7eb;
        border-color: #6b7280;
        transform: translateY(0.5px);
      }

      /* Modal Overlay and Dialog */
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        font-family: ${this.getFontFamily()};
      }

      .modal-dialog {
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        padding: 24px;
        max-width: 500px;
        width: 90%;
        max-height: 90vh;
        overflow-y: auto;
      }

      .modal-header {
        margin-bottom: 16px;
      }

      .modal-title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #333;
      }

      .modal-body {
        margin-bottom: 20px;
      }

      .modal-message {
        margin: 0 0 16px 0;
        font-size: 14px;
        color: #555;
        line-height: 1.5;
      }

      .modal-input {
        width: 100%;
        padding: 8px 12px;
        border: 2px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        box-sizing: border-box;
      }

      .modal-input:focus {
        outline: none;
        border-color: #007acc;
      }

      .modal-footer {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
      }

      .modal-button {
        padding: 8px 16px;
        border: none;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s;
      }

      .modal-button.primary {
        background: #007acc;
        color: white;
      }

      .modal-button.primary:hover {
        background: #005fa3;
      }

      .modal-button.secondary {
        background: #f8f9fa;
        color: #666;
        border: 1px solid #ddd;
      }

      .modal-button.secondary:hover {
        background: #e9ecef;
      }
    `;
  }


  private calculateNewPosition(pathElement : any, axis : 'x' | 'y'): number {
        const pathLength = pathElement.getTotalLength();
        const midpointLength = pathLength / 2;
        const offset = 0; //getRandomOffsetAlongPath(); // commenting out to remove jitter

        let targetLength = midpointLength + offset;

        if (targetLength >= pathLength) {
            targetLength = midpointLength;
        }

        const point = pathElement.getPointAtLength(targetLength);
        return axis === 'x' ? point.x : point.y;
    }

  

  /**
   * Show loading indicator
   */
  private showLoading(): void {
    const loading = this.root.querySelector('#loading') as HTMLElement;
    const error = this.root.querySelector('#error') as HTMLElement;
    if (this.loadingShowTimer !== null) {
      window.clearTimeout(this.loadingShowTimer);
      this.loadingShowTimer = null;
    }
    if (!loading.classList.contains('visible')) {
      this.loadingShowTimer = window.setTimeout(() => {
        loading.classList.add('visible');
        this.loadingShowTimer = null;
      }, WebColaCnDGraph.LOADING_INDICATOR_DELAY_MS);
    }
    error.style.display = 'none';
  }

  /**
   * Update loading progress message
   */
  private updateLoadingProgress(message: string): void {
    const loading = this.root.querySelector('#loading') as HTMLElement;
    const progressEl = this.root.querySelector('#loading-progress') as HTMLElement;
    if (progressEl) {
      progressEl.textContent = message;
      loading.setAttribute('aria-label', message);
    }
  }

  /**
   * Hide loading indicator
   */
  private hideLoading(): void {
    const loading = this.root.querySelector('#loading') as HTMLElement;
    if (this.loadingShowTimer !== null) {
      window.clearTimeout(this.loadingShowTimer);
      this.loadingShowTimer = null;
    }
    loading.classList.remove('visible');
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    const error = this.root.querySelector('#error') as HTMLElement;
    this.hideLoading();
    error.style.display = 'block';
    error.textContent = message;
  }

  /**
   * Show error icon
   */
  private showErrorIcon(): void {
    const errorIcon = this.root.querySelector('#error-icon') as HTMLElement;
    errorIcon.classList.add('visible');
  }

  /**
   * Hide error icon
   */
  private hideErrorIcon(): void {
    const errorIcon = this.root.querySelector('#error-icon') as HTMLElement;
    errorIcon.classList.remove('visible');
  }

  // =========================================
  // MODAL DIALOG METHODS
  // =========================================

  /**
   * Show a confirmation dialog
   */
  private showConfirmDialog(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';

      overlay.innerHTML = `
        <div class="modal-dialog">
          <div class="modal-header">
            <h3 class="modal-title">Confirm Action</h3>
          </div>
          <div class="modal-body">
            <p class="modal-message">${message}</p>
          </div>
          <div class="modal-footer">
            <button class="modal-button secondary" data-action="cancel">Cancel</button>
            <button class="modal-button primary" data-action="confirm">Confirm</button>
          </div>
        </div>
      `;

      // Add event listeners
      overlay.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('modal-overlay')) {
          // Clicked outside dialog
          this.root.removeChild(overlay);
          resolve(false);
        } else if (target.dataset.action === 'cancel') {
          this.root.removeChild(overlay);
          resolve(false);
        } else if (target.dataset.action === 'confirm') {
          this.root.removeChild(overlay);
          resolve(true);
        }
      });

      // Handle escape key
      const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          this.root.removeChild(overlay);
          document.removeEventListener('keydown', handleKeydown);
          resolve(false);
        }
      };
      document.addEventListener('keydown', handleKeydown);

      this.root.appendChild(overlay);
      
      // Focus the confirm button
      const confirmBtn = overlay.querySelector('[data-action="confirm"]') as HTMLButtonElement;
      confirmBtn?.focus();
    });
  }

  /**
   * Show a prompt dialog for text input
   */
  private showPromptDialog(message: string, defaultValue: string = ''): Promise<string | null> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';

      overlay.innerHTML = `
        <div class="modal-dialog">
          <div class="modal-header">
            <h3 class="modal-title">Input Required</h3>
          </div>
          <div class="modal-body">
            <p class="modal-message">${message}</p>
            <input type="text" class="modal-input" value="${defaultValue}" placeholder="Enter text...">
          </div>
          <div class="modal-footer">
            <button class="modal-button secondary" data-action="cancel">Cancel</button>
            <button class="modal-button primary" data-action="ok">OK</button>
          </div>
        </div>
      `;

      const input = overlay.querySelector('.modal-input') as HTMLInputElement;

      // Add event listeners
      overlay.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('modal-overlay')) {
          // Clicked outside dialog
          this.root.removeChild(overlay);
          resolve(null);
        } else if (target.dataset.action === 'cancel') {
          this.root.removeChild(overlay);
          resolve(null);
        } else if (target.dataset.action === 'ok') {
          const value = input.value;
          this.root.removeChild(overlay);
          resolve(value);
        }
      });

      // Handle enter and escape keys
      const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          const value = input.value;
          this.root.removeChild(overlay);
          document.removeEventListener('keydown', handleKeydown);
          resolve(value);
        } else if (e.key === 'Escape') {
          this.root.removeChild(overlay);
          document.removeEventListener('keydown', handleKeydown);
          resolve(null);
        }
      };
      document.addEventListener('keydown', handleKeydown);

      this.root.appendChild(overlay);
      
      // Focus and select the input
      input.focus();
      input.select();
    });
  }

  /**
   * Show a prompt dialog for text input with a delete button option
   * @param message - Dialog message
   * @param defaultValue - Default input value
   * @returns Promise that resolves to: input value, null (cancel), or 'DELETE' (delete action)
   */
  private showEdgeEditDialog(message: string, defaultValue: string = ''): Promise<string | null> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';

      overlay.innerHTML = `
        <div class="modal-dialog">
          <div class="modal-header">
            <h3 class="modal-title">Edit Edge</h3>
          </div>
          <div class="modal-body">
            <p class="modal-message">${message}</p>
            <input type="text" class="modal-input" value="${defaultValue}" placeholder="Enter text...">
          </div>
          <div class="modal-footer">
            <button class="modal-button secondary" data-action="cancel">Cancel</button>
            <button class="modal-button danger" data-action="delete" style="background: #dc3545; margin-right: auto;">Delete Edge</button>
            <button class="modal-button primary" data-action="ok">OK</button>
          </div>
        </div>
      `;

      const input = overlay.querySelector('.modal-input') as HTMLInputElement;

      // Add event listeners
      overlay.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('modal-overlay')) {
          // Clicked outside dialog
          this.root.removeChild(overlay);
          resolve(null);
        } else if (target.dataset.action === 'cancel') {
          this.root.removeChild(overlay);
          resolve(null);
        } else if (target.dataset.action === 'delete') {
          this.root.removeChild(overlay);
          resolve('DELETE'); // Special signal for deletion
        } else if (target.dataset.action === 'ok') {
          const value = input.value;
          this.root.removeChild(overlay);
          resolve(value);
        }
      });

      // Handle enter and escape keys
      const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          const value = input.value;
          this.root.removeChild(overlay);
          document.removeEventListener('keydown', handleKeydown);
          resolve(value);
        } else if (e.key === 'Escape') {
          this.root.removeChild(overlay);
          document.removeEventListener('keydown', handleKeydown);
          resolve(null);
        }
      };
      document.addEventListener('keydown', handleKeydown);

      this.root.appendChild(overlay);
      
      // Focus and select the input
      input.focus();
      input.select();
    });
  }

  // =========================================
  // SCREENSHOT / EXPORT API
  // =========================================

  /**
   * Captures the current graph as a high-resolution PNG and downloads it.
   * Inlines all computed styles and converts external images to base64
   * for accurate offline rendering.
   */
  public async takeScreenshot(): Promise<void> {
    const svg = this.shadowRoot?.querySelector('#svg') as SVGSVGElement | null;
    if (!svg) {
      console.warn('No SVG element found for screenshot.');
      return;
    }

    try {
      // Clone the SVG so we don't mutate the live DOM
      const svgClone = svg.cloneNode(true) as SVGSVGElement;

      // Read viewBox dimensions for proper sizing
      const viewBox = svg.getAttribute('viewBox');
      let width = svg.clientWidth || 800;
      let height = svg.clientHeight || 600;
      if (viewBox) {
        const parts = viewBox.split(/[\s,]+/).map(Number);
        if (parts.length === 4) {
          width = parts[2];
          height = parts[3];
        }
        svgClone.setAttribute('viewBox', viewBox);
      }
      svgClone.setAttribute('width', String(width));
      svgClone.setAttribute('height', String(height));
      svgClone.removeAttribute('preserveAspectRatio');

      // Inline computed styles from the live SVG into the clone so they survive serialization
      this.inlineComputedStyles(svg, svgClone);

      // Convert <image> hrefs to base64 data URIs so icons render in the PNG
      await this.convertImagesToBase64(svgClone);

      // Match the on-screen canvas background so PNG exports look like the live view
      const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      bgRect.setAttribute('width', '100%');
      bgRect.setAttribute('height', '100%');
      bgRect.setAttribute('fill', this.getCanvasBackground());
      svgClone.insertBefore(bgRect, svgClone.firstChild);

      // Serialize the clone to a string
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgClone);

      // Render to a high-res canvas
      const scale = WebColaCnDGraph.SCREENSHOT_SCALE;
      const blob = await this.svgStringToPngBlob(svgString, width, height, scale);
      if (!blob) {
        console.error('Failed to generate PNG blob from SVG.');
        return;
      }

      // Trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.download = `graph-screenshot-${timestamp}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Screenshot failed:', error);
    }
  }

  /**
   * Walks the original and cloned SVG trees in parallel, copying key computed
   * style properties onto the clone as inline styles.
   */
  private inlineComputedStyles(original: Element, clone: Element): void {
    const STYLE_PROPS = [
      'font-family', 'font-size', 'font-weight', 'font-style',
      'fill', 'stroke', 'stroke-width', 'stroke-dasharray', 'stroke-linejoin',
      'opacity', 'text-anchor', 'dominant-baseline',
      'paint-order', 'visibility', 'display',
    ];

    const origChildren = original.children;
    const cloneChildren = clone.children;

    // Inline styles on the current element
    if (original instanceof HTMLElement || original instanceof SVGElement) {
      const computed = getComputedStyle(original);
      const cloneEl = clone as HTMLElement | SVGElement;
      for (const prop of STYLE_PROPS) {
        const val = computed.getPropertyValue(prop);
        if (val) {
          cloneEl.style.setProperty(prop, val);
        }
      }
    }

    // Recurse into children
    const len = Math.min(origChildren.length, cloneChildren.length);
    for (let i = 0; i < len; i++) {
      this.inlineComputedStyles(origChildren[i], cloneChildren[i]);
    }
  }

  /**
   * Finds all <image> elements in the cloned SVG and replaces their
   * href/xlink:href with base64 data URIs so they render when serialized.
   */
  private async convertImagesToBase64(svgClone: SVGSVGElement): Promise<void> {
    const images = svgClone.querySelectorAll('image');
    const promises: Promise<void>[] = [];

    images.forEach((img) => {
      const href = img.getAttribute('href') || img.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
      if (!href || href.startsWith('data:')) return;

      const promise = this.fetchImageAsBase64(href)
        .then((dataUri) => {
          img.setAttribute('href', dataUri);
          img.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
        })
        .catch(() => {
          // If CORS or network prevents fetching, remove the image gracefully
          img.remove();
        });
      promises.push(promise);
    });

    await Promise.all(promises);
  }

  /**
   * Fetches an image URL and returns a base64 data URI.
   */
  private fetchImageAsBase64(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('No canvas context')); return; }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => reject(new Error(`Failed to load: ${url}`));
      img.src = url;
    });
  }

  /**
   * Renders an SVG string to a PNG Blob at the given scale factor.
   */
  private svgStringToPngBlob(
    svgString: string,
    width: number,
    height: number,
    scale: number
  ): Promise<Blob | null> {
    return new Promise((resolve) => {
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext('2d');
        if (!ctx) { URL.revokeObjectURL(url); resolve(null); return; }

        ctx.scale(scale, scale);
        ctx.fillStyle = this.getCanvasBackground();
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          resolve(blob);
        }, 'image/png');
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };

      img.src = url;
    });
  }

  // =========================================
  // EVENT-BASED STATE INTEGRATION API
  // =========================================

  // =========================================
  // LIFECYCLE METHODS AND CLEANUP
  // =========================================

  /**
   * Called when the custom element is disconnected from the DOM.
   * Performs cleanup to prevent memory leaks.
   */
  disconnectedCallback(): void {
    this.dispose();
  }

  /**
   * Disposes of resources to prevent memory leaks.
   * Should be called when the component is no longer needed.
   * 
   * This method cleans up:
   * - D3 selections and event listeners
   * - WebCola layout references
   * - Keyboard event handlers
   * - Temporary UI elements (modals, overlays)
   */
  public dispose(): void {
    // Remove keyboard event handlers
    this.detachInputModeListeners();
    this.deactivateInputMode();
    
    // Clear D3 selections and remove event listeners
    if (this.svg) {
      this.svg.on('.zoom', null); // Remove zoom event handlers
      this.svg.selectAll('*').remove(); // Remove all child elements
    }
    
    if (this.container) {
      this.container.selectAll('*').remove(); // Remove all container children
    }
    
    // Clear node drag event handlers
    if (this.svgNodes) {
      this.svgNodes.on('.drag', null);
      this.svgNodes.on('.cnd', null);
    }
    
    // Clear WebCola layout reference
    if (this.colaLayout) {
      // Stop any ongoing layout computation
      if (typeof (this.colaLayout as any).stop === 'function') {
        (this.colaLayout as any).stop();
      }
      // Remove event handlers
      (this.colaLayout as any).on('tick', null);
      (this.colaLayout as any).on('end', null);
    }
    
    // Clear stored references to help garbage collection
    this.currentLayout = null as any;
    this.colaLayout = null as any;
    this.svgNodes = null as any;
    this.svgLinkGroups = null as any;
    this.svgGroups = null as any;
    this.svgGroupLabels = null as any;
    this.svgGroupLabelBgs = null as any;
    this.zoomBehavior = null as any;
    this.storedTransform = null as any;
    
    // Clear drag state
    this.dragStartPositions.clear();
    
    // Clear edge creation state
    this.cleanupEdgeCreation();
    
    // Clear text measurement canvas
    if (this.textMeasurementCanvas) {
      this.textMeasurementCanvas = null;
    }
  }

  /**
   * Returns memory usage statistics for monitoring and debugging.
   * Useful for tracking memory consumption and identifying potential leaks.
   * 
   * @returns Object containing various memory-related metrics
   */
  public getMemoryStats(): {
    nodeCount: number;
    edgeCount: number;
    groupCount: number;
    constraintCount: number;
    hasActiveLayout: boolean;
  } {
    return {
      nodeCount: this.currentLayout?.nodes?.length || 0,
      edgeCount: this.currentLayout?.links?.length || 0,
      groupCount: this.currentLayout?.groups?.length || 0,
      constraintCount: this.currentLayout?.constraints?.length || 0,
      hasActiveLayout: !!this.colaLayout
    };
  }

}

// Register the custom element only in browser environments
if (typeof customElements !== 'undefined' && typeof HTMLElement !== 'undefined') {
  customElements.define('webcola-cnd-graph', WebColaCnDGraph);
}
